import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DoCheck, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import { environment } from '../environments/environment';

type ConfidenceLevel = 'low' | 'high';
type ReviewTab = 'driver' | 'violations' | 'validation' | 'change-log';
type FieldStatus = 'pending' | 'accepted' | 'rejected' | 'editing';
type AppTheme = 'light' | 'dark';
type WorkflowMode = 'import' | 'review';
type UploadStatus = 'idle' | 'reading' | 'authenticating' | 'queued' | 'running' | 'done' | 'error';
type UploadQueueStatus = 'pending' | 'reading' | 'queued' | 'running' | 'done' | 'error';
type DocumentMarkMode = 'all' | 'focus';
type BboxEditAction = 'move' | 'resize-nw' | 'resize-ne' | 'resize-se' | 'resize-sw';
type JsonPathPart = string | number;
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';
type DriverHistoryExportMode = 'today' | 'custom';
type SpreadsheetCell = string | number | null | undefined;

const VIOLATIONS_FIELD = 'Violations/Convictions Failure to Appear or Accidents';
const VIOLATIONS_COUNT_FIELD = 'Violations/Convictions Failure to Appear or Accidents - Count';
const VIOLATIONS_SUMMARY_FIELD = 'Violations/Convictions Failure to Appear or Accidents - Summary';
const SUSPENSIONS_FIELD = 'Suspensions';
const SUSPENSIONS_COUNT_FIELD = 'Suspensions - Count';
const SUSPENSIONS_SUMMARY_FIELD = 'Suspensions - Summary';
const ORIGINAL_DESCRIPTION_FIELD = 'Description.original_description';
const VIOLATION_FORM_FIELDS: Array<{ fieldKey: keyof ViolationForm; jsonField: string }> = [
  { fieldKey: 'type', jsonField: 'Type' },
  { fieldKey: 'violationDate', jsonField: 'Violation Date' },
  { fieldKey: 'convictionDate', jsonField: 'Conviction Date' },
  { fieldKey: 'stateCode', jsonField: 'St Code' },
  { fieldKey: 'location', jsonField: 'Location' },
  { fieldKey: 'violationCode', jsonField: 'Viol Code' },
  { fieldKey: 'speed', jsonField: 'Speed' },
  { fieldKey: 'points', jsonField: 'Points' },
  { fieldKey: 'description', jsonField: 'Description' },
];
const DRIVER_EXPORT_HEADERS = [
  'exclude',
  'suffix',
  'given_name',
  'last_name',
  'dob',
  'licenseno',
  'experience',
  'licensestate',
  'licenseexpdate',
  'licenseclasstype',
  'licensemedicalexp',
  'driver_phone',
  'driver_email',
  'driver_physical_address',
  'driver_physical_address_city',
  'driver_physical_address_state',
  'driver_physical_zip',
] as const;

interface SourceLLM {
  page: number;
  line_id?: string;
  bbox?: [number, number, number, number];
  class_name?: string;
}

interface FieldData {
  value: string | number | boolean;
  conf: string | number;
  comment?: string;
  validation_comment?: string;
  line_id?: string | null;
  page?: number;
  bbox?: [number, number, number, number] | number[];
  unit?: string | null;
  user_reviewed?: boolean;
  review_status?: string;
  source_llm?: SourceLLM;
}

interface DriverRecord {
  [key: string]: unknown;
}

interface MvrDocument {
  drivers: DriverRecord[];
}

interface DuplicateDriverNotice {
  driverIndex: number;
  fileName: string;
  driverName: string;
  licenseNumber: string;
  dob: string;
  message: string;
}

interface DuplicateDriverChoice {
  skippedNotices: DuplicateDriverNotice[];
  cancelled: boolean;
}

interface DuplicateDriverFilterResult {
  documents: MvrDocument[];
  cancelled: boolean;
}

interface UploadQueueItem {
  id: string;
  file: File;
  objectUrl: string;
  status: UploadQueueStatus;
  message: string;
  batchId?: number | string;
  error?: string;
}

interface MvrBatchFilePayload {
  file_name: string;
  file_data: string;
}

interface ApiCredentials {
  user_id: string;
  email: string;
  password: string;
}

interface ReviewField {
  id: string;
  label: string;
  value: string;
  confidence: number;
  level: ConfidenceLevel;
  source: string;
  evidence: string;
  validationComment?: string;
  helper: string;
  mismatch?: string;
  required?: boolean;
  userReviewed?: boolean;
  documentValue?: string;
  page?: number;
  lineId?: string;
  bbox?: [number, number, number, number];
  unit?: string | null;
  status: FieldStatus;
  rawName: string;
  jsonPath: JsonPathPart[];
}

interface DriverFieldStateSnapshot {
  value: string;
  confidence: number;
  level: ConfidenceLevel;
  status: FieldStatus;
  userReviewed?: boolean;
  documentValue?: string;
  mismatch?: string;
  page?: number;
  lineId?: string;
  bbox?: [number, number, number, number];
  unit?: string | null;
  validationComment?: string;
  editOriginalValue?: string;
  editOriginalValidationComment?: string;
  editOriginalBbox?: [number, number, number, number] | null;
  edited: boolean;
  submitted: boolean;
}

interface DetailItem {
  title: string;
  value: string;
  meta?: string;
  badge?: string;
  fields?: DetailPair[];
}

interface DetailPair {
  label: string;
  value: string;
  validationComment?: string;
  fieldId?: string;
}

interface DetailSection {
  id: string;
  title: string;
  count: number;
  items: DetailItem[];
  summary?: string;
}

interface ViolationItem {
  type: string;
  violationDate: string;
  convictionDate: string;
  stateCode: string;
  violationCode: string;
  description: string;
  originalDescription: string;
  descriptionConfidence: number | null;
  descriptionOptions: DescriptionOption[];
  speed: string;
  location: string;
  points: string;
  summary: string;
  fieldIds: Record<string, string>;
}

interface ViolationFieldRow {
  key: string;
  label: string;
  jsonField: string;
  value: string;
  fieldId: string;
  confidence: number;
  isDescription?: boolean;
  descriptionConfidence?: number | null;
  descriptionOptions?: DescriptionOption[];
}

interface SuspensionItem {
  action: string;
  orderDate: string;
  effectiveDate: string;
  clearDate: string;
  endDate: string;
  code: string;
  avdCode: string;
  description: string;
  summary: string;
  fieldIds: Record<string, string>;
}

interface SuspensionFieldRow {
  key: string;
  label: string;
  jsonField: string;
  value: string;
  fieldId: string;
  confidence: number;
}

interface DescriptionOption {
  key: string;
  value: string;
  confidence: number | null;
  svcCode?: string;
  bbox?: [number, number, number, number];
  page?: number;
  unit?: string | null;
}

interface ViolationForm {
  type: string;
  violationDate: string;
  convictionDate: string;
  stateCode: string;
  location: string;
  violationCode: string;
  speed: string;
  points: string;
  description: string;
}

interface DriverSummary {
  index: number;
  name: string;
  initials: string;
  license: string;
  lowCount: number;
  extractedCount: number;
  filledPercent: number;
  averageConfidence: number;
  status: string;
}

interface DriverDashboardRow {
  seq: DriverSummary;
  driver2?: DriverSummary;
  driver3?: DriverSummary;
}

interface ChangeLogEntry {
  id: string;
  field: string;
  previousValue: string;
  newValue: string;
  changedAt: string;
  changeType: string;
}

interface ValidationItem {
  id: string;
  driverIndex: number;
  driverName: string;
  field: string;
  value: string;
  comment: string;
  path: JsonPathPart[];
}

interface BboxResizeHandle {
  action: BboxEditAction;
  className: string;
  label: string;
}

interface BboxDragState {
  fieldId: string;
  action: BboxEditAction;
  startX: number;
  startY: number;
  startBbox: [number, number, number, number];
  moved: boolean;
}

interface BboxAdjustment {
  driverIndex: number;
  fieldId: string;
  fieldName: string;
  label: string;
  path: JsonPathPart[];
  value: string;
  page: number | undefined;
  lineId: string | undefined;
  bbox: [number, number, number, number];
  unit: string | null | undefined;
}

interface ReviewFeedbackOperation {
  action: 'update' | 'add';
  path: JsonPathPart[];
  value?: unknown;
  bbox: [number, number, number, number] | [];
  page: number | null;
  unit: string | null;
  comment?: string;
}

interface ReviewFeedbackResponse {
  reviewed_blob_url?: string;
  blob_name?: string;
  reviewed_json?: MvrDocument;
  success?: boolean;
  error?: string;
  field_name?: string;
  submitted_value?: unknown;
}

interface BboxReviewFeedbackPayload {
  batch_id: number | string;
  file_name: string;
  pdf_path: string;
  operations: ReviewFeedbackOperation[];
  include_updated_json: boolean;
  save_to_blob: boolean;
}

const REVIEW_FIELDS = [
  'Order Date',
  'License Number',
  'Given Name',
  'Last Name',
  'State of Driver Record',
  'Rec Type',
  'Driver Address',
  'Driver City',
  'Driver State',
  'Driver ZIP',
  'Sex',
  'Weight',
  'Date of Birth',
  'Age',
  'Eye Color',
  'Height',
  'Hair Color',
  'Issue Date',
  'Expiration Date',
  'License Status',
  'Miscellaneous State Data',
  'Processed Date',
  'Years Of Experience',
  "Two Years of Class A Driver's Experience",
] as const;

const SUPPLEMENTAL_REVIEW_ROOTS = new Set(['License Information', 'CDL Medical Information']);
const EMPTY_FIELD_REVIEW_ROOTS = new Set([
  'License Information',
  'CDL Medical Information',
  VIOLATIONS_FIELD,
  SUSPENSIONS_FIELD,
]);

const LABELS: Record<string, string> = {
  'Years Of Experience': 'YRS Of EXP',
  'Date of Birth': 'DOB',
  'State of Driver Record': 'LIC State',
  'Driver ZIP': 'Driver Zip',
};

const FIELD_IDS: Record<string, string> = {
  'Order Date': 'order-date',
  'License Number': 'license-number',
  'Years Of Experience': 'years-exp',
  "Two Years of Class A Driver's Experience": 'two-years-class-a-experience',
  'Date of Birth': 'dob',
  'Given Name': 'given-name',
  'Last Name': 'last-name',
  'State of Driver Record': 'lic-state',
  'Rec Type': 'rec-type',
  'Driver Address': 'driver-address',
  'Driver City': 'driver-city',
  'Driver State': 'driver-state',
  'Driver ZIP': 'driver-zip',
  'Sex': 'sex',
  'Weight': 'weight',
  'Age': 'age',
  'Eye Color': 'eye-color',
  'Height': 'height',
  'Hair Color': 'hair-color',
  'Issue Date': 'issue-date',
  'Expiration Date': 'expiration-date',
  'License Status': 'license-status',
  'Miscellaneous State Data': 'misc-state-data',
  'Processed Date': 'processed-date',
};

const FALLBACK_CONFIDENCE: Record<string, number> = {
  'Order Date': 64,
  'License Number': 64,
  'Years Of Experience': 75,
  'Date of Birth': 68,
  'Given Name': 94,
  'Last Name': 90,
  'State of Driver Record': 96,
  'License Status': 90,
};

const LOW_CONFIDENCE_THRESHOLD = 80;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, AfterViewInit, DoCheck, OnDestroy {
  private readonly apiConfig = environment.mvrApi;
  private readonly apiBaseUrl = this.apiConfig.baseUrl.replace(/\/$/, '');
  private readonly apiKeyValidationUrl = this.apiConfig.apiKeyValidationUrl;
  private readonly pollIntervalMs = this.apiConfig.pollIntervalMs;
  private readonly pollTimeoutMs = this.apiConfig.pollTimeoutMs;
  private readonly allowedParentOrigins: readonly string[] = environment.allowedParentOrigins ?? [];
  // Credentials and the API key are kept in-memory only; never persisted to web storage.
  private apiKey = '';
  // Upload guardrails to limit client-side resource exhaustion.
  private readonly maxPdfBytes = 25 * 1024 * 1024;
  private readonly maxTotalUploadBytes = 100 * 1024 * 1024;
  private readonly maxPdfFiles = 25;

  quote = {
    id: '1068524',
    insured: 'Ravi Choudhary',
    status: 'QS:RECEIVED',
  };

  readonly reasons = [
    '-- select reason --',
    'OCR / extraction error',
    'Document value is incorrect',
    'Falcon driver record is correct',
    'Manual reviewer correction',
    'Other',
  ];

  readonly violationEditableFields = [
    { label: 'Type', jsonField: 'Type', valueKey: 'type' },
    { label: 'Violation Date', jsonField: 'Violation Date', valueKey: 'violationDate' },
    { label: 'Conviction Date', jsonField: 'Conviction Date', valueKey: 'convictionDate' },
    { label: 'ST Code', jsonField: 'St Code', valueKey: 'stateCode' },
    { label: 'Location', jsonField: 'Location', valueKey: 'location' },
    { label: 'Viol Code', jsonField: 'Viol Code', valueKey: 'violationCode' },
    { label: 'Speed', jsonField: 'Speed', valueKey: 'speed' },
    { label: 'Points', jsonField: 'Points', valueKey: 'points' },
  ] as const;

  workflowMode: WorkflowMode = 'import';
  activeTab: ReviewTab = 'driver';
  theme: AppTheme = 'light';
  selectedReason = '-- select reason --';
  selectedFieldId = '';
  showSubmitModal = false;
  showCancelModal = false;
  showDuplicateModal = false;
  duplicateNoticesForReview: DuplicateDriverNotice[] = [];
  selectedDuplicateDriverIndexes = new Set<number>();
  duplicateDriverSearch = '';
  submittedToFalcon = false;
  submitStatus: SubmitStatus = 'idle';
  submitMessage = '';
  driverHistoryExportMode: DriverHistoryExportMode = 'today';
  driverHistoryFromDate = this.todayInputDate();
  driverHistoryToDate = this.todayInputDate();
  driverHistoryExportStatus: SubmitStatus = 'idle';
  driverHistoryExportMessage = '';
  driverHistoryTenant = '';
  reviewedBlobUrl = '';
  bboxSaveStatus: SubmitStatus = 'idle';
  bboxSaveMessage = '';
  pdfFileName = '';
  pdfPath = '';
  selectedPdfFile: File | null = null;
  selectedPdfFiles: File[] = [];
  uploadQueue: UploadQueueItem[] = [];
  uploadStatus: UploadStatus = 'idle';
  uploadMessage = 'Upload the MVR PDF to begin AI extraction.';
  uploadError = '';
  batchId: number | string | null = null;
  pollAttempt = 0;
  mvrData: MvrDocument | null = null;
  mvrProvider = '';
  activeDriverIndex = 0;
  pdfPageScale = 1.55;
  currentPdfPage = 1;
  pdfPageCount = 1;
  documentMarkMode: DocumentMarkMode = 'all';
  renderedPdfPage = 1;
  detailSections: DetailSection[] = [];
  lowGroupOpen = true;
  highGroupOpen = false;
  openDetailSections: Record<string, boolean> = {};
  openDetailItems: Record<string, boolean> = {};
  openViolationItems: Record<string, boolean> = {};
  openSuspensionItems: Record<string, boolean> = {};
  descriptionSelections: Record<string, string> = {};
  descriptionDropdownOpenKey = '';
  descriptionDropdownStyle: Record<string, string> = {};
  descriptionSearchTerms: Record<string, string> = {};
  descriptionSaveStatus: Record<string, SubmitStatus> = {};
  descriptionSaveMessage: Record<string, string> = {};
  violationFieldEditValues: Record<string, string> = {};
  violationFieldEditStatus: Record<string, SubmitStatus> = {};
  violationFieldEditMessage: Record<string, string> = {};
  activeViolationEditIndex: number | null = null;
  activeViolationEditJsonField = '';
  activeViolationEditLabel = '';
  suspensionFieldEditValues: Record<string, string> = {};
  suspensionFieldEditStatus: Record<string, SubmitStatus> = {};
  suspensionFieldEditMessage: Record<string, string> = {};
  activeSuspensionEditIndex: number | null = null;
  activeSuspensionEditJsonField = '';
  activeSuspensionEditLabel = '';
  changeLogEntries: ChangeLogEntry[] = [];
  validationItems: ValidationItem[] = [];
  validationFieldEditValues: Record<string, string> = {};
  validationFieldEditStatus: Record<string, SubmitStatus> = {};
  validationFieldEditMessage: Record<string, string> = {};
  activeValidationEditId = '';
  activeValidationEditFieldId = '';
  showViolationForm = false;
  violationSaveStatus: SubmitStatus = 'idle';
  violationSaveMessage = '';
  newViolation: ViolationForm = this.emptyViolationForm();
  savedViolationFieldValues: Partial<Record<keyof ViolationForm, string>> = {};
  savedViolationIndex: number | null = null;
  savedViolationDescriptionOptions: DescriptionOption[] = [];
  savedViolationDescriptionSelection = '';
  bboxEditEnabled = false;
  readonly bboxResizeHandles: BboxResizeHandle[] = [
    { action: 'resize-nw', className: 'nw', label: 'Resize top left' },
    { action: 'resize-ne', className: 'ne', label: 'Resize top right' },
    { action: 'resize-se', className: 'se', label: 'Resize bottom right' },
    { action: 'resize-sw', className: 'sw', label: 'Resize bottom left' },
  ];

  @ViewChildren('editValueInput') private readonly editValueInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('driverRibbon') private readonly driverRibbon?: ElementRef<HTMLElement>;
  @ViewChild('pdfCanvas') private readonly pdfCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('pdfFrame') private readonly pdfFrame?: ElementRef<HTMLElement>;
  private pdfDocument: pdfjsLib.PDFDocumentProxy | null = null;
  private renderTask: pdfjsLib.RenderTask | null = null;
  private pdfSourceUrl = '';
  private pdfObjectUrl = '';
  private readonly editedFieldIds = new Set<string>();
  private readonly bboxDraftFieldIds = new Set<string>();
  private readonly bboxSavedFieldIds = new Set<string>();
  private readonly bboxOverrides = new Map<string, [number, number, number, number]>();
  private readonly bboxSavedAdjustments = new Map<string, BboxAdjustment>();
  private readonly submittedFieldUpdateKeys = new Set<string>();
  private readonly updatedViolationDriverIndexes = new Set<number>();
  private readonly editOriginalValues = new Map<string, string>();
  private readonly editOriginalValidationComments = new Map<string, string>();
  private readonly editOriginalBboxes = new Map<string, [number, number, number, number] | null>();
  private readonly driverFieldState = new Map<number, Map<string, DriverFieldStateSnapshot>>();
  private readonly driverEditedFieldIds = new Map<number, Set<string>>();
  private readonly driverSubmittedFieldKeys = new Map<number, Set<string>>();
  private finalSubmitAfterValidationFixPending = false;
  private autoSubmitAfterValidationFixQueued = false;
  private duplicateChoiceResolver: ((choice: DuplicateDriverChoice) => void) | null = null;
  private activeBboxDrag: BboxDragState | null = null;
  private suppressNextHotspotClick = false;
  private apiCredentials: ApiCredentials | null = null;

  fields: ReviewField[] = [];
  private detailDocumentFields: ReviewField[] = [];
  private readonly onBboxPointerMove = (event: PointerEvent): void => this.updateActiveBboxDrag(event);
  private readonly onBboxPointerUp = (): void => this.endActiveBboxDrag();

  // Resolves once the pdf.js worker is configured; awaited before any getDocument call.
  private readonly pdfWorkerReady: Promise<void>;

  constructor() {
    this.pdfWorkerReady = this.configurePdfWorker();
  }

  private async configurePdfWorker(): Promise<void> {
    const workerUrl = `${window.location.origin}/pdf.worker.min.mjs?v=4.10.38`;
    try {
      // Some hosts (Azure static/CDN) serve .mjs as application/octet-stream, which strict MIME
      // checking rejects for module workers. Re-wrap the worker in a same-origin JS blob to bypass it.
      const response = await fetch(workerUrl);
      if (!response.ok) throw new Error(`Worker fetch failed (${response.status})`);
      const blob = new Blob([await response.text()], { type: 'text/javascript' });
      pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
    } catch {
      pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    }
  }

  async ngOnInit(): Promise<void> {
    this.loadThemePreference();
    this.captureApiKeyFromUrl();
    this.captureDriverHistoryContextFromUrl();
  }

  @HostListener('window:message', ['$event'])
  onParentMessage(event: MessageEvent): void {
    if (!this.isTrustedParentMessage(event)) return;

    const data = event.data;
    if (!data || typeof data !== 'object') return;

    const message = data as Record<string, unknown>;
    this.captureDriverHistoryContextFromMessage(message);
    if (message['source'] !== 'falcon' || message['type'] !== 'MVR_FALCON_VALIDATION_FAILED') return;

    const items = this.validationItemsFromFalconErrors(
      message['errors'] ?? message['validationErrors'] ?? message['validation_errors'],
    );

    this.validationItems = items.length
      ? items
      : [this.fallbackFalconValidationItem(String(message['message'] ?? 'Falcon validation failed.'))];
    this.submitStatus = 'error';
    this.submittedToFalcon = false;
    this.showSubmitModal = false;
    this.activeTab = 'validation';
    this.finalSubmitAfterValidationFixPending = true;
    this.submitMessage = `${this.validationItems.length} Falcon validation ${
      this.validationItems.length === 1 ? 'issue was' : 'issues were'
    } found. Please fix the validation items before submitting again.`;
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.workflowMode === 'review') {
      await this.loadPdfDocument();
      await this.renderPdfPage(this.selectedPdfPage);
    }
  }

  ngDoCheck(): void {
    const currentValidationCount = this.validationItems.length;
    if (
      this.finalSubmitAfterValidationFixPending
      && this.workflowMode === 'review'
      && this.activeTab === 'validation'
      && currentValidationCount === 0
    ) {
      void this.submitAfterFinalValidationFix();
    }
  }

  ngOnDestroy(): void {
    this.removeBboxDragListeners();
  }

  get isDarkTheme(): boolean {
    return this.theme === 'dark';
  }

  get selectedField(): ReviewField {
    return this.allDocumentFields.find((field) => field.id === this.selectedFieldId) ?? this.fields[0] ?? this.detailDocumentFields[0];
  }

  get selectedPdfPage(): number {
    return this.currentPdfPage;
  }

  get pdfZoomPercent(): number {
    return Math.round(this.pdfPageScale * 100);
  }

  get documentHotspots(): ReviewField[] {
    const pageFields = this.allDocumentFields.filter((field) => field.page === this.selectedPdfPage && field.bbox);
    const selectableFields = pageFields.filter((field) => {
      if (!this.isBboxAnnotationReviewField(field)) return true;

      return !pageFields.some((reviewField) => (
        !this.isBboxAnnotationReviewField(reviewField)
        && this.normalizedReviewValue(reviewField.value) === this.normalizedReviewValue(field.value)
        && this.boundingBoxesOverlap(reviewField.bbox, field.bbox)
      ));
    });

    if (this.documentMarkMode === 'all') {
      return selectableFields;
    }

    return this.selectedField?.page === this.selectedPdfPage && this.selectedField.bbox ? [this.selectedField] : [];
  }

  private get allDocumentFields(): ReviewField[] {
    return [...this.fields, ...this.detailDocumentFields];
  }

  get documentMarkButtonLabel(): string {
    return this.documentMarkMode === 'all' ? 'All' : 'Focus';
  }

  get isDocumentFocusMode(): boolean {
    return this.documentMarkMode === 'focus';
  }

  get canSaveSelectedBbox(): boolean {
    return this.unsavedBboxAdjustmentCount > 0 && this.bboxSaveStatus !== 'submitting';
  }

  get canAddBoundingBox(): boolean {
    return this.bboxEditEnabled && !!this.selectedField;
  }

  get bboxEditStatusLabel(): string {
    if (!this.bboxEditEnabled) return '';
    if (this.bboxSaveStatus === 'submitting') return 'Saving box to Falcon';
    if (this.bboxSaveStatus === 'error') return this.bboxSaveMessage || 'Box save failed';
    if (this.bboxSaveStatus === 'success' && this.bboxSaveMessage) return this.bboxSaveMessage;
    if (this.unsavedBboxAdjustmentCount) return `${this.unsavedBboxAdjustmentCount} unsaved boxes`;
    if (!this.selectedField?.bbox) return 'Click Add Box to place a new box';
    if (this.bboxSavedFieldIds.has(this.bboxKey(this.selectedField))) return 'Box saved';
    return 'Drag or resize selected box';
  }

  get unsavedBboxAdjustmentCount(): number {
    return this.draftBboxAdjustments().length;
  }

  get savedBboxAdjustmentCount(): number {
    return this.bboxSavedFieldIds.size;
  }

  get pendingFields(): ReviewField[] {
    return this.fields.filter((field) => field.status === 'pending' || field.status === 'editing');
  }

  get lowPendingFields(): ReviewField[] {
    return this.fields.filter((field) => field.level === 'low' && (field.status === 'pending' || field.status === 'editing'));
  }

  get canBatchAcceptHigh(): boolean {
    return this.highPendingFields.length > 0 && this.submitStatus !== 'submitting';
  }

  get canBatchAcceptPending(): boolean {
    return this.pendingFields.length > 0 && this.submitStatus !== 'submitting';
  }

  get canDownloadDriverHistory(): boolean {
    return this.driverHistoryExportStatus !== 'submitting'
      && !!this.driverHistoryFromDate
      && !!this.driverHistoryToDate;
  }

  get driverHistoryMaxDate(): string {
    return this.todayInputDate();
  }

  get totalFields(): number {
    return this.fields.length;
  }

  get extractedFieldCount(): number {
    return this.fields.filter((field) => this.isExtractedReviewField(field)).length;
  }

  get totalExtractedFieldCount(): number {
    return (this.mvrData?.drivers ?? []).reduce((total, driver) => {
      return total + this.buildReviewFields(driver).filter((field) => this.isExtractedReviewField(field)).length;
    }, 0);
  }

  get lowConfidenceFieldCount(): number {
    return this.fields.filter((field) => this.isLowConfidenceReviewField(field)).length;
  }

  get totalLowConfidenceFieldCount(): number {
    return (this.mvrData?.drivers ?? []).reduce((total, driver) => {
      return total + this.buildReviewFields(driver).filter((field) => this.isLowConfidenceReviewField(field)).length;
    }, 0);
  }

  get mvrProviderLabel(): string {
    return this.mvrProvider || 'Not Found';
  }

  get completedFields(): number {
    return this.acceptedCount + this.rejectedCount;
  }

  get completionPercent(): number {
    return this.totalFields ? Math.round((this.completedFields / this.totalFields) * 100) : 0;
  }

  get lowFields(): ReviewField[] {
    return this.fields.filter((field) => field.level === 'low' && field.status !== 'accepted');
  }

  get verificationFields(): ReviewField[] {
    return this.fields.filter((field) => field.status !== 'accepted');
  }

  get highFields(): ReviewField[] {
    return this.fields.filter((field) => field.level === 'high');
  }

  private get highPendingFields(): ReviewField[] {
    return this.fields.filter((field) => field.level === 'high' && (field.status === 'pending' || field.status === 'editing'));
  }

  get acceptedCount(): number {
    return this.fields.filter((field) => field.status === 'accepted').length;
  }

  get rejectedCount(): number {
    return this.fields.filter((field) => field.status === 'rejected').length;
  }

  get editedCount(): number {
    return this.editedFieldIds.size;
  }

  isFieldEdited(field: ReviewField): boolean {
    return this.editedFieldIds.has(field.id);
  }

  highFieldStatusLabel(field: ReviewField): string {
    if (this.isFieldEdited(field)) return 'Edited';
    if (field.status === 'accepted') return 'Accepted';
    if (field.status === 'rejected') return 'Rejected';
    if (field.status === 'editing') return 'Editing';
    return 'Ready';
  }

  isUserReviewedField(field: ReviewField): boolean {
    return field.userReviewed || field.status === 'accepted';
  }

  isClassADriverExperienceField(field: ReviewField): boolean {
    return this.requiredFieldKey(field.rawName) === this.requiredFieldKey("Two Years of Class A Driver's Experience");
  }

  displayReviewFieldValue(field: ReviewField): string {
    if (this.isClassADriverExperienceField(field)) {
      if (!field.value.trim()) return 'Not Found';
      return field.value.trim().toLowerCase() === 'true' ? 'True' : 'False';
    }
    return this.displayExtractedValue(field.value);
  }

  hasExtractedValue(value: string | null | undefined): boolean {
    return !!String(value ?? '').trim();
  }

  displayExtractedValue(value: string | null | undefined): string {
    return this.hasExtractedValue(value) ? String(value).trim() : 'Not Found';
  }

  canSaveEditedField(field: ReviewField): boolean {
    const originalValue = this.editOriginalValues.get(field.id) ?? field.documentValue ?? '';
    const valueChanged = field.value.trim() !== originalValue.trim();
    const commentChanged = (field.validationComment ?? '') !== (this.editOriginalValidationComments.get(field.id) ?? '');
    const reasonIsSelected = this.isClassADriverExperienceField(field) || this.selectedReason !== this.reasons[0];
    return field.status === 'editing' && this.submitStatus !== 'submitting' && reasonIsSelected && (valueChanged || commentChanged || this.isBboxDraft(field));
  }

  get validationCount(): number {
    return this.validationItems.length;
  }

  isValidationItemEditing(item: ValidationItem): boolean {
    return this.activeValidationEditId === item.id;
  }

  canEditValidationItem(item: ValidationItem): boolean {
    const driver = this.mvrData?.drivers?.[item.driverIndex];
    if (!driver) return false;
    if (item.driverIndex === this.activeDriverIndex) return !!this.validationReviewField(item);
    return !!this.findValidationReviewField(item, [...this.buildReviewFields(driver), ...this.buildDetailDocumentFields(driver)]);
  }

  startValidationItemEdit(item: ValidationItem): void {
    const field = this.activateValidationReviewField(item, false);
    if (!field) {
      this.validationFieldEditStatus[item.id] = 'error';
      this.validationFieldEditMessage[item.id] = 'This validation item is not mapped to an editable field.';
      return;
    }

    if (this.activeValidationEditId && this.activeValidationEditId !== item.id) {
      const previousItem = this.validationItems.find((validationItem) => validationItem.id === this.activeValidationEditId);
      if (previousItem) this.cancelValidationItemEdit(previousItem);
    }

    this.activeValidationEditId = item.id;
    this.activeValidationEditFieldId = field.id;
    this.validationFieldEditValues[item.id] = this.isMissingRequiredValue(field.value) ? '' : field.value;
    this.validationFieldEditStatus[item.id] = 'idle';
    this.validationFieldEditMessage[item.id] = '';
    this.selectedReason = this.reasons[0];
    this.prepareValidationFieldForEdit(field);
  }

  onValidationItemValueChange(item: ValidationItem, value: string): void {
    this.validationFieldEditValues[item.id] = value;
    const field = this.validationReviewField(item);
    if (!field || this.activeValidationEditId !== item.id) return;
    this.onFieldValueChange(field, value);
  }

  canSaveValidationItem(item: ValidationItem): boolean {
    if (this.activeValidationEditId !== item.id || this.submitStatus === 'submitting') return false;
    if (this.selectedReason === this.reasons[0]) return false;

    const field = this.validationReviewField(item);
    if (!field) return false;

    const nextValue = String(this.validationFieldEditValues[item.id] ?? '').trim();
    if (this.isMissingRequiredValue(nextValue) && !this.isBboxDraft(field)) return false;

    return true;
  }

  async saveValidationItemEdit(item: ValidationItem): Promise<void> {
    const field = this.activateValidationReviewField(item, false);
    if (!field || !this.canSaveValidationItem(item)) return;

    this.validationFieldEditStatus[item.id] = 'submitting';
    this.validationFieldEditMessage[item.id] = 'Saving validation fix.';
    field.value = String(this.validationFieldEditValues[item.id] ?? '').trim();
    field.status = 'editing';

    await this.acceptField(field, true);
    this.activeTab = 'validation';

    if (this.submitStatus === 'success') {
      this.validationFieldEditStatus[item.id] = 'success';
      this.validationFieldEditMessage[item.id] = 'Validation fix saved.';
      this.validationItems = this.validationItems.filter((validationItem) => validationItem.id !== item.id);
      this.finalSubmitAfterValidationFixPending = true;
      delete this.validationFieldEditValues[item.id];
      delete this.validationFieldEditStatus[item.id];
      delete this.validationFieldEditMessage[item.id];
      this.activeValidationEditId = '';
      this.activeValidationEditFieldId = '';
      if (!this.validationItems.length && !this.submittedToFalcon) {
        this.rememberCurrentDriverFieldState();
        this.showSubmitModal = false;
        await this.submitToFalcon();
      }
      return;
    }

    this.validationFieldEditStatus[item.id] = 'error';
    this.validationFieldEditMessage[item.id] = this.submitMessage || 'Unable to save validation fix.';
  }

  cancelValidationItemEdit(item: ValidationItem): void {
    const field = this.validationReviewField(item);
    if (field && field.status === 'editing') this.cancelEdit(field);
    this.activeTab = 'validation';
    delete this.validationFieldEditValues[item.id];
    delete this.validationFieldEditStatus[item.id];
    delete this.validationFieldEditMessage[item.id];
    if (this.activeValidationEditId === item.id) {
      this.activeValidationEditId = '';
      this.activeValidationEditFieldId = '';
    }
  }


  get changeLogCount(): number {
    return this.changeLogEntries.length;
  }

  get violationCount(): number {
    const driver = this.currentDriver;
    if (!driver) return 0;
    return this.toArray(driver[VIOLATIONS_FIELD]).length || Number.parseInt(this.fieldValue(driver[VIOLATIONS_COUNT_FIELD]), 10) || 0;
  }

  get suspensionCount(): number {
    const driver = this.currentDriver;
    if (!driver) return 0;
    return this.toArray(driver[SUSPENSIONS_FIELD]).length || Number.parseInt(this.fieldValue(driver[SUSPENSIONS_COUNT_FIELD]), 10) || 0;
  }

  get drivingRecordCount(): number {
    return this.violationCount + this.suspensionCount;
  }

  get violationSummary(): string {
    const driver = this.currentDriver;
    return driver ? this.fieldValue(driver[VIOLATIONS_SUMMARY_FIELD]) : '';
  }

  get suspensionSummary(): string {
    const driver = this.currentDriver;
    return driver ? this.fieldValue(driver[SUSPENSIONS_SUMMARY_FIELD]) : '';
  }

  get violationItems(): ViolationItem[] {
    const driver = this.currentDriver;
    if (!driver) return [];

    return this.toArray(driver[VIOLATIONS_FIELD]).map((record, index) => {
      const type = this.fieldValue(record['Type']) || 'VIOL';
      const violationDate = this.fieldValue(record['Violation Date']);
      const convictionDate = this.fieldValue(record['Conviction Date']);
      const stateCode = this.fieldValue(record['St Code']);
      const violationCode = this.fieldValue(record['Viol Code']);
      const descriptionOptions = this.violationDescriptionOptions(record['Description']);
      const currentDescription = this.fieldValue(record['Description']);
      const selectedDescription = this.selectedViolationDescription(index, descriptionOptions, currentDescription);
      const selectedDescriptionOption = this.descriptionOptionForValue(descriptionOptions, selectedDescription);
      const description = selectedDescription || currentDescription;
      const originalDescription = this.violationOriginalDescription(record['Description']);
      const speed = this.fieldValue(record['Speed']);
      const location = this.fieldValue(record['Location']);
      const points = this.fieldValue(record['Points']);

      return {
        type,
        violationDate,
        convictionDate,
        stateCode,
        violationCode,
        description,
        originalDescription,
        descriptionConfidence: selectedDescriptionOption?.confidence ?? null,
        descriptionOptions,
        speed,
        location,
        points,
        fieldIds: {
          type: this.detailFieldId([VIOLATIONS_FIELD, index, 'Type']),
          violationDate: this.detailFieldId([VIOLATIONS_FIELD, index, 'Violation Date']),
          convictionDate: this.detailFieldId([VIOLATIONS_FIELD, index, 'Conviction Date']),
          stateCode: this.detailFieldId([VIOLATIONS_FIELD, index, 'St Code']),
          location: this.detailFieldId([VIOLATIONS_FIELD, index, 'Location']),
          violationCode: this.detailFieldId([VIOLATIONS_FIELD, index, 'Viol Code']),
          speed: this.detailFieldId([VIOLATIONS_FIELD, index, 'Speed']),
          points: this.detailFieldId([VIOLATIONS_FIELD, index, 'Points']),
          description: this.detailFieldId([VIOLATIONS_FIELD, index, 'Description']),
          originalDescription: this.detailFieldId([VIOLATIONS_FIELD, index, 'Description']),
        },
        summary: [violationDate, type, description].filter(Boolean).join(' · '),
      };
    });
  }

  get canSaveViolation(): boolean {
    return this.violationSaveStatus !== 'submitting';
  }

  get suspensionItems(): SuspensionItem[] {
    const driver = this.currentDriver;
    if (!driver) return [];

    return this.toArray(driver[SUSPENSIONS_FIELD]).map((record, index) => {
      const action = this.fieldValue(record['Action']);
      const orderDate = this.fieldValue(record['Order Date']);
      const effectiveDate = this.fieldValue(record['Effective Date']);
      const clearDate = this.fieldValue(record['Clear Date']);
      const endDate = this.fieldValue(record['End Date']);
      const code = this.fieldValue(record['Code']);
      const avdCode = this.fieldValue(record['AVD Code']);
      const description = this.fieldValue(record['Description']);

      return {
        action,
        orderDate,
        effectiveDate,
        clearDate,
        endDate,
        code,
        avdCode,
        description,
        summary: [effectiveDate || orderDate, action, description].filter(Boolean).join(' - '),
        fieldIds: {
          action: this.detailFieldId([SUSPENSIONS_FIELD, index, 'Action']),
          orderDate: this.detailFieldId([SUSPENSIONS_FIELD, index, 'Order Date']),
          effectiveDate: this.detailFieldId([SUSPENSIONS_FIELD, index, 'Effective Date']),
          clearDate: this.detailFieldId([SUSPENSIONS_FIELD, index, 'Clear Date']),
          endDate: this.detailFieldId([SUSPENSIONS_FIELD, index, 'End Date']),
          code: this.detailFieldId([SUSPENSIONS_FIELD, index, 'Code']),
          avdCode: this.detailFieldId([SUSPENSIONS_FIELD, index, 'AVD Code']),
          description: this.detailFieldId([SUSPENSIONS_FIELD, index, 'Description']),
        },
      };
    });
  }

  suspensionFieldRows(suspension: SuspensionItem, suspensionIndex: number): SuspensionFieldRow[] {
    return [
      this.suspensionFieldRow(suspensionIndex, 'action', 'Action', 'Action', suspension.action, suspension.fieldIds['action']),
      this.suspensionFieldRow(suspensionIndex, 'orderDate', 'Order Date', 'Order Date', suspension.orderDate, suspension.fieldIds['orderDate']),
      this.suspensionFieldRow(suspensionIndex, 'effectiveDate', 'Effective Date', 'Effective Date', suspension.effectiveDate, suspension.fieldIds['effectiveDate']),
      this.suspensionFieldRow(suspensionIndex, 'clearDate', 'Clear Date', 'Clear Date', suspension.clearDate, suspension.fieldIds['clearDate']),
      this.suspensionFieldRow(suspensionIndex, 'endDate', 'End Date', 'End Date', suspension.endDate, suspension.fieldIds['endDate']),
      this.suspensionFieldRow(suspensionIndex, 'code', 'Code', 'Code', suspension.code, suspension.fieldIds['code']),
      this.suspensionFieldRow(suspensionIndex, 'avdCode', 'AVD Code', 'AVD Code', suspension.avdCode, suspension.fieldIds['avdCode']),
      this.suspensionFieldRow(suspensionIndex, 'description', 'Description', 'Description', suspension.description, suspension.fieldIds['description']),
    ];
  }

  private suspensionFieldRow(
    suspensionIndex: number,
    key: string,
    label: string,
    jsonField: string,
    value: string,
    fieldId: string,
  ): SuspensionFieldRow {
    return {
      key,
      label,
      jsonField,
      value,
      fieldId,
      confidence: this.suspensionFieldConfidence(suspensionIndex, jsonField),
    };
  }

  private suspensionFieldConfidence(suspensionIndex: number, jsonField: string): number {
    const record = this.toArray(this.currentDriver?.[SUSPENSIONS_FIELD])[suspensionIndex];
    const fieldData = record?.[jsonField] as FieldData | undefined;
    return fieldData ? this.confidenceFor(jsonField, fieldData) : 80;
  }

  violationFieldRows(violation: ViolationItem, violationIndex: number): ViolationFieldRow[] {
    return [
      this.violationFieldRow(violationIndex, 'type', 'Type', 'Type', violation.type, violation.fieldIds['type']),
      this.violationFieldRow(violationIndex, 'violationDate', 'Violation Date', 'Violation Date', violation.violationDate, violation.fieldIds['violationDate']),
      this.violationFieldRow(violationIndex, 'convictionDate', 'Conviction Date', 'Conviction Date', violation.convictionDate, violation.fieldIds['convictionDate']),
      this.violationFieldRow(violationIndex, 'stateCode', 'ST Code', 'St Code', violation.stateCode, violation.fieldIds['stateCode']),
      this.violationFieldRow(violationIndex, 'location', 'Location', 'Location', violation.location, violation.fieldIds['location']),
      this.violationFieldRow(violationIndex, 'violationCode', 'Viol Code', 'Viol Code', violation.violationCode, violation.fieldIds['violationCode']),
      this.violationFieldRow(violationIndex, 'speed', 'Speed', 'Speed', violation.speed, violation.fieldIds['speed']),
      this.violationFieldRow(violationIndex, 'points', 'Points', 'Points', violation.points, violation.fieldIds['points']),
      this.violationFieldRow(violationIndex, 'originalDescription', 'Original Value', ORIGINAL_DESCRIPTION_FIELD, violation.originalDescription, violation.fieldIds['originalDescription']),
      {
        ...this.violationFieldRow(violationIndex, 'description', 'Falcon Description', 'Description', violation.description, violation.fieldIds['description']),
        isDescription: true,
        descriptionConfidence: violation.descriptionConfidence,
        descriptionOptions: violation.descriptionOptions,
      },
    ];
  }

  private violationFieldRow(
    violationIndex: number,
    key: string,
    label: string,
    jsonField: string,
    value: string,
    fieldId: string,
  ): ViolationFieldRow {
    return {
      key,
      label,
      jsonField,
      value,
      fieldId,
      confidence: this.violationFieldConfidence(violationIndex, jsonField),
    };
  }

  private violationFieldConfidence(violationIndex: number, jsonField: string): number {
    const record = this.toArray(this.currentDriver?.[VIOLATIONS_FIELD])[violationIndex];
    const fieldData = record?.[jsonField] as FieldData | undefined;
    return fieldData ? this.confidenceFor(jsonField, fieldData) : 80;
  }

  descriptionSelectionKey(violationIndex: number): string {
    return `${this.activeDriverIndex}:${violationIndex}`;
  }

  onViolationDescriptionChange(violationIndex: number, value: string): void {
    this.descriptionSelections[this.descriptionSelectionKey(violationIndex)] = value;
  }

  descriptionDropdownKey(violationIndex: number): string {
    return this.descriptionSelectionKey(violationIndex);
  }

  isDescriptionDropdownOpen(violationIndex: number): boolean {
    return this.descriptionDropdownOpenKey === this.descriptionDropdownKey(violationIndex);
  }

  toggleDescriptionDropdown(violationIndex: number, event?: MouseEvent): void {
    const key = this.descriptionDropdownKey(violationIndex);
    if (this.descriptionDropdownOpenKey === key) {
      this.descriptionDropdownOpenKey = '';
      this.descriptionDropdownStyle = {};
      return;
    }
    this.descriptionDropdownOpenKey = key;
    this.descriptionSearchTerms[key] = '';
    this.positionDescriptionDropdown(event?.currentTarget instanceof HTMLElement ? event.currentTarget : null);
  }

  closeDescriptionDropdown(violationIndex: number): void {
    if (this.descriptionDropdownOpenKey === this.descriptionDropdownKey(violationIndex)) {
      this.descriptionDropdownOpenKey = '';
      this.descriptionDropdownStyle = {};
    }
  }

  onDescriptionSearch(violationIndex: number, value: string): void {
    this.descriptionSearchTerms[this.descriptionDropdownKey(violationIndex)] = value;
  }

  filteredDescriptionOptions(violationIndex: number, options: DescriptionOption[] | undefined): DescriptionOption[] {
    const source = options ?? [];
    const query = String(this.descriptionSearchTerms[this.descriptionDropdownKey(violationIndex)] ?? '').trim().toLowerCase();
    const filtered = query
      ? source.filter((option) => `${option.svcCode ?? ''} ${option.value}`.toLowerCase().includes(query))
      : source;
    if (query) return filtered.slice(0, 80);
    const topOptions = filtered.slice(0, 5);
    const topOptionsHaveOnlyZeroConfidence = topOptions.length > 0 && topOptions.every((option) => option.confidence === 0);
    return topOptionsHaveOnlyZeroConfidence ? filtered : topOptions;
  }

  descriptionOptionMeta(option: DescriptionOption): string {
    const parts = [
      option.svcCode,
      option.confidence !== null ? `${option.confidence}%` : '',
    ].filter(Boolean);
    return parts.join(' · ');
  }

  descriptionOptionDisplay(option: DescriptionOption): string {
    return `${option.value}${option.confidence !== null ? ` (${option.confidence}%)` : ''}`;
  }

  selectViolationDescriptionOption(violationIndex: number, option: DescriptionOption): void {
    this.onViolationDescriptionChange(violationIndex, option.value);
    this.descriptionDropdownOpenKey = '';
    this.descriptionDropdownStyle = {};
  }

  private positionDescriptionDropdown(trigger: HTMLElement | null): void {
    if (!trigger) {
      this.descriptionDropdownStyle = {};
      return;
    }
    const triggerRect = trigger.getBoundingClientRect();
    const sidebarRect = trigger.closest('.review-sidebar')?.getBoundingClientRect();
    const topBoundary = sidebarRect ? sidebarRect.top + 112 : 88;
    const top = Math.max(8, topBoundary);
    const availableHeight = Math.max(260, triggerRect.top - top - 10);
    const viewportPadding = 10;
    const width = Math.min(820, window.innerWidth - (viewportPadding * 2), sidebarRect?.width ?? 820);
    const left = Math.min(
      Math.max(viewportPadding, triggerRect.left - 130),
      window.innerWidth - width - viewportPadding,
    );
    this.descriptionDropdownStyle = {
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${Math.min(availableHeight, window.innerHeight - top - 96)}px`,
    };
  }

  violationFieldEditKey(violationIndex: number, jsonField: string): string {
    return `${this.activeDriverIndex}:${violationIndex}:${jsonField}`;
  }

  isEditingViolationField(violationIndex: number, jsonField: string): boolean {
    return this.violationFieldEditStatus[this.violationFieldEditKey(violationIndex, jsonField)] === 'submitting'
      || this.violationFieldEditValues[this.violationFieldEditKey(violationIndex, jsonField)] !== undefined;
  }

  canSaveViolationEditedField(violationIndex: number, jsonField: string): boolean {
    const record = this.toArray(this.currentDriver?.[VIOLATIONS_FIELD])[violationIndex];
    if (!record || this.selectedReason === this.reasons[0]) return false;
    const key = this.violationFieldEditKey(violationIndex, jsonField);
    if (this.violationFieldEditStatus[key] === 'submitting') return false;
    const nextValue = String(this.violationFieldEditValues[key] ?? '').trim();
    const previousValue = this.violationValueForEdit(record, jsonField).trim();
    const editField = this.violationEditReviewField(violationIndex, jsonField);
    return nextValue !== previousValue || (!!editField && this.isBboxDraft(editField));
  }

  startViolationFieldEdit(violationIndex: number, jsonField: string, value: string, label = jsonField): void {
    if (
      this.activeViolationEditIndex !== null
      && this.activeViolationEditJsonField
      && (this.activeViolationEditIndex !== violationIndex || this.activeViolationEditJsonField !== jsonField)
    ) {
      const previousKey = this.violationFieldEditKey(this.activeViolationEditIndex, this.activeViolationEditJsonField);
      delete this.violationFieldEditValues[previousKey];
      delete this.violationFieldEditStatus[previousKey];
      delete this.violationFieldEditMessage[previousKey];
    }

    const key = this.violationFieldEditKey(violationIndex, jsonField);
    this.violationFieldEditValues[key] = value;
    this.violationFieldEditStatus[key] = 'idle';
    this.violationFieldEditMessage[key] = '';
    this.activeViolationEditIndex = violationIndex;
    this.activeViolationEditJsonField = jsonField;
    this.activeViolationEditLabel = label;
    this.selectedReason = this.reasons[0];
    this.prepareViolationFieldBoxEdit(violationIndex, jsonField, value, label);
  }

  startViolationFieldEditFromSelect(violationIndex: number, jsonField: string): void {
    const config = this.violationEditableFields.find((field) => field.jsonField === jsonField);
    const item = this.violationItems[violationIndex];
    if (!config || !item) return;
    const value = String(item[config.valueKey] ?? '');
    this.startViolationFieldEdit(violationIndex, config.jsonField, value, config.label);
  }

  cancelViolationFieldEdit(violationIndex: number, jsonField: string): void {
    const key = this.violationFieldEditKey(violationIndex, jsonField);
    delete this.violationFieldEditValues[key];
    delete this.violationFieldEditStatus[key];
    delete this.violationFieldEditMessage[key];
    if (this.activeViolationEditIndex === violationIndex && this.activeViolationEditJsonField === jsonField) {
      this.activeViolationEditIndex = null;
      this.activeViolationEditJsonField = '';
      this.activeViolationEditLabel = '';
      this.selectedReason = this.reasons[0];
    }
  }

  suspensionFieldEditKey(suspensionIndex: number, jsonField: string): string {
    return `${this.activeDriverIndex}:suspension:${suspensionIndex}:${jsonField}`;
  }

  isEditingSuspensionField(suspensionIndex: number, jsonField: string): boolean {
    const key = this.suspensionFieldEditKey(suspensionIndex, jsonField);
    return this.suspensionFieldEditStatus[key] === 'submitting' || this.suspensionFieldEditValues[key] !== undefined;
  }

  canSaveSuspensionEditedField(suspensionIndex: number, jsonField: string): boolean {
    const record = this.toArray(this.currentDriver?.[SUSPENSIONS_FIELD])[suspensionIndex];
    if (!record || this.selectedReason === this.reasons[0]) return false;
    const key = this.suspensionFieldEditKey(suspensionIndex, jsonField);
    if (this.suspensionFieldEditStatus[key] === 'submitting') return false;
    const nextValue = String(this.suspensionFieldEditValues[key] ?? '').trim();
    const previousValue = this.suspensionValueForEdit(record, jsonField).trim();
    const editField = this.suspensionEditReviewField(suspensionIndex, jsonField);
    return nextValue !== previousValue || (!!editField && this.isBboxDraft(editField));
  }

  startSuspensionFieldEdit(suspensionIndex: number, jsonField: string, value: string, label = jsonField): void {
    if (
      this.activeSuspensionEditIndex !== null
      && this.activeSuspensionEditJsonField
      && (this.activeSuspensionEditIndex !== suspensionIndex || this.activeSuspensionEditJsonField !== jsonField)
    ) {
      const previousKey = this.suspensionFieldEditKey(this.activeSuspensionEditIndex, this.activeSuspensionEditJsonField);
      delete this.suspensionFieldEditValues[previousKey];
      delete this.suspensionFieldEditStatus[previousKey];
      delete this.suspensionFieldEditMessage[previousKey];
    }

    const key = this.suspensionFieldEditKey(suspensionIndex, jsonField);
    this.suspensionFieldEditValues[key] = value;
    this.suspensionFieldEditStatus[key] = 'idle';
    this.suspensionFieldEditMessage[key] = '';
    this.activeSuspensionEditIndex = suspensionIndex;
    this.activeSuspensionEditJsonField = jsonField;
    this.activeSuspensionEditLabel = label;
    this.selectedReason = this.reasons[0];
    this.prepareSuspensionFieldBoxEdit(suspensionIndex, jsonField, value, label);
  }

  cancelSuspensionFieldEdit(suspensionIndex: number, jsonField: string): void {
    const key = this.suspensionFieldEditKey(suspensionIndex, jsonField);
    delete this.suspensionFieldEditValues[key];
    delete this.suspensionFieldEditStatus[key];
    delete this.suspensionFieldEditMessage[key];
    if (this.activeSuspensionEditIndex === suspensionIndex && this.activeSuspensionEditJsonField === jsonField) {
      this.activeSuspensionEditIndex = null;
      this.activeSuspensionEditJsonField = '';
      this.activeSuspensionEditLabel = '';
      this.selectedReason = this.reasons[0];
    }
  }

  private violationEditReviewField(violationIndex: number, jsonField: string): ReviewField | undefined {
    const id = this.detailFieldId(this.violationJsonPath(violationIndex, jsonField));
    return this.detailDocumentFields.find((field) => field.id === id)
      ?? this.fields.find((field) => field.id === id);
  }

  private suspensionEditReviewField(suspensionIndex: number, jsonField: string): ReviewField | undefined {
    const id = this.detailFieldId(this.suspensionJsonPath(suspensionIndex, jsonField));
    return this.detailDocumentFields.find((field) => field.id === id)
      ?? this.fields.find((field) => field.id === id);
  }

  private prepareViolationFieldBoxEdit(violationIndex: number, jsonField: string, value: string, label: string): void {
    const driver = this.currentDriver;
    const record = this.toArray(driver?.[VIOLATIONS_FIELD])[violationIndex];
    const path = this.violationJsonPath(violationIndex, jsonField);
    const id = this.detailFieldId(path);
    const fieldData = record ? this.violationFieldDataForEdit(record, jsonField) : undefined;
    const existingBbox = fieldData ? this.fieldBbox(fieldData) : undefined;
    const page = fieldData?.source_llm?.page ?? fieldData?.page ?? this.currentPdfPage;

    let field = this.detailDocumentFields.find((item) => item.id === id);
    if (!field) {
      const confidence = fieldData ? this.confidenceFor(jsonField, fieldData) : 80;
      field = {
        id,
        label: `Violation ${violationIndex + 1}: ${label}`,
        value,
        documentValue: value,
        confidence,
        level: this.confidenceLevelFor(value, confidence),
        source: 'VIOLATION',
        evidence: `${label} appears in the violation record.`,
        helper: `Review the highlighted ${label} value.`,
        page,
        bbox: existingBbox,
        unit: fieldData?.unit ?? (existingBbox ? 'inch' : null),
        status: 'pending',
        rawName: path.map((part) => (typeof part === 'number' ? String(part + 1) : part)).join(' '),
        jsonPath: path,
      };
      this.detailDocumentFields.push(field);
    }

    field.value = value;
    field.documentValue = field.documentValue ?? value;
    field.page = field.page ?? page;
    field.unit = field.unit ?? (field.bbox ? 'inch' : null);
    if (!field.bbox) {
      field.page = this.currentPdfPage;
      field.unit = 'inch';
      field.bbox = this.defaultBboxForCurrentView();
    }

    this.bboxEditEnabled = true;
    this.selectedFieldId = field.id;
    this.bboxOverrides.set(this.bboxKey(field), [...field.bbox]);
    this.bboxSaveStatus = 'idle';
    this.bboxSaveMessage = `${label} box ready. Drag or resize it, then save.`;
    void this.updatePdfPage(field.page ?? this.currentPdfPage).then(() => this.scrollPdfToField(field));
  }

  private prepareSuspensionFieldBoxEdit(suspensionIndex: number, jsonField: string, value: string, label: string): void {
    const driver = this.currentDriver;
    const record = this.toArray(driver?.[SUSPENSIONS_FIELD])[suspensionIndex];
    const path = this.suspensionJsonPath(suspensionIndex, jsonField);
    const id = this.detailFieldId(path);
    const fieldData = record ? this.suspensionFieldDataForEdit(record, jsonField) : undefined;
    const existingBbox = fieldData ? this.fieldBbox(fieldData) : undefined;
    const page = fieldData?.source_llm?.page ?? fieldData?.page ?? this.currentPdfPage;

    let field = this.detailDocumentFields.find((item) => item.id === id);
    if (!field) {
      const confidence = fieldData ? this.confidenceFor(jsonField, fieldData) : 80;
      field = {
        id,
        label: `Suspensions ${suspensionIndex + 1}: ${label}`,
        value,
        documentValue: value,
        confidence,
        level: this.confidenceLevelFor(value, confidence),
        source: 'SUSPENSION',
        evidence: `${label} appears in the suspension record.`,
        helper: `Review the highlighted ${label} value.`,
        page,
        bbox: existingBbox,
        unit: fieldData?.unit ?? (existingBbox ? 'inch' : null),
        status: 'pending',
        rawName: path.map((part) => (typeof part === 'number' ? String(part + 1) : part)).join(' '),
        jsonPath: path,
      };
      this.detailDocumentFields.push(field);
    }

    field.value = value;
    field.documentValue = field.documentValue ?? value;
    field.page = field.page ?? page;
    field.unit = field.unit ?? (field.bbox ? 'inch' : null);
    if (!field.bbox) {
      field.page = this.currentPdfPage;
      field.unit = 'inch';
      field.bbox = this.defaultBboxForCurrentView();
    }

    this.bboxEditEnabled = true;
    this.selectedFieldId = field.id;
    this.bboxOverrides.set(this.bboxKey(field), [...field.bbox]);
    this.bboxSaveStatus = 'idle';
    this.bboxSaveMessage = `${label} box ready. Drag or resize it, then save.`;
    void this.updatePdfPage(field.page ?? this.currentPdfPage).then(() => this.scrollPdfToField(field));
  }

  async saveViolationExistingField(violationIndex: number, jsonField: string): Promise<void> {
    const driver = this.currentDriver;
    const record = this.toArray(driver?.[VIOLATIONS_FIELD])[violationIndex];
    const key = this.violationFieldEditKey(violationIndex, jsonField);
    if (!driver || !record || !this.batchId) {
      this.violationFieldEditStatus[key] = 'error';
      this.violationFieldEditMessage[key] = 'Missing batch or violation record.';
      return;
    }

    const value = String(this.violationFieldEditValues[key] ?? '').trim();
    if (this.selectedReason === this.reasons[0]) {
      this.violationFieldEditStatus[key] = 'error';
      this.violationFieldEditMessage[key] = 'Select a reason before saving.';
      return;
    }
    const fieldData = this.violationFieldDataForEdit(record, jsonField);
    const editField = this.violationEditReviewField(violationIndex, jsonField);
    if (editField) editField.value = value;
    const usesBbox = !!editField && this.isBboxDraft(editField);
    const reasonComment = `Reviewer correction: ${this.selectedReason}`;
    const operation: ReviewFeedbackOperation = usesBbox
      ? { ...this.reviewOperationForBboxAdjustment(this.bboxAdjustmentFor(editField)), comment: reasonComment }
      : {
          action: 'update',
          path: ['drivers', this.fileLocalDriverIndex(this.activeDriverIndex), ...this.violationJsonPath(violationIndex, jsonField)],
          value,
          bbox: fieldData ? this.fieldBbox(fieldData) ?? [] : [],
          page: fieldData?.page ?? null,
          unit: fieldData?.unit ?? null,
          comment: reasonComment,
        };

    this.violationFieldEditStatus[key] = 'submitting';
    this.violationFieldEditMessage[key] = 'Saving.';

    try {
      const token = await this.getMvrToken();
      const response = usesBbox
        ? await this.submitMvrReviewFeedbackByBbox(token, [operation])
        : await this.submitMvrReviewFeedback(token, [operation]);
      const previousValue = this.violationValueForEdit(record, jsonField) || '--';
      const reviewedValue = editField ? this.reviewedViolationFieldValue(response.reviewed_json, editField, jsonField) : '';
      const savedValue = reviewedValue || value;
      const reviewedRecord = this.reviewedViolationRecord(response.reviewed_json, this.activeDriverIndex, violationIndex);
      const reviewedDescription = reviewedRecord?.['Description'];
      if (jsonField === ORIGINAL_DESCRIPTION_FIELD && reviewedDescription) {
        record['Description'] = reviewedDescription;
        this.descriptionSelections[this.descriptionSelectionKey(violationIndex)] = this.fieldValue(reviewedDescription);
      } else {
        this.patchViolationSimpleField(record, jsonField, savedValue, fieldData);
      }
      if (editField) {
        editField.value = savedValue;
        editField.documentValue = savedValue;
      }
      if (usesBbox && editField) {
        const bboxKey = this.bboxKey(editField);
        this.bboxDraftFieldIds.delete(bboxKey);
        this.bboxSavedFieldIds.add(bboxKey);
        this.bboxSavedAdjustments.set(bboxKey, this.bboxAdjustmentFor(editField));
        this.bboxEditEnabled = false;
      }
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      this.addChangeLogEntry(`Violation ${jsonField}`, previousValue, savedValue || '--', 'Violation field edit');
      delete this.violationFieldEditValues[key];
      this.violationFieldEditStatus[key] = 'success';
      delete this.violationFieldEditMessage[key];
      this.activeViolationEditIndex = null;
      this.activeViolationEditJsonField = '';
      this.activeViolationEditLabel = '';
      this.selectedReason = this.reasons[0];
    } catch (error) {
      this.violationFieldEditStatus[key] = 'error';
      this.violationFieldEditMessage[key] = error instanceof Error ? error.message : 'Unable to save field.';
    }
  }

  async saveSuspensionExistingField(suspensionIndex: number, jsonField: string): Promise<void> {
    const driver = this.currentDriver;
    const record = this.toArray(driver?.[SUSPENSIONS_FIELD])[suspensionIndex];
    const key = this.suspensionFieldEditKey(suspensionIndex, jsonField);
    if (!driver || !record || !this.batchId) {
      this.suspensionFieldEditStatus[key] = 'error';
      this.suspensionFieldEditMessage[key] = 'Missing batch or suspension record.';
      return;
    }

    const value = String(this.suspensionFieldEditValues[key] ?? '').trim();
    if (this.selectedReason === this.reasons[0]) {
      this.suspensionFieldEditStatus[key] = 'error';
      this.suspensionFieldEditMessage[key] = 'Select a reason before saving.';
      return;
    }

    const fieldData = this.suspensionFieldDataForEdit(record, jsonField);
    const editField = this.suspensionEditReviewField(suspensionIndex, jsonField);
    if (editField) editField.value = value;
    const usesBbox = !!editField && this.isBboxDraft(editField);
    const reasonComment = `Reviewer correction: ${this.selectedReason}`;
    const operation: ReviewFeedbackOperation = usesBbox
      ? { ...this.reviewOperationForBboxAdjustment(this.bboxAdjustmentFor(editField)), comment: reasonComment }
      : {
          action: 'update',
          path: ['drivers', this.fileLocalDriverIndex(this.activeDriverIndex), ...this.suspensionJsonPath(suspensionIndex, jsonField)],
          value,
          bbox: fieldData ? this.fieldBbox(fieldData) ?? [] : [],
          page: fieldData?.page ?? null,
          unit: fieldData?.unit ?? null,
          comment: reasonComment,
        };

    this.suspensionFieldEditStatus[key] = 'submitting';
    this.suspensionFieldEditMessage[key] = 'Saving.';

    try {
      const token = await this.getMvrToken();
      const response = usesBbox
        ? await this.submitMvrReviewFeedbackByBbox(token, [operation])
        : await this.submitMvrReviewFeedback(token, [operation]);
      const previousValue = this.suspensionValueForEdit(record, jsonField) || '--';
      const reviewedValue = editField ? this.reviewedSuspensionFieldValue(response.reviewed_json, editField, jsonField) : '';
      const savedValue = reviewedValue || value;

      this.patchSuspensionSimpleField(record, jsonField, savedValue, fieldData);
      if (editField) {
        editField.value = savedValue;
        editField.documentValue = savedValue;
      }
      if (usesBbox && editField) {
        const bboxKey = this.bboxKey(editField);
        this.bboxDraftFieldIds.delete(bboxKey);
        this.bboxSavedFieldIds.add(bboxKey);
        this.bboxSavedAdjustments.set(bboxKey, this.bboxAdjustmentFor(editField));
        this.bboxEditEnabled = false;
      }
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      this.addChangeLogEntry(`Suspension ${jsonField}`, previousValue, savedValue || '--', 'Suspension field edit');
      delete this.suspensionFieldEditValues[key];
      this.suspensionFieldEditStatus[key] = 'success';
      delete this.suspensionFieldEditMessage[key];
      this.activeSuspensionEditIndex = null;
      this.activeSuspensionEditJsonField = '';
      this.activeSuspensionEditLabel = '';
      this.selectedReason = this.reasons[0];
    } catch (error) {
      this.suspensionFieldEditStatus[key] = 'error';
      this.suspensionFieldEditMessage[key] = error instanceof Error ? error.message : 'Unable to save field.';
    }
  }

  async saveViolationDescription(violationIndex: number): Promise<void> {
    const driver = this.currentDriver;
    const record = this.toArray(driver?.[VIOLATIONS_FIELD])[violationIndex];
    if (!driver || !record || !this.batchId) {
      const key = this.descriptionSelectionKey(violationIndex);
      this.descriptionSaveStatus[key] = 'error';
      this.descriptionSaveMessage[key] = 'Missing batch or violation record.';
      return;
    }

    const options = this.violationDescriptionOptions(record['Description']);
    const currentDescription = this.fieldValue(record['Description']);
    const selectedValue = this.selectedViolationDescription(violationIndex, options, currentDescription);
    const selectedOption = this.descriptionOptionForValue(options, selectedValue);
    const key = this.descriptionSelectionKey(violationIndex);

    this.descriptionSaveStatus[key] = 'submitting';
    this.descriptionSaveMessage[key] = 'Saving description.';

    const descriptionOperation: ReviewFeedbackOperation = {
      action: 'update',
      path: ['drivers', this.fileLocalDriverIndex(this.activeDriverIndex), VIOLATIONS_FIELD, violationIndex, 'Description'],
      value: selectedValue,
      bbox: selectedOption?.bbox ? [...selectedOption.bbox] : [],
      page: selectedOption?.page ?? null,
      unit: selectedOption?.unit ?? (selectedOption?.bbox ? 'inch' : null),
      comment: 'Violation description selected by reviewer',
    };
    const operations = [descriptionOperation];
    if (selectedOption?.svcCode) {
      operations.push({
        action: 'update',
        path: ['drivers', this.fileLocalDriverIndex(this.activeDriverIndex), VIOLATIONS_FIELD, violationIndex, 'Viol Code'],
        value: selectedOption.svcCode,
        bbox: [],
        page: null,
        unit: null,
        comment: 'Violation code selected from description by reviewer',
      });
    }

    try {
      const token = await this.getMvrToken();
      const response = await this.submitMvrReviewFeedback(token, operations);
      const previousValue = this.fieldValue(record['Description']) || options[0]?.value || '--';
      const reviewedRecord = this.reviewedViolationRecord(response.reviewed_json, this.activeDriverIndex, violationIndex);
      const reviewedDescription = reviewedRecord?.['Description'];
      const reviewedViolationCode = reviewedRecord?.['Viol Code'];
      const responseValue = this.fieldValue(reviewedDescription);
      const savedValue = responseValue || selectedValue;

      if (reviewedDescription) {
        record['Description'] = reviewedDescription;
        this.descriptionSelections[key] = savedValue;
      } else {
        this.patchViolationDescription(record, selectedValue, selectedOption);
        this.descriptionSelections[key] = selectedValue;
      }

      if (reviewedViolationCode) {
        record['Viol Code'] = reviewedViolationCode;
      } else if (selectedOption?.svcCode) {
        this.patchViolationCode(record, selectedOption.svcCode);
      }
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      this.descriptionSaveStatus[key] = 'success';
      this.descriptionSaveMessage[key] = 'Description saved.';
      this.addChangeLogEntry('Violation Description', previousValue, savedValue || '--', 'Violation description update');
    } catch (error) {
      this.descriptionSaveStatus[key] = 'error';
      this.descriptionSaveMessage[key] = error instanceof Error ? error.message : 'Unable to save description.';
    }
  }

  get activeDriverName(): string {
    const driver = this.currentDriver;
    return driver ? this.driverName(driver) : this.quote.insured;
  }


  get driverSummaries(): DriverSummary[] {
    return (this.mvrData?.drivers ?? []).map((driver, index) => {
      const fields = this.buildReviewFields(driver);
      const filledCount = fields.filter((field) => this.isExtractedReviewField(field)).length;
      const lowCount = fields.filter((field) => this.isLowConfidenceReviewField(field)).length;
      const filledPercent = fields.length ? Math.round((filledCount / fields.length) * 100) : 0;
      const averageConfidence = fields.length
        ? Math.round(fields.reduce((total, field) => total + field.confidence, 0) / fields.length)
        : 0;

      return {
        index,
        name: this.driverName(driver),
        initials: this.driverInitials(driver),
        license: this.fieldValue(driver['License Number']) || 'No license',
        lowCount,
        extractedCount: filledCount,
        filledPercent,
        averageConfidence,
        status: lowCount ? 'Reviewing' : filledPercent >= 100 ? 'Verified' : 'Pending Data',
      };
    });
  }

  private isExtractedReviewField(field: ReviewField): boolean {
    return !!field.value.trim();
  }

  private isLowConfidenceReviewField(field: ReviewField): boolean {
    return field.level === 'low';
  }

  get driverDashboardRows(): DriverDashboardRow[] {
    const summaries = this.driverSummaries;
    const rows: DriverDashboardRow[] = [];

    for (let index = 0; index < summaries.length; index += 3) {
      const rowDrivers = summaries.slice(index, index + 3);

      rows.push({
        seq: rowDrivers[0],
        driver2: rowDrivers[1],
        driver3: rowDrivers[2],
      });
    }

    return rows;
  }

  get driverDashboardColumnClass(): string {
    return `driver-columns-${Math.min(3, Math.max(1, this.driverSummaries.length))}`;
  }

  get activeDriverPositionLabel(): string {
    return `Driver ${this.activeDriverIndex + 1} of ${this.driverSummaries.length}`;
  }

  get hasMultipleDrivers(): boolean {
    return this.driverSummaries.length > 1;
  }

  private get currentDriver(): DriverRecord | undefined {
    return this.mvrData?.drivers[this.activeDriverIndex];
  }

  get isProcessingUpload(): boolean {
    return ['reading', 'authenticating', 'queued', 'running'].includes(this.uploadStatus);
  }

  get canProcessUpload(): boolean {
    return this.uploadQueue.some((item) => item.status === 'pending' || item.status === 'error') && !this.isProcessingUpload;
  }

  get selectedPdfSize(): string {
    const totalSize = this.selectedPdfFiles.reduce((total, file) => total + file.size, 0);
    if (!totalSize) return '';
    const sizeKb = totalSize / 1024;
    return sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${Math.round(sizeKb)} KB`;
  }

  get selectedPdfCount(): number {
    return this.uploadQueue.length;
  }

  get completedUploadCount(): number {
    return this.uploadQueue.filter((item) => item.status === 'done').length;
  }

  get failedUploadCount(): number {
    return this.uploadQueue.filter((item) => item.status === 'error').length;
  }

  get pendingUploadCount(): number {
    return this.uploadQueue.filter((item) => item.status === 'pending' || item.status === 'reading' || item.status === 'queued' || item.status === 'running').length;
  }

  get uploadQueueSummary(): string {
    if (!this.selectedPdfCount) return 'No files selected';
    return `${this.selectedPdfCount} selected · ${this.completedUploadCount} done · ${this.pendingUploadCount} pending${this.failedUploadCount ? ` · ${this.failedUploadCount} failed` : ''}`;
  }

  get uploadStepLabel(): string {
    const labels: Record<UploadStatus, string> = {
      idle: 'Ready',
      reading: 'Encoding PDF',
      authenticating: 'Authenticating',
      queued: 'Queued',
      running: 'Extracting',
      done: 'Ready for review',
      error: 'Needs attention',
    };
    return labels[this.uploadStatus];
  }

  async batchAcceptHigh(): Promise<void> {
    await this.batchAcceptFields(this.highPendingFields, 'high confidence fields');
  }

  async batchAcceptPending(): Promise<void> {
    await this.batchAcceptFields(this.pendingFields, 'pending fields');
  }

  setDriverHistoryToday(): void {
    const today = this.todayInputDate();
    this.driverHistoryExportMode = 'today';
    this.driverHistoryFromDate = today;
    this.driverHistoryToDate = today;
    this.driverHistoryExportMessage = '';
    if (this.driverHistoryExportStatus !== 'submitting') this.driverHistoryExportStatus = 'idle';
  }

  setDriverHistoryCustom(): void {
    this.driverHistoryExportMode = 'custom';
    this.driverHistoryExportMessage = '';
    if (this.driverHistoryExportStatus !== 'submitting') this.driverHistoryExportStatus = 'idle';
  }

  async downloadMvrDriverHistory(): Promise<void> {
    if (this.driverHistoryExportStatus === 'submitting') return;

    const tenant = this.driverHistoryTenant.trim();
    if (!this.driverHistoryFromDate || !this.driverHistoryToDate) {
      this.driverHistoryExportStatus = 'error';
      this.driverHistoryExportMessage = 'Select a from and to date.';
      return;
    }

    const today = this.todayInputDate();
    if (this.driverHistoryFromDate > today || this.driverHistoryToDate > today) {
      this.driverHistoryExportStatus = 'error';
      this.driverHistoryExportMessage = 'Future dates are not allowed.';
      return;
    }

    if (this.driverHistoryFromDate > this.driverHistoryToDate) {
      this.driverHistoryExportStatus = 'error';
      this.driverHistoryExportMessage = 'From date cannot be after to date.';
      return;
    }

    const apiKey = this.currentApiKey();
    if (!apiKey) {
      this.driverHistoryExportStatus = 'error';
      this.driverHistoryExportMessage = 'Missing API key for driver history export.';
      return;
    }

    this.driverHistoryExportStatus = 'submitting';
    this.driverHistoryExportMessage = 'Preparing driver history export.';

    try {
      const response = await fetch(`${this.apiBaseUrl}/export-mvr-driver-history`, {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          tenant,
          from_date: this.driverHistoryFromDate,
          to_date: this.driverHistoryToDate,
        }),
      });

      if (!response.ok) {
        throw new Error(await this.apiErrorMessage(response, 'Driver history export failed'));
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        const data = (await response.json()) as Record<string, unknown>;
        throw new Error(String(data['message'] ?? data['error'] ?? 'Driver history export did not return an Excel file.'));
      }

      const blob = await response.blob();
      this.downloadBlob(blob, this.driverHistoryExportFileName(response));
      this.driverHistoryExportStatus = 'success';
      this.driverHistoryExportMessage = 'Driver history export downloaded.';
    } catch (error) {
      this.driverHistoryExportStatus = 'error';
      this.driverHistoryExportMessage = error instanceof Error ? error.message : 'Unable to download driver history export.';
    }
  }

  downloadDriverExcel(): void {
    const rows = this.driverExportRows();
    if (rows.length <= 1) return;

    const blob = this.createXlsxBlob(rows);
    this.downloadBlob(blob, `${this.slug(this.pdfFileName || 'mvr-drivers') || 'mvr-drivers'}-drivers.xlsx`);
  }

  selectField(field: ReviewField, scrollToCard = false): void {
    const currentField = this.allDocumentFields.find((item) => item.id === field.id) ?? field;
    this.selectedFieldId = currentField.id;
    this.selectedReason = '-- select reason --';
    void this.updatePdfPage(currentField.page ?? this.currentPdfPage).then(() => this.scrollPdfToField(currentField));
    if (scrollToCard) {
      this.activeTab = 'driver';
      this.openFieldContainer(currentField);
      setTimeout(() => this.scrollSelectedFieldIntoView(currentField.id), 30);
      setTimeout(() => this.scrollSelectedFieldIntoView(currentField.id), 180);
    }
  }

  selectDocumentField(field: ReviewField): void {
    this.selectField(field, true);
  }

  selectFieldById(fieldId?: string, scrollToCard = true): void {
    if (!fieldId) return;
    const field = this.allDocumentFields.find((item) => item.id === fieldId);
    if (field) this.selectField(field, scrollToCard);
  }

  async acceptField(field: ReviewField, forceSave = false): Promise<void> {
    const wasEditing = field.status === 'editing';
    if (wasEditing && !forceSave && !this.canSaveEditedField(field)) return;
    const preservedLowGroupOpen = this.lowGroupOpen;
    const preservedHighGroupOpen = this.highGroupOpen;
    const previousValue = this.editOriginalValues.get(field.id) ?? field.documentValue ?? '';

    const comment = wasEditing
      ? this.isClassADriverExperienceField(field) ? 'Reviewer correction' : `Reviewer correction: ${this.selectedReason}`
      : 'Accepted by reviewer';
    const response = await this.submitFieldUpdate(field, comment);
    if (!response) return;

    if (wasEditing) {
      this.editedFieldIds.add(field.id);
      this.addChangeLogEntry(field.label, previousValue, field.value, 'Field edit');
      this.editOriginalValues.delete(field.id);
      this.editOriginalValidationComments.delete(field.id);
      this.editOriginalBboxes.delete(field.id);
    }

    this.markFieldAccepted(field);
    this.persistReviewFieldToDriver(field, { markUserReviewed: true });
    this.rememberCurrentDriverFieldState();
    const reboundField = this.bindReviewedJsonFromFeedback(response, this.activeDriverIndex, field.id);
    if (wasEditing) {
      this.lowGroupOpen = preservedLowGroupOpen;
      this.highGroupOpen = preservedHighGroupOpen;
    }
    const currentField = reboundField ?? field;
    this.markFieldAccepted(currentField);
    if (wasEditing) {
      this.editedFieldIds.add(currentField.id);
    }
    this.persistReviewFieldToDriver(currentField, { markUserReviewed: true });
    this.rememberCurrentDriverFieldState();
    this.selectedReason = '-- select reason --';
    this.bboxEditEnabled = false;
    if (wasEditing) {
      this.selectedFieldId = currentField.id;
      void this.updatePdfPage(currentField.page ?? this.currentPdfPage).then(() => this.scrollPdfToField(currentField));
    } else {
      this.highGroupOpen = true;
      this.selectNextPendingField();
    }
  }

  editField(field: ReviewField): void {
    this.editOriginalValues.set(field.id, field.value);
    this.editOriginalValidationComments.set(field.id, field.validationComment ?? '');
    this.editOriginalBboxes.set(field.id, field.bbox ? [...field.bbox] : null);
    field.mismatch = undefined;
    field.status = 'editing';
    this.bboxEditEnabled = true;
    field.page = field.page ?? this.currentPdfPage;
    field.unit = field.unit ?? 'inch';
    field.bbox = field.bbox ?? this.defaultBboxForCurrentView();
    this.bboxOverrides.set(this.bboxKey(field), [...field.bbox]);
    this.selectField(field);
    setTimeout(() => this.editValueInputs.first?.nativeElement.focus());
  }

  cancelEdit(field: ReviewField): void {
    field.status = 'pending';
    field.mismatch = undefined;
    this.editedFieldIds.delete(field.id);
    const originalValue = this.editOriginalValues.get(field.id);
    if (originalValue !== undefined) field.value = originalValue;
    const originalValidationComment = this.editOriginalValidationComments.get(field.id);
    if (originalValidationComment !== undefined) {
      field.validationComment = originalValidationComment;
      field.evidence = originalValidationComment;
    }
    const key = this.bboxKey(field);
    if (this.bboxDraftFieldIds.has(key)) {
      this.bboxDraftFieldIds.delete(key);
      this.bboxSavedFieldIds.delete(key);
      this.bboxSavedAdjustments.delete(key);
    }
    const originalBbox = this.editOriginalBboxes.get(field.id);
    if (originalBbox) {
      field.bbox = [...originalBbox];
      this.bboxOverrides.set(key, [...originalBbox]);
    } else if (this.editOriginalBboxes.has(field.id)) {
      field.bbox = undefined;
      this.bboxOverrides.delete(key);
    }
    this.editOriginalValues.delete(field.id);
    this.editOriginalValidationComments.delete(field.id);
    this.editOriginalBboxes.delete(field.id);
    this.selectedReason = '-- select reason --';
    this.bboxEditEnabled = false;
    this.persistReviewFieldToDriver(field);
    this.rememberCurrentDriverFieldState();
  }

  rejectField(field: ReviewField): void {
    field.status = 'rejected';
    this.persistReviewFieldToDriver(field);
    this.rememberCurrentDriverFieldState();
    this.selectNextPendingField();
  }

  onFieldValueChange(field: ReviewField, value: string): void {
    field.value = String(value ?? '');
    field.mismatch = undefined;
    this.persistReviewFieldToDriver(field);
    this.rememberCurrentDriverFieldState();
  }

  onFieldEvidenceChange(field: ReviewField, value: string): void {
    field.validationComment = String(value ?? '');
    field.evidence = field.validationComment;
    this.persistReviewFieldToDriver(field);
    this.rememberCurrentDriverFieldState();
  }

  toggleTheme(): void {
    this.theme = this.isDarkTheme ? 'light' : 'dark';
  }

  toggleDocumentMarks(): void {
    this.documentMarkMode = this.documentMarkMode === 'all' ? 'focus' : 'all';
  }

  toggleBboxEdit(): void {
    this.bboxEditEnabled = !this.bboxEditEnabled;
    if (!this.bboxEditEnabled) {
      this.endActiveBboxDrag(false);
    }
  }

  addBoundingBoxForSelectedField(): void {
    const field = this.selectedField;
    if (!this.canAddBoundingBox || !field) return;

    field.page = this.currentPdfPage;
    field.unit = field.unit ?? 'inch';
    field.bbox = this.defaultBboxForCurrentView();
    const key = this.bboxKey(field);
    this.bboxOverrides.set(key, [...field.bbox]);
    this.bboxDraftFieldIds.add(key);
    this.bboxSavedFieldIds.delete(key);
    this.bboxSavedAdjustments.delete(key);
    this.bboxSaveStatus = 'idle';
    this.bboxSaveMessage = 'New box added. Drag or resize it, then save.';
    void this.updatePdfPage(this.currentPdfPage).then(() => this.scrollPdfToField(field));
  }

  addViolationFieldBox(fieldKey: keyof ViolationForm, label: string, jsonField: string): void {
    const driver = this.currentDriver;
    if (!driver) return;

    this.bboxEditEnabled = true;
    const violationIndex = this.toArray(driver[VIOLATIONS_FIELD]).length;
    const id = `violation-draft-${this.slug(String(fieldKey))}`;
    const value = '';
    const existingField = this.detailDocumentFields.find((field) => field.id === id);
    const draftField: ReviewField =
      existingField ??
      ({
        id,
        label,
        value,
        documentValue: value,
        confidence: 100,
        level: 'high',
        source: 'MANUAL VIOLATION',
        evidence: `${label} bounding box added manually.`,
        helper: `Drag or resize the ${label} box, then save it.`,
        page: this.currentPdfPage,
        bbox: this.defaultBboxForCurrentView(),
        unit: 'inch',
        status: 'editing',
        rawName: `Violation Draft:${String(fieldKey)}:${jsonField}`,
        jsonPath: [VIOLATIONS_FIELD, violationIndex, jsonField],
      } as ReviewField);

    draftField.value = value;
    draftField.documentValue = value;
    draftField.page = this.currentPdfPage;
    draftField.bbox = draftField.bbox ?? this.defaultBboxForCurrentView();
    if (!existingField) this.detailDocumentFields.push(draftField);

    this.selectedFieldId = draftField.id;
    const key = this.bboxKey(draftField);
    this.bboxOverrides.set(key, [...draftField.bbox]);
    this.bboxDraftFieldIds.add(key);
    this.bboxSavedFieldIds.delete(key);
    this.bboxSavedAdjustments.delete(key);
    this.bboxSaveStatus = 'idle';
    this.bboxSaveMessage = `${label} box added. Drag or resize it, then save.`;
    void this.updatePdfPage(this.currentPdfPage).then(() => this.scrollPdfToField(draftField));
  }

  focusViolationFormField(fieldKey: keyof ViolationForm, label: string, jsonField: string): void {
    if (!this.bboxEditEnabled) return;
    this.addViolationFieldBox(fieldKey, label, jsonField);
  }

  private violationDraftMeta(field: ReviewField): { fieldKey: keyof ViolationForm; jsonField: string } | undefined {
    return this.parseViolationDraftName(field.rawName);
  }

  private parseViolationDraftName(rawName: string): { fieldKey: keyof ViolationForm; jsonField: string } | undefined {
    if (!rawName.startsWith('Violation Draft:')) return undefined;
    const [, fieldKey, jsonField] = rawName.split(':');
    if (!fieldKey || !jsonField) return undefined;
    return { fieldKey: fieldKey as keyof ViolationForm, jsonField };
  }

  private reviewedViolationFieldValue(reviewedJson: MvrDocument | undefined, field: ReviewField, jsonField: string): string {
    const reviewedDriver = reviewedJson?.drivers?.[this.fileLocalDriverIndex(this.activeDriverIndex)];
    if (!reviewedDriver) return '';

    const pathValue = this.valueAtPath(reviewedDriver, field.jsonPath);
    if (jsonField === ORIGINAL_DESCRIPTION_FIELD) {
      const pathOriginalDescription = this.violationOriginalDescription(pathValue);
      if (pathOriginalDescription) return pathOriginalDescription;
    }
    const directValue = this.fieldValue(pathValue);
    if (directValue) return directValue;
    if (pathValue !== null && pathValue !== undefined && (typeof pathValue !== 'object' || Array.isArray(pathValue))) {
      return String(pathValue).trim();
    }

    const violationIndex = field.jsonPath.find((part) => typeof part === 'number');
    const records = this.toArray(reviewedDriver[VIOLATIONS_FIELD]);
    const record = typeof violationIndex === 'number' ? records[violationIndex] : records[records.length - 1];
    if (jsonField === ORIGINAL_DESCRIPTION_FIELD) return this.violationOriginalDescription((record as DriverRecord | undefined)?.['Description']);
    return this.fieldValue((record as DriverRecord | undefined)?.[jsonField]);
  }

  private valueAtPath(root: unknown, path: JsonPathPart[]): unknown {
    return path.reduce<unknown>((current, part) => {
      if (current == null) return undefined;
      if (Array.isArray(current) && typeof part === 'number') return current[part];
      if (typeof current === 'object') return (current as DriverRecord)[String(part)];
      return undefined;
    }, root);
  }

  private rememberCurrentDriverFieldState(): void {
    const driver = this.currentDriver;
    if (!driver) return;

    const state = new Map<string, DriverFieldStateSnapshot>();
    this.allDocumentFields.forEach((field) => {
      this.persistReviewFieldToDriver(field, { markUserReviewed: field.userReviewed || field.status === 'accepted' });
      state.set(field.id, {
        value: field.value,
        confidence: field.confidence,
        level: field.level,
        status: field.status,
        userReviewed: field.userReviewed,
        documentValue: field.documentValue,
        mismatch: field.mismatch,
        page: field.page,
        lineId: field.lineId,
        bbox: field.bbox ? [...field.bbox] : undefined,
        unit: field.unit,
        validationComment: field.validationComment,
        editOriginalValue: this.editOriginalValues.get(field.id),
        editOriginalValidationComment: this.editOriginalValidationComments.get(field.id),
        editOriginalBbox: this.editOriginalBboxes.has(field.id)
          ? this.cloneOptionalBbox(this.editOriginalBboxes.get(field.id))
          : undefined,
        edited: this.editedFieldIds.has(field.id),
        submitted: this.submittedFieldUpdateKeys.has(this.fieldUpdateKey(this.activeDriverIndex, field)),
      });
    });

    this.driverFieldState.set(this.activeDriverIndex, state);
    this.driverEditedFieldIds.set(this.activeDriverIndex, new Set(this.editedFieldIds));
    this.driverSubmittedFieldKeys.set(this.activeDriverIndex, new Set(this.submittedFieldUpdateKeys));
  }

  private restoreDriverFieldState(driverIndex: number): void {
    const state = this.driverFieldState.get(driverIndex);
    this.editedFieldIds.clear();
    this.submittedFieldUpdateKeys.clear();
    this.editOriginalValues.clear();
    this.editOriginalValidationComments.clear();
    this.editOriginalBboxes.clear();

    this.driverEditedFieldIds.get(driverIndex)?.forEach((id) => this.editedFieldIds.add(id));
    this.driverSubmittedFieldKeys.get(driverIndex)?.forEach((key) => this.submittedFieldUpdateKeys.add(key));
    if (!state) return;

    this.allDocumentFields.forEach((field) => {
      const saved = state.get(field.id);
      if (!saved) return;

      field.value = saved.value;
      field.confidence = saved.confidence;
      field.level = saved.userReviewed || saved.status === 'accepted' ? 'high' : this.confidenceLevelFor(field.value, field.confidence);
      field.status = saved.status;
      field.userReviewed = saved.userReviewed;
      field.documentValue = saved.documentValue;
      field.mismatch = saved.mismatch;
      field.page = saved.page;
      field.lineId = saved.lineId;
      field.bbox = saved.bbox ? [...saved.bbox] : undefined;
      field.unit = saved.unit;
      field.validationComment = saved.validationComment;
      if (field.validationComment !== undefined) field.evidence = field.validationComment;

      if (saved.edited) this.editedFieldIds.add(field.id);
      if (saved.submitted) this.submittedFieldUpdateKeys.add(this.fieldUpdateKey(driverIndex, field));
      if (field.status === 'editing') {
        this.editOriginalValues.set(field.id, saved.editOriginalValue ?? saved.documentValue ?? saved.value);
        this.editOriginalValidationComments.set(field.id, saved.editOriginalValidationComment ?? saved.validationComment ?? '');
        if (saved.editOriginalBbox !== undefined) {
          this.editOriginalBboxes.set(field.id, this.cloneOptionalBbox(saved.editOriginalBbox));
        }
      }
    });
  }

  private persistReviewFieldToDriver(field: ReviewField, options: { markUserReviewed?: boolean } = {}): void {
    const fieldData = this.valueAtPath(this.currentDriver, field.jsonPath);
    if (!this.isFieldData(fieldData)) return;

    fieldData.value = this.isClassADriverExperienceField(field) ? field.value.trim().toLowerCase() === 'true' : field.value;
    if (field.validationComment !== undefined) fieldData.validation_comment = field.validationComment;
    if (field.page !== undefined) fieldData.page = field.page;
    if (field.bbox) fieldData.bbox = [...field.bbox];
    if (field.unit !== undefined) fieldData.unit = field.unit;
    if (options.markUserReviewed) {
      fieldData.user_reviewed = true;
      fieldData.review_status = field.status === 'rejected' ? 'rejected' : 'accepted';
    } else if (field.status === 'editing') {
      fieldData.review_status = 'editing';
    }
  }

  private cloneOptionalBbox(value: [number, number, number, number] | null | undefined): [number, number, number, number] | null {
    return value ? [...value] : null;
  }

  goToPreviousPdfPage(): void {
    void this.updatePdfPage(this.currentPdfPage - 1);
  }

  goToNextPdfPage(): void {
    void this.updatePdfPage(this.currentPdfPage + 1);
  }

  zoomPdfOut(): void {
    this.setPdfZoom(this.pdfPageScale - 0.1);
  }

  zoomPdfIn(): void {
    this.setPdfZoom(this.pdfPageScale + 0.1);
  }

  fitPdfToWidth(): void {
    void this.updatePdfPage(this.currentPdfPage, true);
  }

  openSubmitModal(): void {
    this.rememberCurrentDriverFieldState();
    this.submittedToFalcon = false;
    this.showSubmitModal = true;
  }

  closeSubmitModal(): void {
    this.showSubmitModal = false;
  }

  requestCancelReset(): void {
    this.showCancelModal = true;
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
  }

  processDuplicateDriverAgain(): void {
    const skippedNotices = this.duplicateNoticesForReview.filter((_, index) => !this.selectedDuplicateDriverIndexes.has(index));
    this.resolveDuplicateDriverChoice({ skippedNotices, cancelled: false });
  }

  cancelDuplicateProcessing(): void {
    this.resolveDuplicateDriverChoice({ skippedNotices: this.duplicateNoticesForReview, cancelled: true });
  }

  toggleDuplicateDriver(index: number, event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const selected = new Set(this.selectedDuplicateDriverIndexes);
    if (input.checked) selected.add(index);
    else selected.delete(index);
    this.selectedDuplicateDriverIndexes = selected;
  }

  toggleAllDuplicateDrivers(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.selectedDuplicateDriverIndexes = input.checked
      ? new Set(this.duplicateNoticesForReview.map((_, index) => index))
      : new Set<number>();
  }

  updateDuplicateDriverSearch(event: Event): void {
    const input = event.target;
    if (input instanceof HTMLInputElement) this.duplicateDriverSearch = input.value;
  }

  get filteredDuplicateNoticesForReview(): Array<{ notice: DuplicateDriverNotice; index: number }> {
    const query = this.duplicateDriverSearch.trim().toLocaleLowerCase();
    return this.duplicateNoticesForReview
      .map((notice, index) => ({ notice, index }))
      .filter(({ notice }) => !query || [notice.driverName, notice.licenseNumber, notice.dob].some((value) => value.toLocaleLowerCase().includes(query)));
  }

  get allDuplicateDriversSelected(): boolean {
    return this.duplicateNoticesForReview.length > 0
      && this.selectedDuplicateDriverIndexes.size === this.duplicateNoticesForReview.length;
  }

  get someDuplicateDriversSelected(): boolean {
    return this.selectedDuplicateDriverIndexes.size > 0 && !this.allDuplicateDriversSelected;
  }

  confirmCancelReset(): void {
    this.renderTask?.cancel();
    this.renderTask = null;
    this.pdfDocument = null;
    this.currentPdfPage = 1;
    this.pdfPageCount = 1;
    this.clearSelectedPdf();
    this.workflowMode = 'import';
    this.activeTab = 'driver';
    this.selectedReason = '-- select reason --';
    this.selectedFieldId = '';
    this.showSubmitModal = false;
    this.showCancelModal = false;
    this.showDuplicateModal = false;
    this.duplicateNoticesForReview = [];
    this.selectedDuplicateDriverIndexes.clear();
    this.duplicateDriverSearch = '';
    this.duplicateChoiceResolver = null;
    this.submittedToFalcon = false;
    this.finalSubmitAfterValidationFixPending = false;
    this.submitStatus = 'idle';
    this.submitMessage = '';
    this.reviewedBlobUrl = '';
    this.bboxSaveStatus = 'idle';
    this.bboxSaveMessage = '';
    this.mvrData = null;
    this.activeDriverIndex = 0;
    this.renderedPdfPage = 1;
    this.detailSections = [];
    this.openDetailSections = {};
    this.openDetailItems = {};
    this.openViolationItems = {};
    this.descriptionSelections = {};
    this.descriptionSaveStatus = {};
    this.descriptionSaveMessage = {};
    this.violationFieldEditValues = {};
    this.violationFieldEditStatus = {};
    this.violationFieldEditMessage = {};
    this.activeViolationEditIndex = null;
    this.activeViolationEditJsonField = '';
    this.activeViolationEditLabel = '';
    this.changeLogEntries = [];
    this.validationItems = [];
    this.validationFieldEditValues = {};
    this.validationFieldEditStatus = {};
    this.validationFieldEditMessage = {};
    this.activeValidationEditId = '';
    this.activeValidationEditFieldId = '';
    this.showViolationForm = false;
    this.violationSaveStatus = 'idle';
    this.violationSaveMessage = '';
    this.newViolation = this.emptyViolationForm();
    this.savedViolationFieldValues = {};
    this.savedViolationIndex = null;
    this.savedViolationDescriptionOptions = [];
    this.savedViolationDescriptionSelection = '';
    this.editedFieldIds.clear();
    this.submittedFieldUpdateKeys.clear();
    this.driverFieldState.clear();
    this.driverEditedFieldIds.clear();
    this.driverSubmittedFieldKeys.clear();
    this.updatedViolationDriverIndexes.clear();
    this.editOriginalValues.clear();
    this.editOriginalValidationComments.clear();
    this.editOriginalBboxes.clear();
    this.bboxDraftFieldIds.clear();
    this.bboxSavedFieldIds.clear();
    this.bboxOverrides.clear();
    this.bboxSavedAdjustments.clear();
    this.bboxEditEnabled = false;
  }

  async submitToFalcon(): Promise<void> {
    if (this.submitStatus === 'submitting') return;

    if (!this.batchId) {
      this.submitStatus = 'error';
      this.submitMessage = 'Missing batch ID. Reprocess the PDF before submitting to Falcon.';
      return;
    }

    this.submitStatus = 'submitting';
    this.submitMessage = 'Submitting fields to Falcon.';
    this.reviewedBlobUrl = '';

    try {
      const token = await this.getMvrToken();
      const response = await this.submitFieldsToFalcon(token, this.reviewFileNames());
      const fileResults = this.falconFileResults(response);

      this.mvrProvider = this.falconProviderFromFiles(fileResults) || this.mvrProvider;
      this.validationItems = this.uniqueValidationItems([
        ...this.requiredValidationItemsBeforeSubmit(),
        ...fileResults.flatMap((file) => this.validationItemsFromResponse(file)),
      ]);

      if (this.validationItems.length) {
        this.submittedToFalcon = false;
        this.finalSubmitAfterValidationFixPending = true;
        this.submitStatus = 'error';
        this.submitMessage = `${this.validationItems.length} validation ${this.validationItems.length === 1 ? 'issue was' : 'issues were'} found. Please fix the validation items before submitting to Falcon.`;
        this.showSubmitModal = false;
        this.activeTab = 'validation';
        return;
      }

      const failedFiles = this.failedFalconFiles(fileResults);
      if (failedFiles.length) {
        this.submittedToFalcon = false;
        this.finalSubmitAfterValidationFixPending = false;
        this.submitStatus = 'error';
        this.submitMessage = `${failedFiles.length} file${failedFiles.length === 1 ? '' : 's'} failed to submit to Falcon: ${failedFiles.join(', ')}.`;
        return;
      }

      this.submittedToFalcon = true;
      this.finalSubmitAfterValidationFixPending = false;
      this.submitStatus = 'success';
      this.submitMessage = 'Submitted. No validation issues found.';
      this.notifyFalconReviewSubmitted(response);
      this.showSubmitModal = false;
      if (this.isEmbeddedInParentWindow()) {
        this.activeTab = 'validation';
      } else {
        this.confirmCancelReset();
        this.uploadStatus = 'done';
        this.uploadMessage = 'MVR review submitted successfully. Upload another MVR to continue.';
      }
    } catch (error) {
      this.submitStatus = 'error';
      this.submittedToFalcon = false;
      this.submitMessage = error instanceof Error ? error.message : 'Unable to submit fields to Falcon.';
    }
  }

  private async submitAfterFinalValidationFix(): Promise<void> {
    if (!this.finalSubmitAfterValidationFixPending || this.validationItems.length || this.autoSubmitAfterValidationFixQueued || this.submittedToFalcon) return;

    this.autoSubmitAfterValidationFixQueued = true;
    await this.delay(100);
    try {
      this.autoSubmitAfterValidationFixQueued = false;
      if (this.validationItems.length || this.submitStatus === 'submitting' || this.submittedToFalcon) return;

      this.rememberCurrentDriverFieldState();
      this.showSubmitModal = false;
      await this.submitToFalcon();
    } finally {
      this.autoSubmitAfterValidationFixQueued = false;
    }
  }

  toggleViolationForm(): void {
    this.showViolationForm = !this.showViolationForm;
    this.violationSaveStatus = 'idle';
    this.violationSaveMessage = '';
  }

  cancelViolationForm(): void {
    this.showViolationForm = false;
    this.violationSaveStatus = 'idle';
    this.violationSaveMessage = '';
    this.newViolation = this.emptyViolationForm();
  }

  async addViolation(): Promise<void> {
    if (!this.canSaveViolation || this.violationSaveStatus === 'submitting') return;

    const driver = this.currentDriver;
    if (!driver) return;

    if (!this.batchId) {
      this.violationSaveStatus = 'error';
      this.violationSaveMessage = 'Missing batch ID. Reprocess the PDF before saving violation.';
      return;
    }

    const previousViolations = this.toArray(driver[VIOLATIONS_FIELD]);
    const previousCount = driver[VIOLATIONS_COUNT_FIELD];
    const previousSummary = driver[VIOLATIONS_SUMMARY_FIELD];
    const record = this.violationRecordFromForm(this.newViolation);
    const operations = this.violationReviewOperations(this.activeDriverIndex);

    if (!operations.length) {
      this.violationSaveStatus = 'error';
      this.violationSaveMessage = 'Add a value or bounding box before saving violation.';
      return;
    }

    driver[VIOLATIONS_FIELD] = [...previousViolations, record];
    this.syncViolationSummary(driver);
    this.updatedViolationDriverIndexes.add(this.activeDriverIndex);
    this.violationSaveStatus = 'submitting';
    this.violationSaveMessage = 'Saving violation to Falcon.';

    try {
      const token = await this.getMvrToken();
      const response = await this.submitMvrReviewFeedbackByBbox(token, operations);
      this.violationSaveStatus = 'success';
      this.violationSaveMessage = response.blob_name ? 'Violation saved to Falcon.' : 'Violation added.';
      this.submitMessage = this.violationSaveMessage;
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      if (response.reviewed_json?.drivers?.length) {
        const mergedDrivers = this.mergeReviewedDrivers(response.reviewed_json.drivers);
        const nextDriverIndex = Math.min(this.activeDriverIndex, mergedDrivers.length - 1);
        this.mvrData = { ...response.reviewed_json, drivers: mergedDrivers };
        this.activeDriverIndex = nextDriverIndex;
        this.applyDriver(mergedDrivers[nextDriverIndex]);
        this.activeTab = 'violations';
        this.violationSaveStatus = 'success';
        this.violationSaveMessage = response.blob_name ? 'Violation saved to Falcon.' : 'Violation added.';
        this.submitMessage = this.violationSaveMessage;
        this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      }
      this.openViolationItems[this.violationItemKey(Math.max(this.violationItems.length - 1, 0))] = true;
      this.newViolation = this.emptyViolationForm();
      this.savedViolationFieldValues = {};
      this.savedViolationIndex = null;
      this.savedViolationDescriptionOptions = [];
      this.savedViolationDescriptionSelection = '';
      this.showViolationForm = false;
      this.updatedViolationDriverIndexes.delete(this.activeDriverIndex);
    } catch (error) {
      driver[VIOLATIONS_FIELD] = previousViolations;
      driver[VIOLATIONS_COUNT_FIELD] = previousCount;
      driver[VIOLATIONS_SUMMARY_FIELD] = previousSummary;
      this.updatedViolationDriverIndexes.delete(this.activeDriverIndex);
      this.violationSaveStatus = 'error';
      this.violationSaveMessage = error instanceof Error ? error.message : 'Unable to save violation.';
    }
  }

  onPdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length) void this.setPdfFiles(files);
    input.value = '';
  }

  onPdfDrop(event: DragEvent): void {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files ?? []).filter((item) => item.type === 'application/pdf' || item.name.toLowerCase().endsWith('.pdf'));
    if (files.length) void this.setPdfFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  clearSelectedPdf(event?: Event): void {
    event?.stopPropagation();
    this.revokeUploadObjectUrls();
    this.selectedPdfFile = null;
    this.selectedPdfFiles = [];
    this.uploadQueue = [];
    this.pdfObjectUrl = '';
    this.pdfSourceUrl = '';
    this.pdfFileName = '';
    this.pdfPath = '';
    this.currentPdfPage = 1;
    this.pdfPageCount = 1;
    this.batchId = null;
    this.pollAttempt = 0;
    this.uploadStatus = 'idle';
    this.uploadMessage = 'Upload the MVR PDF to begin AI extraction.';
    this.uploadError = '';
  }

  async processUploadedPdf(): Promise<void> {
    if (!this.uploadQueue.length || this.isProcessingUpload) return;

    this.uploadError = '';
    this.batchId = null;
    this.pollAttempt = 0;
    this.validationItems = [];
    this.finalSubmitAfterValidationFixPending = false;
    const itemsToProcess = this.uploadQueue.filter((item) => item.status !== 'done');

    try {
      if (!itemsToProcess.length) {
        throw new Error('No pending PDF files to process.');
      }

      this.uploadStatus = 'authenticating';
      this.uploadMessage = 'Validating Falcon API key.';
      await this.validateApiKey(true);
      this.uploadMessage = 'Getting secure Falcon API token.';
      const token = await this.getMvrToken();

      this.uploadStatus = 'reading';
      this.uploadMessage = `Encoding ${itemsToProcess.length} PDF${itemsToProcess.length === 1 ? '' : 's'} for Falcon.`;
      itemsToProcess.forEach((item) => {
        item.status = 'reading';
        item.message = 'Encoding file';
        item.error = undefined;
      });
      const files = await Promise.all(
        itemsToProcess.map(async (item) => ({
          file_name: item.file.name,
          file_data: await this.fileToBase64(item.file),
        }))
      );

      this.uploadStatus = 'queued';
      this.uploadMessage = `Starting extraction for ${files.length} PDF${files.length === 1 ? '' : 's'}.`;
      itemsToProcess.forEach((item) => {
        item.status = 'queued';
        item.message = 'Queued for extraction';
      });
      const batch = await this.submitMvrBatch(token, files);
      this.batchId = batch.batch_id;

      this.uploadStatus = 'running';
      this.uploadMessage = `Batch ${batch.batch_id} processing ${files.length} PDF${files.length === 1 ? '' : 's'}.`;
      itemsToProcess.forEach((item) => {
        item.batchId = batch.batch_id;
        item.status = 'running';
        item.message = `Batch ${batch.batch_id} processing`;
      });
      const statusResponse = await this.pollMvrBatch(token, batch.batch_id);

      itemsToProcess.forEach((item) => {
        item.status = 'done';
        item.message = 'Extraction complete';
      });

      const firstSuccessfulResult = {
        file: itemsToProcess[0].file,
        objectUrl: itemsToProcess[0].objectUrl,
        response: statusResponse,
      };

      this.selectedPdfFile = firstSuccessfulResult.file;
      this.pdfFileName = firstSuccessfulResult.file.name;
      this.pdfObjectUrl = firstSuccessfulResult.objectUrl;
      this.pdfSourceUrl = firstSuccessfulResult.objectUrl;
      this.uploadStatus = 'done';
      this.uploadMessage = `${this.completedUploadCount} file${this.completedUploadCount === 1 ? '' : 's'} processed in batch ${this.batchId}. Opening review workspace.`;
      await this.loadApiResult(firstSuccessfulResult.response);
    } catch (error) {
      itemsToProcess.forEach((item) => {
        if (item.status !== 'done') {
          item.status = 'error';
          item.error = error instanceof Error ? error.message : 'Unable to process this PDF.';
          item.message = 'Failed';
        }
      });
      this.uploadStatus = 'error';
      this.uploadError = error instanceof Error ? error.message : 'Unable to process MVR PDF.';
      this.uploadMessage = 'The upload could not be completed.';
    }
  }

  async saveViolationField(fieldKey: keyof ViolationForm, label: string, jsonField: string): Promise<void> {
    if (fieldKey === 'description' && this.savedViolationFieldValues.description !== undefined && this.savedViolationDescriptionOptions.length) {
      await this.updateSavedViolationDescription();
      return;
    }

    const bboxField = this.violationDraftField(fieldKey, jsonField);
    const hasBbox = !!bboxField?.bbox;
    const value = hasBbox ? String(bboxField.value ?? '').trim() : String(this.newViolation[fieldKey] ?? '').trim();
    await this.submitViolationFieldAdd(fieldKey, label, jsonField, value, hasBbox ? bboxField : undefined);
  }

  onSavedViolationDescriptionChange(value: string): void {
    this.savedViolationDescriptionSelection = value;
    this.newViolation.description = value;
  }

  private async updateSavedViolationDescription(): Promise<void> {
    if (!this.batchId || this.savedViolationIndex === null) {
      this.violationSaveStatus = 'error';
      this.violationSaveMessage = 'Missing saved violation record.';
      return;
    }

    const selectedValue = this.savedViolationDescriptionSelection || this.savedViolationDescriptionOptions[0]?.value || '';
    const selectedOption = this.descriptionOptionForValue(this.savedViolationDescriptionOptions, selectedValue);
    const operations = this.violationDescriptionUpdateOperations(this.savedViolationIndex, selectedValue, selectedOption);

    this.violationSaveStatus = 'submitting';
    this.violationSaveMessage = 'Saving description.';

    try {
      const token = await this.getMvrToken();
      const response = await this.submitMvrReviewFeedback(token, operations);
      this.savedViolationFieldValues.description = selectedValue;
      this.newViolation.description = selectedValue;
      if (selectedOption?.svcCode) {
        this.savedViolationFieldValues.violationCode = selectedOption.svcCode;
        this.newViolation.violationCode = selectedOption.svcCode;
      }
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      this.violationSaveStatus = 'success';
      this.violationSaveMessage = 'Description saved.';
      this.addChangeLogEntry('Violation Description', '--', selectedValue || '--', 'Violation description update');
    } catch (error) {
      this.violationSaveStatus = 'error';
      this.violationSaveMessage = error instanceof Error ? error.message : 'Unable to save description.';
    }
  }

  businessConfidence(field: ReviewField): number {
    return Math.max(0, Math.min(100, field.confidence - 11));
  }

  hotspotLeft(field: ReviewField): number {
    return this.bboxPoint(field, 0);
  }

  hotspotTop(field: ReviewField): number {
    return this.bboxPoint(field, 1);
  }

  hotspotWidth(field: ReviewField): number {
    if (!field.bbox) return 0;
    return Math.max((field.bbox[2] - field.bbox[0]) * 72 * this.pdfPageScale, 34);
  }

  hotspotHeight(field: ReviewField): number {
    if (!field.bbox) return 0;
    return Math.max((field.bbox[3] - field.bbox[1]) * 72 * this.pdfPageScale, 18);
  }

  canEditBoundingBox(field: ReviewField): boolean {
    return this.bboxEditEnabled && field.id === this.selectedFieldId && !!field.bbox;
  }

  isBboxDraft(field: ReviewField): boolean {
    return this.bboxDraftFieldIds.has(this.bboxKey(field));
  }

  isBboxSaved(field: ReviewField): boolean {
    return this.bboxSavedFieldIds.has(this.bboxKey(field));
  }

  onDocumentHotspotClick(event: MouseEvent, field: ReviewField): void {
    if (this.suppressNextHotspotClick) {
      this.suppressNextHotspotClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.selectFieldById(field.id, true);
  }

  startBboxMove(event: PointerEvent, field: ReviewField): void {
    this.beginBboxDrag(event, field, 'move');
  }

  startBboxResize(event: PointerEvent, field: ReviewField, action: BboxEditAction): void {
    this.beginBboxDrag(event, field, action);
  }

  async saveSelectedBoundingBox(): Promise<void> {
    if (this.bboxSaveStatus === 'submitting') return;

    const adjustments = this.draftBboxAdjustments();
    if (!adjustments.length) return;
    const selectedFieldId = this.selectedFieldId;

    if (!this.batchId) {
      this.bboxSaveStatus = 'error';
      this.bboxSaveMessage = 'Missing batch ID. Reprocess the PDF before saving box changes.';
      return;
    }

    this.bboxSaveStatus = 'submitting';
    this.bboxSaveMessage = adjustments.length === 1 ? 'Saving box to Falcon.' : `Saving ${adjustments.length} boxes to Falcon.`;

    try {
      const token = await this.getMvrToken();
      const operations = adjustments.map((adjustment) => this.reviewOperationForBboxAdjustment(adjustment));
      const response = await this.submitMvrReviewFeedbackByBbox(token, operations);

      adjustments.forEach((adjustment) => {
        const key = this.bboxAdjustmentKey(adjustment);
        this.bboxDraftFieldIds.delete(key);
        this.bboxSavedFieldIds.add(key);
        this.bboxSavedAdjustments.set(key, adjustment);
        this.addChangeLogEntry(adjustment.label, 'Previous bounding box', 'Updated bounding box', 'Bounding box edit');
        const field = this.allDocumentFields.find((item) => item.id === adjustment.fieldId);
        const meta = field ? this.violationDraftMeta(field) : undefined;
        if (field && meta) {
          const patchedValue = this.reviewedViolationFieldValue(response.reviewed_json, field, meta.jsonField) || field.value || '';
          this.savedViolationFieldValues[meta.fieldKey] = patchedValue;
          this.newViolation[meta.fieldKey] = patchedValue;
          field.value = patchedValue;
          field.documentValue = patchedValue;
        }
      });
      this.bboxSaveStatus = 'success';
      this.bboxSaveMessage = response.blob_name
        ? `${adjustments.length} ${adjustments.length === 1 ? 'box' : 'boxes'} saved to Falcon`
        : `${adjustments.length} ${adjustments.length === 1 ? 'box' : 'boxes'} saved`;
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      this.bboxEditEnabled = false;

      if (response.reviewed_json?.drivers?.length) {
        const mergedDrivers = this.mergeReviewedDrivers(response.reviewed_json.drivers);
        const nextDriverIndex = Math.min(this.activeDriverIndex, mergedDrivers.length - 1);
        this.mvrData = { ...response.reviewed_json, drivers: mergedDrivers };
        this.activeDriverIndex = nextDriverIndex;
        this.applyDriver(mergedDrivers[nextDriverIndex]);
        const selectedField = this.allDocumentFields.find((field) => field.id === selectedFieldId);
        if (selectedField) {
          this.selectedFieldId = selectedField.id;
          const activeValidationItem = this.validationItems.find((item) => item.id === this.activeValidationEditId);
          if (activeValidationItem && selectedField.id === this.activeValidationEditFieldId) {
            this.validationFieldEditValues[activeValidationItem.id] = selectedField.value;
          }
          void this.updatePdfPage(selectedField.page ?? this.currentPdfPage).then(() => this.scrollPdfToField(selectedField));
        }
        this.bboxSaveStatus = 'success';
        this.bboxSaveMessage = response.blob_name
          ? `${adjustments.length} ${adjustments.length === 1 ? 'box' : 'boxes'} saved to Falcon`
          : `${adjustments.length} ${adjustments.length === 1 ? 'box' : 'boxes'} saved`;
        this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
        this.bboxEditEnabled = false;
      }
    } catch (error) {
      this.bboxSaveStatus = 'error';
      this.bboxSaveMessage = error instanceof Error ? error.message : 'Unable to save box to Falcon.';
    }
  }

  toggleDetailSection(sectionId: string): void {
    this.openDetailSections[sectionId] = !this.openDetailSections[sectionId];
  }

  isDetailSectionOpen(sectionId: string): boolean {
    return this.openDetailSections[sectionId] ?? false;
  }

  toggleDetailItem(sectionId: string, index: number): void {
    const key = this.detailItemKey(sectionId, index);
    this.openDetailItems[key] = !this.openDetailItems[key];
  }

  isDetailItemOpen(sectionId: string, index: number): boolean {
    return this.openDetailItems[this.detailItemKey(sectionId, index)] ?? false;
  }

  toggleViolationItem(index: number): void {
    const key = this.violationItemKey(index);
    this.openViolationItems[key] = !this.openViolationItems[key];
  }

  isViolationItemOpen(index: number): boolean {
    return this.openViolationItems[this.violationItemKey(index)] ?? false;
  }

  toggleSuspensionItem(index: number): void {
    const key = this.suspensionItemKey(index);
    this.openSuspensionItems[key] = !this.openSuspensionItems[key];
  }

  isSuspensionItemOpen(index: number): boolean {
    return this.openSuspensionItems[this.suspensionItemKey(index)] ?? false;
  }

  selectDriver(index: number): void {
    const driver = this.mvrData?.drivers[index];
    if (!driver) return;

    if (index !== this.activeDriverIndex) {
      this.rememberCurrentDriverFieldState();
    }
    this.activeDriverIndex = index;
    this.activeTab = 'driver';
    this.selectedReason = '-- select reason --';
    this.applyDriver(driver);
    setTimeout(() => this.scrollActiveDriverIntoView());
  }

  selectAdjacentDriver(direction: -1 | 1): void {
    const count = this.driverSummaries.length;
    if (count <= 1) return;

    const nextIndex = (this.activeDriverIndex + direction + count) % count;
    this.selectDriver(nextIndex);
  }

  selectDriverFromDropdown(index: number | string): void {
    const parsedIndex = Number(index);
    if (!Number.isInteger(parsedIndex)) return;
    this.selectDriver(parsedIndex);
  }

  private loadThemePreference(): void {
    this.theme = 'light';
    localStorage.removeItem('mvr-review-theme');
  }

  private applyDriver(driver: DriverRecord): void {
    this.pdfFileName = this.fieldValue(driver['File Name']) || this.selectedPdfFile?.name || 'MVR.pdf';
    const driverPdfSource = this.pdfSourceForDriver(driver);
    if (driverPdfSource && driverPdfSource !== this.pdfSourceUrl) {
      this.pdfSourceUrl = driverPdfSource;
      this.pdfDocument = null;
    }
    this.quote = {
      id: this.fieldValue(driver['License Number']) || '1068524',
      insured: this.driverName(driver),
      status: 'QS:RECEIVED',
    };

    this.fields = this.buildReviewFields(driver);
    this.detailDocumentFields = this.buildDetailDocumentFields(driver);
    this.detailSections = this.withDetailFieldIds(this.buildDetailSections(driver));
    this.openDetailSections = this.detailSections.reduce<Record<string, boolean>>((sections, section) => {
      sections[section.id] = false;
      return sections;
    }, {});
    this.editedFieldIds.clear();
    this.submittedFieldUpdateKeys.clear();
    this.submittedToFalcon = false;
    this.submitStatus = 'idle';
    this.submitMessage = '';
    this.reviewedBlobUrl = '';
    this.bboxSaveStatus = 'idle';
    this.bboxSaveMessage = '';
    this.openDetailItems = {};
    this.openViolationItems = {};
    this.openSuspensionItems = {};
    this.violationFieldEditValues = {};
    this.violationFieldEditStatus = {};
    this.violationFieldEditMessage = {};
    this.activeViolationEditIndex = null;
    this.activeViolationEditJsonField = '';
    this.activeViolationEditLabel = '';
    this.editOriginalValues.clear();
    this.editOriginalValidationComments.clear();
    this.editOriginalBboxes.clear();
    this.applyBboxOverrides();
    this.restoreDriverFieldState(this.activeDriverIndex);
    this.lowGroupOpen = true;
    this.highGroupOpen = false;
    const initialField = this.initialDriverFocusField();
    this.selectedFieldId = initialField?.id ?? '';
    if (initialField && this.pdfCanvas) {
      void this.updatePdfPage(initialField.page ?? 1, true).then(() => this.scrollPdfToField(initialField));
    }
    setTimeout(() => this.scrollActiveDriverIntoView());
  }

  private initialDriverFocusField(): ReviewField | undefined {
    const preferredFields = ['Given Name', 'License Number', 'Last Name', 'Date of Birth'];
    return (
      preferredFields
        .map((fieldName) => this.fields.find((field) => field.rawName === fieldName && field.bbox))
        .find(Boolean) ??
      this.fields.find((field) => field.bbox) ??
      this.lowFields[0] ??
      this.fields[0]
    );
  }

  private scrollActiveDriverIntoView(): void {
    const ribbon = this.driverRibbon?.nativeElement;
    const activeCard = ribbon?.querySelector<HTMLElement>(`[data-driver-index="${this.activeDriverIndex}"]`);
    activeCard?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  private buildReviewFields(driver: DriverRecord): ReviewField[] {
    const primaryFields = REVIEW_FIELDS.flatMap((fieldName) => {
      const field = driver[fieldName] as FieldData | undefined;
      if (!field) return [];
      return [this.toReviewField(fieldName, field)];
    });

    const supplementalFields: ReviewField[] = [];
    SUPPLEMENTAL_REVIEW_ROOTS.forEach((root) => {
      this.collectDetailDocumentFields(driver[root], [root], supplementalFields, true);
    });

    return [...primaryFields, ...supplementalFields];
  }

  private buildDetailDocumentFields(driver: DriverRecord): ReviewField[] {
    const reviewFieldNames = new Set<string>(REVIEW_FIELDS);
    const detailFields: ReviewField[] = [];

    Object.entries(driver).forEach(([key, value]) => {
      if (reviewFieldNames.has(key)) return;
      if (SUPPLEMENTAL_REVIEW_ROOTS.has(key)) return;
      this.collectDetailDocumentFields(value, [key], detailFields, EMPTY_FIELD_REVIEW_ROOTS.has(key));
    });

    return detailFields;
  }

  private collectDetailDocumentFields(value: unknown, path: JsonPathPart[], fields: ReviewField[], includeEmptyFields = false): void {
    if (Array.isArray(value)) {
      value.forEach((item, index) => this.collectDetailDocumentFields(item, [...path, index], fields, includeEmptyFields));
      return;
    }

    if (!value || typeof value !== 'object') return;

    const descriptionField = this.descriptionCandidateFieldData(value);
    if (descriptionField) {
      const bbox = this.fieldBbox(descriptionField);
      if (bbox) fields.push(this.toDetailDocumentField(path, descriptionField));
      return;
    }

    if (this.isFieldData(value)) {
      const bbox = this.fieldBbox(value);
      if (bbox || includeEmptyFields) fields.push(this.toDetailDocumentField(path, value));
      return;
    }

    Object.entries(value as DriverRecord).forEach(([key, childValue]) => {
      this.collectDetailDocumentFields(childValue, [...path, key], fields, includeEmptyFields);
    });
  }

  private isFieldData(value: unknown): value is FieldData {
    return !!value && typeof value === 'object' && ('value' in value || 'conf' in value);
  }

  isRequiredReviewField(field: ReviewField | null | undefined): boolean {
    if (!field) return false;
    if (field.required) return true;

    const path = field.jsonPath ?? [];
    const fieldName = path.length ? String(path[path.length - 1]) : field.rawName || field.label;
    const section = path
      .slice(0, -1)
      .map((part) => (typeof part === 'number' ? String(part + 1) : part))
      .join(' ');

    return this.isRequiredReviewFieldName(fieldName, section);
  }

  private isRequiredReviewFieldName(fieldName: string, section = ''): boolean {
    const name = this.requiredFieldKey(fieldName);
    const sectionKey = this.requiredFieldKey(section);
    const baseRequired = new Set([
      'given name',
      'last name',
      'date of birth',
      'dob',
      'license number',
      'lic',
      'lic number',
      'lic #',
      'yrs of exp',
      'yrs of experience',
      'years of exp',
      'years of experience',
      'state of driver record',
      'lic state',
      'license state',
    ]);

    if (baseRequired.has(name)) return true;

    const licenseSection = !sectionKey || sectionKey.includes('license information') || sectionKey === 'license';
    if (licenseSection && ['expiration date', 'lic exp date', 'license expiration date', 'class', 'lic class', 'license class'].includes(name)) {
      return true;
    }

    return false;
  }

  private activateValidationReviewField(item: ValidationItem, openDriverTab: boolean): ReviewField | undefined {
    const driver = this.mvrData?.drivers?.[item.driverIndex];
    if (!driver) return undefined;

    if (item.driverIndex !== this.activeDriverIndex) {
      this.rememberCurrentDriverFieldState();
      this.activeDriverIndex = item.driverIndex;
      this.applyDriver(driver);
    }

    const field = this.validationReviewField(item);
    if (!field) return undefined;

    this.selectedFieldId = field.id;
    if (openDriverTab) {
      this.activeTab = 'driver';
      this.openFieldContainer(field);
      setTimeout(() => this.scrollSelectedFieldIntoView(field.id), 30);
      setTimeout(() => this.scrollSelectedFieldIntoView(field.id), 180);
    }

    void this.updatePdfPage(field.page ?? this.currentPdfPage).then(() => this.scrollPdfToFieldWhenReady(field));
    return field;
  }

  private validationReviewField(item: ValidationItem): ReviewField | undefined {
    if (item.driverIndex !== this.activeDriverIndex) return undefined;
    return this.findValidationReviewField(item, this.allDocumentFields);
  }

  private findValidationReviewField(item: ValidationItem, fields: ReviewField[]): ReviewField | undefined {
    const path = item.path ?? [];
    const pathMatchedField = path.length
      ? fields.find((field) => this.areJsonPathsEqual(field.jsonPath, path))
      : undefined;
    if (pathMatchedField) return pathMatchedField;

    const normalizedField = this.requiredFieldKey(item.field);
    return fields.find((field) => this.requiredFieldKey(field.label) === normalizedField || this.requiredFieldKey(field.rawName) === normalizedField);
  }

  private prepareValidationFieldForEdit(field: ReviewField): void {
    if (!this.editOriginalValues.has(field.id)) {
      this.editOriginalValues.set(field.id, field.value);
    }
    if (!this.editOriginalValidationComments.has(field.id)) {
      this.editOriginalValidationComments.set(field.id, field.validationComment ?? '');
    }
    if (!this.editOriginalBboxes.has(field.id)) {
      this.editOriginalBboxes.set(field.id, field.bbox ? [...field.bbox] : null);
    }
    field.mismatch = undefined;
    field.status = 'editing';
    this.bboxEditEnabled = true;
    field.page = field.page ?? this.currentPdfPage;
    field.unit = field.unit ?? 'inch';
    field.bbox = field.bbox ?? this.defaultBboxForCurrentView();
    this.bboxOverrides.set(this.bboxKey(field), [...field.bbox]);
    this.selectedFieldId = field.id;
    this.persistReviewFieldToDriver(field);
    this.rememberCurrentDriverFieldState();
    void this.updatePdfPage(field.page ?? this.currentPdfPage).then(() => this.scrollPdfToFieldWhenReady(field));
  }

  private areJsonPathsEqual(left: JsonPathPart[] | undefined, right: JsonPathPart[] | undefined): boolean {
    if (!left || !right || left.length !== right.length) return false;
    return left.every((part, index) => part === right[index]);
  }

  private requiredValidationItemsBeforeSubmit(): ValidationItem[] {
    const drivers = this.mvrData?.drivers ?? [];
    const items: ValidationItem[] = [];

    drivers.forEach((driver, driverIndex) => {
      this.buildReviewFields(driver).forEach((field) => {
        if (!this.isRequiredReviewField(field) || !this.isMissingRequiredValue(field.value)) return;

        const path = field.jsonPath?.length ? field.jsonPath : [field.rawName ?? field.label];
        items.push({
          id: `required-${driverIndex}-${field.id}`,
          driverIndex,
          driverName: this.driverName(driver),
          field: field.label,
          value: this.validationPlainDisplayValue(field.value),
          comment: `${field.label} is required before submitting to Falcon.`,
          path,
        });
      });
    });

    return items;
  }

  private uniqueValidationItems(items: ValidationItem[]): ValidationItem[] {
    const seen = new Set<string>();

    return items.filter((item) => {
      const pathKey = item.path?.join('.') ?? item.field;
      const key = `${item.driverIndex}:${pathKey}:${item.comment}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private isMissingRequiredValue(value: unknown): boolean {
    if (value === null || value === undefined) return true;

    const hasWrappedValue = typeof value === 'object' && 'value' in value;
    const normalized = String(hasWrappedValue ? this.fieldValue(value) : value)
      .trim()
      .toLowerCase();

    return !normalized || ['--', 'not found', 'n/a', 'na', 'null', 'undefined'].includes(normalized);
  }

  private requiredFieldKey(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9#]+/g, ' ')
      .trim();
  }

  private toDetailDocumentField(path: JsonPathPart[], field: FieldData): ReviewField {
    const fieldName = String(path[path.length - 1] ?? 'Field');
    const section = path.slice(0, -1).map((part) => (typeof part === 'number' ? String(part + 1) : part)).join(' ');
    const confidence = this.confidenceFor(fieldName, field);
    const userReviewed = this.isUserReviewedFieldData(field);
    const value = this.fieldValue(field);
    const isLowConfidence = !userReviewed && this.isLowConfidenceField(field, value, confidence);
    const label = section ? `${section}: ${LABELS[fieldName] ?? fieldName}` : LABELS[fieldName] ?? fieldName;
    const bbox = this.fieldBbox(field);
    const lineId = field.source_llm?.line_id || field.line_id || undefined;
    const page = field.source_llm?.page ?? field.page;
    const sourceClass = field.source_llm?.class_name;

    const validationComment = this.fieldEvidenceComment(field);

    return {
      id: this.detailFieldId(path),
      label,
      value,
      documentValue: value,
      confidence,
      level: userReviewed ? 'high' : isLowConfidence ? 'low' : 'high',
      source: section.toUpperCase(),
      evidence: validationComment || `${LABELS[fieldName] ?? fieldName} appears in ${this.pdfFileName}${page ? ` on page ${page}` : ''}${
        lineId ? `, line ${lineId}` : ''
      }.`,
      validationComment,
      helper: `Review the highlighted ${LABELS[fieldName] ?? fieldName}${section ? ` in the ${section} section` : ''}.`,
      mismatch: isLowConfidence ? `Extracted ${LABELS[fieldName] ?? fieldName} value is ${value || '—'}.` : undefined,
      required: this.isRequiredReviewFieldName(fieldName, section),
      userReviewed,
      page,
      lineId: sourceClass || lineId,
      bbox,
      unit: field.unit ?? null,
      status: userReviewed ? 'accepted' : 'pending',
      rawName: path.map((part) => (typeof part === 'number' ? String(part + 1) : part)).join(' '),
      jsonPath: path,
    };
  }

  private detailFieldId(path: JsonPathPart[]): string {
    return `detail-${path.map((part) => this.slug(typeof part === 'number' ? String(part + 1) : part)).join('-')}`;
  }

  private isBboxAnnotationReviewField(field: ReviewField): boolean {
    return field.jsonPath.some((part) => typeof part === 'string' && /bbox[_\s-]?annotations?/i.test(part));
  }

  private normalizedReviewValue(value: string): string {
    return value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private boundingBoxesOverlap(
    left?: [number, number, number, number],
    right?: [number, number, number, number],
  ): boolean {
    if (!left || !right) return false;
    const intersectionWidth = Math.max(0, Math.min(left[2], right[2]) - Math.max(left[0], right[0]));
    const intersectionHeight = Math.max(0, Math.min(left[3], right[3]) - Math.max(left[1], right[1]));
    const intersectionArea = intersectionWidth * intersectionHeight;
    const leftArea = Math.max(0, left[2] - left[0]) * Math.max(0, left[3] - left[1]);
    const rightArea = Math.max(0, right[2] - right[0]) * Math.max(0, right[3] - right[1]);
    const smallerArea = Math.min(leftArea, rightArea);
    return smallerArea > 0 && intersectionArea / smallerArea >= 0.65;
  }

  private toReviewField(fieldName: string, field: FieldData): ReviewField {
    const confidence = this.confidenceFor(fieldName, field);
    const userReviewed = this.isUserReviewedFieldData(field);
    const label = LABELS[fieldName] ?? fieldName;
    const value = this.fieldValue(field);
    const isLowConfidence = !userReviewed && this.isLowConfidenceField(field, value, confidence);
    const bbox = this.fieldBbox(field);
    const lineId = field.source_llm?.line_id || field.line_id || undefined;
    const page = field.source_llm?.page ?? field.page;
    const sourceClass = field.source_llm?.class_name;
    const validationComment = this.fieldEvidenceComment(field);

    return {
      id: FIELD_IDS[fieldName] ?? this.slug(fieldName),
      label,
      value,
      documentValue: value,
      confidence,
      level: userReviewed ? 'high' : isLowConfidence ? 'low' : 'high',
      source: fieldName === 'State of Driver Record' ? 'DRIVER FORM' : 'MVR DOCUMENT',
      evidence: validationComment || `${label} appears in ${this.pdfFileName}${page ? ` on page ${page}` : ''}${
        lineId ? `, line ${lineId}` : ''
      }.`,
      validationComment,
      helper: isLowConfidence
        ? `Review the highlighted ${label} in the source MVR document.`
        : 'High confidence field; review only if the source looks incorrect.',
      mismatch:
        isLowConfidence
          ? `Falcon driver record should be compared against extracted value ${value || '—'}.`
          : undefined,
      required: this.isRequiredReviewFieldName(fieldName),
      userReviewed,
      page,
      lineId: sourceClass || lineId,
      bbox,
      unit: field.unit ?? null,
      status: userReviewed ? 'accepted' : 'pending',
      rawName: fieldName,
      jsonPath: [fieldName],
    };
  }

  private fieldEvidenceComment(field: FieldData): string | undefined {
    const savedComment = field.comment?.trim() ?? '';
    const evidenceMarker = 'Evidence:';
    const evidenceIndex = savedComment.indexOf(evidenceMarker);
    if (evidenceIndex >= 0) return savedComment.slice(evidenceIndex + evidenceMarker.length).trim();
    return field.validation_comment?.trim() || undefined;
  }

  private fieldBbox(field: FieldData): [number, number, number, number] | undefined {
    const bbox = field.source_llm?.bbox ?? field.bbox;
    if (!Array.isArray(bbox) || bbox.length < 4) return undefined;
    const points = bbox.slice(0, 4).map((point) => Number(point));
    return points.every(Number.isFinite) ? (points as [number, number, number, number]) : undefined;
  }

  private bboxPoint(field: ReviewField, index: 0 | 1): number {
    if (!field.bbox) return 0;
    return field.bbox[index] * 72 * this.pdfPageScale;
  }

  private beginBboxDrag(event: PointerEvent, field: ReviewField, action: BboxEditAction): void {
    if (!this.bboxEditEnabled || !field.bbox) return;

    event.preventDefault();
    event.stopPropagation();
    this.selectedFieldId = field.id;
    this.activeBboxDrag = {
      fieldId: field.id,
      action,
      startX: event.clientX,
      startY: event.clientY,
      startBbox: [...field.bbox],
      moved: false,
    };
    this.addBboxDragListeners();
  }

  private updateActiveBboxDrag(event: PointerEvent): void {
    if (!this.activeBboxDrag) return;

    event.preventDefault();
    const drag = this.activeBboxDrag;
    const field = this.allDocumentFields.find((item) => item.id === drag.fieldId);
    if (!field?.bbox) return;

    const factor = 72 * this.pdfPageScale;
    const dx = (event.clientX - drag.startX) / factor;
    const dy = (event.clientY - drag.startY) / factor;
    if (Math.abs(event.clientX - drag.startX) > 2 || Math.abs(event.clientY - drag.startY) > 2) {
      drag.moved = true;
    }

    field.bbox = this.constrainedBbox(drag.startBbox, dx, dy, drag.action);
  }

  private endActiveBboxDrag(markDraft = true): void {
    if (this.activeBboxDrag?.moved && markDraft) {
      const field = this.allDocumentFields.find((item) => item.id === this.activeBboxDrag?.fieldId);
      if (field?.bbox) {
        const key = this.bboxKey(field);
        this.bboxOverrides.set(key, [...field.bbox]);
        this.bboxDraftFieldIds.add(key);
        this.bboxSavedFieldIds.delete(key);
        this.bboxSavedAdjustments.delete(key);
      }
      this.suppressNextHotspotClick = true;
    }

    this.activeBboxDrag = null;
    this.removeBboxDragListeners();
  }

  private addBboxDragListeners(): void {
    window.addEventListener('pointermove', this.onBboxPointerMove);
    window.addEventListener('pointerup', this.onBboxPointerUp);
    window.addEventListener('pointercancel', this.onBboxPointerUp);
  }

  private removeBboxDragListeners(): void {
    window.removeEventListener('pointermove', this.onBboxPointerMove);
    window.removeEventListener('pointerup', this.onBboxPointerUp);
    window.removeEventListener('pointercancel', this.onBboxPointerUp);
  }

  private constrainedBbox(
    bbox: [number, number, number, number],
    dx: number,
    dy: number,
    action: BboxEditAction,
  ): [number, number, number, number] {
    let [x1, y1, x2, y2] = bbox;
    const factor = 72 * this.pdfPageScale;
    const pageWidth = (this.pdfCanvas?.nativeElement.width ?? 0) / factor;
    const pageHeight = (this.pdfCanvas?.nativeElement.height ?? 0) / factor;
    const minWidth = 12 / factor;
    const minHeight = 8 / factor;

    if (action === 'move') {
      x1 += dx;
      x2 += dx;
      y1 += dy;
      y2 += dy;

      if (x1 < 0) {
        x2 -= x1;
        x1 = 0;
      }
      if (y1 < 0) {
        y2 -= y1;
        y1 = 0;
      }
      if (pageWidth && x2 > pageWidth) {
        x1 -= x2 - pageWidth;
        x2 = pageWidth;
      }
      if (pageHeight && y2 > pageHeight) {
        y1 -= y2 - pageHeight;
        y2 = pageHeight;
      }
    } else {
      if (action.includes('w')) x1 += dx;
      if (action.includes('e')) x2 += dx;
      if (action.includes('n')) y1 += dy;
      if (action.includes('s')) y2 += dy;

      x1 = Math.max(0, Math.min(x1, x2 - minWidth));
      x2 = Math.min(pageWidth || x2, Math.max(x2, x1 + minWidth));
      y1 = Math.max(0, Math.min(y1, y2 - minHeight));
      y2 = Math.min(pageHeight || y2, Math.max(y2, y1 + minHeight));
    }

    return [
      this.roundBboxPoint(x1),
      this.roundBboxPoint(y1),
      this.roundBboxPoint(Math.max(x1 + minWidth, x2)),
      this.roundBboxPoint(Math.max(y1 + minHeight, y2)),
    ];
  }

  private defaultBboxForCurrentView(): [number, number, number, number] {
    const factor = 72 * this.pdfPageScale;
    const frame = this.pdfFrame?.nativeElement;
    const canvas = this.pdfCanvas?.nativeElement;
    const visibleLeft = (frame?.scrollLeft ?? 0) / factor;
    const visibleTop = (frame?.scrollTop ?? 0) / factor;
    const visibleWidth = (frame?.clientWidth ?? canvas?.width ?? 420) / factor;
    const visibleHeight = (frame?.clientHeight ?? canvas?.height ?? 360) / factor;
    const pageWidth = (canvas?.width ?? 612 * this.pdfPageScale) / factor;
    const pageHeight = (canvas?.height ?? 792 * this.pdfPageScale) / factor;
    const width = Math.min(1.8, Math.max(0.8, visibleWidth * 0.32));
    const height = Math.min(0.45, Math.max(0.22, visibleHeight * 0.08));
    const x1 = Math.max(0, Math.min(pageWidth - width, visibleLeft + (visibleWidth - width) / 2));
    const y1 = Math.max(0, Math.min(pageHeight - height, visibleTop + (visibleHeight - height) / 2));

    return [
      this.roundBboxPoint(x1),
      this.roundBboxPoint(y1),
      this.roundBboxPoint(x1 + width),
      this.roundBboxPoint(y1 + height),
    ];
  }

  private roundBboxPoint(value: number): number {
    return Math.round(value * 10000) / 10000;
  }

  private reviewFeedbackOperations(): ReviewFeedbackOperation[] {
    const operations = new Map<string, ReviewFeedbackOperation>();

    this.allDocumentFields
      .filter((field) => this.editedFieldIds.has(field.id) && !this.submittedFieldUpdateKeys.has(this.fieldUpdateKey(this.activeDriverIndex, field)))
      .forEach((field) => {
        operations.set(this.operationKey(this.activeDriverIndex, field.jsonPath), this.reviewOperationForField(field, this.activeDriverIndex, 'Value corrected by reviewer'));
      });

    this.updatedViolationDriverIndexes.forEach((driverIndex) => {
      this.violationReviewOperations(driverIndex).forEach((operation) => {
        operations.set(`${operation.path.join('/')}:${operation.comment ?? ''}`, operation);
      });
    });

    return [...operations.values()];
  }

  private async submitFieldUpdate(field: ReviewField, comment: string): Promise<ReviewFeedbackResponse | null> {
    if (!this.batchId) {
      this.submitStatus = 'error';
      this.submitMessage = 'Missing batch ID. Reprocess the PDF before saving review feedback.';
      return null;
    }

    this.submitStatus = 'submitting';
    this.submitMessage = 'Saving field update to Falcon.';

    try {
      const token = await this.getMvrToken();
      const usesBbox = this.isBboxDraft(field);
      const operation = usesBbox ? this.reviewOperationForBboxAdjustment(this.bboxAdjustmentFor(field)) : this.reviewOperationForField(field, this.activeDriverIndex, comment);
      const response = usesBbox ? await this.submitMvrReviewFeedbackByBbox(token, [operation]) : await this.submitMvrReviewFeedback(token, [operation]);
      if (usesBbox) {
        const patchedValue = this.reviewedFieldValue(response.reviewed_json, field) || field.value;
        field.value = patchedValue;
        field.documentValue = patchedValue;
        const key = this.bboxKey(field);
        this.bboxDraftFieldIds.delete(key);
        this.bboxSavedFieldIds.add(key);
        this.bboxSavedAdjustments.set(key, this.bboxAdjustmentFor(field));
        this.bboxEditEnabled = false;
      }
      this.submittedFieldUpdateKeys.add(this.fieldUpdateKey(this.activeDriverIndex, field));
      field.mismatch = undefined;
      this.submitStatus = 'success';
      this.submitMessage = response.blob_name ? `Field update saved at ${response.blob_name}.` : 'Field update saved to Falcon.';
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      return response;
    } catch (error) {
      this.submitStatus = 'error';
      this.submitMessage = error instanceof Error ? error.message : 'Unable to save field update.';
      field.mismatch = this.submitMessage;
      return null;
    }
  }

  private async batchAcceptFields(fields: ReviewField[], label: string): Promise<void> {
    if (this.submitStatus === 'submitting') return;

    const fieldsToAccept = fields.filter((field) => field.status === 'pending' || field.status === 'editing');
    if (!fieldsToAccept.length) return;

    if (!this.batchId) {
      this.submitStatus = 'error';
      this.submitMessage = 'Missing batch ID. Reprocess the PDF before saving review feedback.';
      return;
    }

    this.submitStatus = 'submitting';
    this.submitMessage = `Saving ${label} to Falcon.`;

    try {
      const token = await this.getMvrToken();
      const operations = fieldsToAccept.map((field) => this.reviewOperationForField(field, this.activeDriverIndex, 'Accepted by reviewer'));
      const response = await this.submitMvrReviewFeedback(token, operations);
      const acceptedFieldIds = new Set(fieldsToAccept.map((field) => field.id));

      fieldsToAccept.forEach((field) => {
        this.markFieldAccepted(field);
        this.editedFieldIds.delete(field.id);
        this.submittedFieldUpdateKeys.add(this.fieldUpdateKey(this.activeDriverIndex, field));
        this.persistReviewFieldToDriver(field, { markUserReviewed: true });
      });
      this.rememberCurrentDriverFieldState();
      this.bindReviewedJsonFromFeedback(response, this.activeDriverIndex, this.selectedFieldId);
      this.allDocumentFields
        .filter((field) => acceptedFieldIds.has(field.id))
        .forEach((field) => {
          this.markFieldAccepted(field);
          this.editedFieldIds.delete(field.id);
          this.submittedFieldUpdateKeys.add(this.fieldUpdateKey(this.activeDriverIndex, field));
          this.persistReviewFieldToDriver(field, { markUserReviewed: true });
        });
      this.rememberCurrentDriverFieldState();

      this.selectedReason = this.reasons[0];
      this.selectNextPendingField();
      this.submitStatus = 'success';
      this.submitMessage = response.blob_name ? `${this.capitalize(label)} saved at ${response.blob_name}.` : `${this.capitalize(label)} saved to Falcon.`;
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
    } catch (error) {
      this.submitStatus = 'error';
      this.submitMessage = error instanceof Error ? error.message : `Unable to save ${label}.`;
    }
  }

  private reviewOperationForField(field: ReviewField, driverIndex: number, comment: string): ReviewFeedbackOperation {
    const operationComment = this.isClassADriverExperienceField(field)
      ? `${comment} | Evidence: ${field.validationComment?.trim() ?? ''}`
      : comment;
    return {
      action: 'update',
      path: ['drivers', this.fileLocalDriverIndex(driverIndex), ...field.jsonPath],
      value: this.isClassADriverExperienceField(field) ? field.value.trim().toLowerCase() === 'true' : field.value,
      bbox: field.bbox ? [...field.bbox] : [],
      page: field.page ?? null,
      unit: field.unit ?? (field.bbox ? 'inch' : null),
      comment: operationComment,
    };
  }

  // Review merges every file's drivers into one list, but each file's backend json
  // indexes its drivers locally, so translate the global review index to the file-local one.
  private fileLocalDriverIndex(globalIndex: number): number {
    const drivers = this.mvrData?.drivers ?? [];
    const targetFile = this.fieldValue(drivers[globalIndex]?.['File Name']);
    let localIndex = 0;
    for (let i = 0; i < globalIndex; i++) {
      if (this.fieldValue(drivers[i]?.['File Name']) === targetFile) localIndex++;
    }
    return localIndex;
  }

  private reviewedFieldValue(reviewedJson: MvrDocument | undefined, field: ReviewField): string {
    const reviewedDriver = reviewedJson?.drivers?.[this.fileLocalDriverIndex(this.activeDriverIndex)];
    if (!reviewedDriver) return '';
    return this.fieldValue(this.valueAtPath(reviewedDriver, field.jsonPath));
  }

  private bindReviewedJsonFromFeedback(response: ReviewFeedbackResponse, driverIndex: number, selectedFieldId?: string): ReviewField | undefined {
    if (!response.reviewed_json?.drivers?.length) {
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      return undefined;
    }

    const preservedStatus = this.submitStatus;
    const preservedMessage = this.submitMessage;
    const preservedBlobUrl = this.reviewedBlobUrl;
    const mergedDrivers = this.mergeReviewedDrivers(response.reviewed_json.drivers);
    const nextDriverIndex = Math.min(Math.max(driverIndex, 0), mergedDrivers.length - 1);
    const previousState = this.driverFieldState.get(nextDriverIndex);
    const previousEditedFieldIds = new Set(this.driverEditedFieldIds.get(nextDriverIndex) ?? []);
    const previousSubmittedFieldKeys = new Set(this.driverSubmittedFieldKeys.get(nextDriverIndex) ?? []);

    this.driverFieldState.delete(nextDriverIndex);
    this.driverEditedFieldIds.delete(nextDriverIndex);
    this.driverSubmittedFieldKeys.delete(nextDriverIndex);
    this.mvrData = { ...response.reviewed_json, drivers: mergedDrivers };
    this.activeDriverIndex = nextDriverIndex;
    this.applyDriver(mergedDrivers[nextDriverIndex]);

    this.editedFieldIds.clear();
    this.submittedFieldUpdateKeys.clear();
    previousEditedFieldIds.forEach((id) => this.editedFieldIds.add(id));
    previousSubmittedFieldKeys.forEach((key) => this.submittedFieldUpdateKeys.add(key));
    previousState?.forEach((saved, fieldId) => {
      const field = this.allDocumentFields.find((item) => item.id === fieldId);
      if (!field) return;
      field.status = saved.status;
      field.userReviewed = saved.userReviewed;
      field.mismatch = saved.mismatch;
      if (saved.status === 'accepted' || saved.userReviewed) {
        field.confidence = saved.confidence;
        this.markFieldAccepted(field);
      }
      if (saved.edited) this.editedFieldIds.add(field.id);
      if (saved.submitted) this.submittedFieldUpdateKeys.add(this.fieldUpdateKey(nextDriverIndex, field));
    });

    const reboundField = selectedFieldId ? this.allDocumentFields.find((field) => field.id === selectedFieldId) : undefined;
    if (reboundField) {
      this.selectedFieldId = reboundField.id;
    }

    this.submitStatus = preservedStatus;
    this.submitMessage = preservedMessage;
    this.reviewedBlobUrl = response.reviewed_blob_url ?? preservedBlobUrl;
    this.rememberCurrentDriverFieldState();
    return reboundField;
  }

  private operationKey(driverIndex: number, path: JsonPathPart[]): string {
    return `${driverIndex}:${path.join('/')}`;
  }

  private fieldUpdateKey(driverIndex: number, field: ReviewField): string {
    return this.operationKey(driverIndex, field.jsonPath);
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private savedBboxAdjustments(): BboxAdjustment[] {
    return [...this.bboxSavedAdjustments.values()];
  }

  private draftBboxAdjustments(): BboxAdjustment[] {
    return this.allDocumentFields
      .filter((field) => field.bbox && this.bboxDraftFieldIds.has(this.bboxKey(field)))
      .map((field) => this.bboxAdjustmentFor(field));
  }

  private reviewOperationForBboxAdjustment(adjustment: BboxAdjustment): ReviewFeedbackOperation {
    const isViolationDraft = adjustment.fieldName.startsWith('Violation Draft:');
    return {
      action: isViolationDraft ? 'add' : 'update',
      path: adjustment.path,
      value: isViolationDraft ? adjustment.value : '',
      bbox: adjustment.bbox,
      page: adjustment.page ?? null,
      unit: adjustment.unit ?? 'inch',
      comment: isViolationDraft ? 'Violation field added from bounding box by reviewer' : 'Bounding box corrected by reviewer',
    };
  }

  private async submitViolationFieldAdd(
    fieldKey: keyof ViolationForm,
    label: string,
    jsonField: string,
    value: string,
    bboxField: ReviewField | undefined,
  ): Promise<boolean> {
    if (!this.batchId) {
      this.violationSaveStatus = 'error';
      this.violationSaveMessage = 'Missing batch ID. Reprocess the PDF before saving violation field.';
      return false;
    }

    const driverIndex = this.activeDriverIndex;
    const violationIndex = this.savedViolationIndex ?? this.toArray(this.currentDriver?.[VIOLATIONS_FIELD]).length;
    const operation: ReviewFeedbackOperation = {
      action: 'add',
      path: ['drivers', this.fileLocalDriverIndex(driverIndex), VIOLATIONS_FIELD, violationIndex, jsonField],
      value,
      bbox: bboxField?.bbox ? [...bboxField.bbox] : [],
      page: bboxField?.page ?? null,
      unit: bboxField?.unit ?? (bboxField?.bbox ? 'inch' : null),
      comment: 'Violation field added by reviewer',
    };

    this.violationSaveStatus = 'submitting';
    this.violationSaveMessage = `Saving ${label} to Falcon.`;

    try {
      const token = await this.getMvrToken();
      const response = await this.submitMvrReviewFeedbackByBbox(token, [operation]);
      this.savedViolationIndex = violationIndex;
      this.violationSaveStatus = 'success';
      this.violationSaveMessage = response.blob_name ? `${label} saved to Falcon.` : `${label} saved.`;
      this.reviewedBlobUrl = response.reviewed_blob_url ?? this.reviewedBlobUrl;
      const patchedValue = bboxField ? this.reviewedViolationFieldValue(response.reviewed_json, bboxField, jsonField) || value : value;
      this.savedViolationFieldValues[fieldKey] = patchedValue;
      this.newViolation[fieldKey] = patchedValue;
      if (fieldKey === 'description') {
        this.captureSavedViolationDescriptionOptions(response.reviewed_json, driverIndex, violationIndex, patchedValue);
      }
      if (bboxField) {
        bboxField.value = patchedValue;
        bboxField.documentValue = patchedValue;
      }
      this.addChangeLogEntry(label, '--', patchedValue || '--', 'Violation field add');
      return true;
    } catch (error) {
      this.violationSaveStatus = 'error';
      this.violationSaveMessage = error instanceof Error ? error.message : `Unable to save ${label}.`;
      return false;
    }
  }

  private violationDescriptionUpdateOperations(
    violationIndex: number,
    selectedValue: string,
    selectedOption: DescriptionOption | undefined,
  ): ReviewFeedbackOperation[] {
    const operations: ReviewFeedbackOperation[] = [
      {
        action: 'update',
        path: ['drivers', this.fileLocalDriverIndex(this.activeDriverIndex), VIOLATIONS_FIELD, violationIndex, 'Description'],
        value: selectedValue,
        bbox: selectedOption?.bbox ? [...selectedOption.bbox] : [],
        page: selectedOption?.page ?? null,
        unit: selectedOption?.unit ?? (selectedOption?.bbox ? 'inch' : null),
        comment: 'Violation description selected by reviewer',
      },
    ];

    if (selectedOption?.svcCode) {
      operations.push({
        action: 'update',
        path: ['drivers', this.fileLocalDriverIndex(this.activeDriverIndex), VIOLATIONS_FIELD, violationIndex, 'Viol Code'],
        value: selectedOption.svcCode,
        bbox: [],
        page: null,
        unit: null,
        comment: 'Violation code selected from description by reviewer',
      });
    }

    return operations;
  }

  private captureSavedViolationDescriptionOptions(
    reviewedJson: MvrDocument | undefined,
    driverIndex: number,
    violationIndex: number,
    fallbackValue: string,
  ): void {
    const reviewedDriver = reviewedJson?.drivers?.[this.fileLocalDriverIndex(driverIndex)];
    const reviewedDescription = this.valueAtPath(reviewedDriver, [VIOLATIONS_FIELD, violationIndex, 'Description']);
    const options = this.violationDescriptionOptions(reviewedDescription);
    this.savedViolationDescriptionOptions = options;
    this.savedViolationDescriptionSelection = options.find((option) => option.value === fallbackValue)?.value || options[0]?.value || fallbackValue;
    if (this.savedViolationDescriptionSelection) {
      this.savedViolationFieldValues.description = this.savedViolationDescriptionSelection;
      this.newViolation.description = this.savedViolationDescriptionSelection;
    }
    const selectedOption = this.descriptionOptionForValue(options, this.savedViolationDescriptionSelection);
    if (selectedOption?.svcCode) {
      this.savedViolationFieldValues.violationCode = selectedOption.svcCode;
      this.newViolation.violationCode = selectedOption.svcCode;
    }
  }

  private violationReviewOperations(driverIndex: number): ReviewFeedbackOperation[] {
    return VIOLATION_FORM_FIELDS
      .map(({ fieldKey, jsonField }) => this.violationFieldAddOperation(driverIndex, fieldKey, jsonField))
      .filter((operation): operation is ReviewFeedbackOperation => !!operation);
  }

  private violationFieldAddOperation(
    driverIndex: number,
    fieldKey: keyof ViolationForm,
    jsonField: string,
  ): ReviewFeedbackOperation | undefined {
    const savedValue = String(this.savedViolationFieldValues[fieldKey] ?? '').trim();
    const formValue = String(this.newViolation[fieldKey] ?? '').trim();
    const adjustment = this.violationFieldAdjustment(fieldKey, jsonField);
    const draftField = this.violationDraftField(fieldKey, jsonField);
    const bbox = adjustment?.bbox ?? draftField?.bbox;
    const value = savedValue || formValue || draftField?.value || '';
    const violationIndex = this.violationFieldIndex(draftField) ?? this.toArray(this.currentDriver?.[VIOLATIONS_FIELD]).length;

    if (!value && !bbox) return undefined;

    return {
      action: 'add',
      path: ['drivers', this.fileLocalDriverIndex(driverIndex), VIOLATIONS_FIELD, violationIndex, jsonField],
      value,
      bbox: bbox ? [...bbox] : [],
      page: bbox ? adjustment?.page ?? draftField?.page ?? null : null,
      unit: bbox ? adjustment?.unit ?? draftField?.unit ?? 'inch' : null,
      comment: 'Violation field added by reviewer',
    };
  }

  private violationFieldIndex(field: ReviewField | undefined): number | undefined {
    const index = field?.jsonPath.find((part) => typeof part === 'number');
    return typeof index === 'number' ? index : undefined;
  }

  private violationRecordFromForm(form: ViolationForm): DriverRecord {
    return {
      Type: this.violationFieldData('type', 'Type', form.type),
      'Violation Date': this.violationFieldData('violationDate', 'Violation Date', form.violationDate),
      'Conviction Date': this.violationFieldData('convictionDate', 'Conviction Date', form.convictionDate),
      'St Code': this.violationFieldData('stateCode', 'St Code', form.stateCode),
      Location: this.violationFieldData('location', 'Location', form.location),
      'Viol Code': this.violationFieldData('violationCode', 'Viol Code', form.violationCode),
      Speed: this.violationFieldData('speed', 'Speed', form.speed),
      Points: this.violationFieldData('points', 'Points', form.points),
      Description: this.violationFieldData('description', 'Description', form.description),
    };
  }

  private syncViolationSummary(driver: DriverRecord): void {
    const violations = this.toArray(driver[VIOLATIONS_FIELD]);
    const count = violations.length;
    const summary = count === 1 ? '1 violation record found.' : `${count} violation records found.`;
    driver[VIOLATIONS_COUNT_FIELD] = this.manualFieldData(String(count));
    driver[VIOLATIONS_SUMMARY_FIELD] = this.manualFieldData(summary);
  }

  private manualFieldData(value: string): FieldData {
    return {
      value: value.trim(),
      conf: '1.00',
      bbox: [],
      unit: null,
    };
  }

  private violationFieldData(fieldKey: keyof ViolationForm, jsonField: string, fallbackValue: string): FieldData {
    const savedValue = String(this.savedViolationFieldValues[fieldKey] ?? '').trim();
    const adjustment = this.violationFieldAdjustment(fieldKey, jsonField);
    const draftField = this.violationDraftField(fieldKey, jsonField);
    const value = savedValue || String(fallbackValue ?? '').trim() || draftField?.value || '';
    const bbox = adjustment?.bbox ?? draftField?.bbox;

    if (!bbox) return this.manualFieldData(value);

    return {
      value: value.trim(),
      conf: 'user_reviewed',
      bbox: [...bbox],
      page: adjustment?.page ?? draftField?.page,
      unit: adjustment?.unit ?? draftField?.unit ?? 'inch',
    };
  }

  private violationFieldAdjustment(fieldKey: keyof ViolationForm, jsonField: string): BboxAdjustment | undefined {
    return [...this.bboxSavedAdjustments.values()].find((adjustment) => {
      if (adjustment.driverIndex !== this.activeDriverIndex) return false;
      const meta = this.parseViolationDraftName(adjustment.fieldName);
      return meta?.fieldKey === fieldKey && meta.jsonField === jsonField;
    });
  }

  private violationDraftField(fieldKey: keyof ViolationForm, jsonField: string): ReviewField | undefined {
    return this.allDocumentFields.find((field) => {
      const meta = this.violationDraftMeta(field);
      return meta?.fieldKey === fieldKey && meta.jsonField === jsonField;
    });
  }

  private emptyViolationForm(): ViolationForm {
    return {
      type: '',
      violationDate: '',
      convictionDate: '',
      stateCode: '',
      location: '',
      violationCode: '',
      speed: '',
      points: '',
      description: '',
    };
  }

  private bboxAdjustmentFor(field: ReviewField): BboxAdjustment {
    return {
      driverIndex: this.activeDriverIndex,
      fieldId: field.id,
      fieldName: field.rawName,
      label: field.label,
      path: ['drivers', this.fileLocalDriverIndex(this.activeDriverIndex), ...field.jsonPath],
      value: field.value,
      page: field.page,
      lineId: field.lineId,
      bbox: [...field.bbox!],
      unit: field.unit ?? 'inch',
    };
  }

  private bboxKey(field: ReviewField, driverIndex = this.activeDriverIndex): string {
    return `${driverIndex}:${field.id}`;
  }

  private bboxAdjustmentKey(adjustment: BboxAdjustment): string {
    return `${adjustment.driverIndex}:${adjustment.fieldId}`;
  }

  private applyBboxOverrides(): void {
    this.allDocumentFields.forEach((field) => {
      const bbox = this.bboxOverrides.get(this.bboxKey(field));
      if (bbox) field.bbox = [...bbox];
    });
  }

  private scrollSelectedFieldIntoView(fieldId: string): void {
    const element = document.querySelector(`[data-field-id="${fieldId}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private openFieldContainer(field: ReviewField): void {
    const root = String(field.jsonPath[0] ?? '');
    if (root === VIOLATIONS_FIELD) {
      this.activeTab = 'violations';
      const violationIndex = field.jsonPath.find((part) => typeof part === 'number');
      if (typeof violationIndex === 'number') {
        this.openViolationItems[this.violationItemKey(violationIndex)] = true;
      }
      return;
    }

    if (root === SUSPENSIONS_FIELD) {
      this.activeTab = 'violations';
      const suspensionIndex = field.jsonPath.find((part) => typeof part === 'number');
      if (typeof suspensionIndex === 'number') {
        this.openSuspensionItems[this.suspensionItemKey(suspensionIndex)] = true;
      }
      return;
    }

    this.activeTab = 'driver';
    const isPrimaryField = this.fields.some((item) => item.id === field.id);
    if (isPrimaryField) {
      if (field.level === 'low' && field.status !== 'accepted') {
        this.lowGroupOpen = true;
      } else {
        this.highGroupOpen = true;
      }
      return;
    }

    const sectionId = root === 'License Information'
      ? 'license-information'
      : root === 'CDL Medical Information'
        ? 'cdl-medical'
        : this.slug(root);
    if (!sectionId) return;

    this.openDetailSections[sectionId] = true;
    const itemIndex = field.jsonPath.find((part) => typeof part === 'number');
    if (typeof itemIndex === 'number') {
      this.openDetailItems[this.detailItemKey(sectionId, itemIndex)] = true;
    }
  }

  private detailItemKey(sectionId: string, index: number): string {
    return `${sectionId}-${index}`;
  }

  private violationItemKey(index: number): string {
    return `violation-${index}`;
  }

  private suspensionItemKey(index: number): string {
    return `suspension-${index}`;
  }

  private addChangeLogEntry(field: string, previousValue: string, newValue: string, changeType: string): void {
    this.changeLogEntries = [
      {
        id: `${Date.now()}-${this.changeLogEntries.length}`,
        field,
        previousValue: previousValue || '--',
        newValue: newValue || '--',
        changedAt: new Date().toLocaleString(),
        changeType,
      },
      ...this.changeLogEntries,
    ];
  }

  private withDetailFieldIds(sections: DetailSection[]): DetailSection[] {
    const fieldNamesByLabel: Record<string, string> = {
      Class: 'Class',
      'Issue Date': 'Issue Date',
      'Expiration Date': 'Expiration Date',
      Endorsements: 'Endorsements',
      Restrictions: 'Restrictions',
      'Certificate Type': 'Self Certificate Type',
      Status: 'Status',
      'Issued Date': 'Issued Date',
    };

    return sections.map((section) => ({
      ...section,
      items: section.items.map((item, itemIndex) => ({
        ...item,
        fields: item.fields?.map((field) => {
          const fieldName = fieldNamesByLabel[field.label];
          if (!fieldName) return field;
          const root = section.id === 'license-information' ? 'License Information' : 'CDL Medical Information';
          const path: JsonPathPart[] = section.id === 'license-information' ? [root, itemIndex, fieldName] : [root, fieldName];
          return { ...field, fieldId: this.detailFieldId(path) };
        }),
      })),
    }));
  }

  private buildDetailSections(driver: DriverRecord): DetailSection[] {
    return [];
  }

  private licenseInformationItems(driver: DriverRecord): DetailItem[] {
    return this.toArray(driver['License Information']).map((record, index) => {
      const type = this.fieldValue(record['Type']) || `License ${index + 1}`;
      const licenseClass = this.fieldValue(record['Class']);
      const classField = record['Class'];
      const licenseClassEvidence = this.isFieldData(classField) ? this.fieldEvidenceComment(classField) : undefined;
      const issue = this.fieldValue(record['Issue Date']);
      const exp = this.fieldValue(record['Expiration Date']);
      const status = this.fieldValue(record['Status']) || 'VALID';
      const endorsements = this.fieldValue(record['Endorsements']);
      const restrictions = this.fieldValue(record['Restrictions']);

      return {
        title: type,
        value: index === 0 ? `License ${index + 1}` : type,
        badge: status,
        fields: [
          { label: 'Class', value: licenseClass || '—', validationComment: licenseClassEvidence },
          { label: 'Issue Date', value: issue || '—' },
          { label: 'Expiration Date', value: exp || '—' },
          { label: 'Endorsements', value: endorsements || '—' },
          { label: 'Restrictions', value: restrictions || '—' },
        ],
        meta: `${status}${exp ? ` · Exp: ${exp}` : ''}`,
      };
    });
  }

  private cdlMedicalItems(driver: DriverRecord): DetailItem[] {
    const cdl = driver['CDL Medical Information'];
    const records = Array.isArray(cdl) ? (cdl as DriverRecord[]) : cdl && typeof cdl === 'object' ? [cdl as DriverRecord] : [];

    return records.map((record) => {
      const status = this.fieldValue(record['Status']);
      const certType = this.fieldValue(record['Self Certificate Type']);
      const issued = this.fieldValue(record['Issued Date']);
      const exp = this.fieldValue(record['Expiration Date']);

      return {
        title: certType || 'CDL Medical',
        value: certType || 'No certificate type',
        badge: status || 'CERTIFIED',
        meta: exp ? `Exp: ${exp}` : undefined,
        fields: [
          { label: 'Certificate Type', value: certType || '—' },
          { label: 'Status', value: status || '—' },
          { label: 'Issued Date', value: issued || '—' },
          { label: 'Expiration Date', value: exp || '—' },
        ],
      };
    });
  }

  private toArray(value: unknown): DriverRecord[] {
    return Array.isArray(value) ? (value as DriverRecord[]) : [];
  }

  private driverExportRows(): SpreadsheetCell[][] {
    const drivers = this.mvrData?.drivers ?? [];
    return [
      [...DRIVER_EXPORT_HEADERS],
      ...drivers.map((driver) => this.driverExportRow(driver)),
    ];
  }

  private driverExportRow(driver: DriverRecord): SpreadsheetCell[] {
    const licenseRecord = this.toArray(driver['License Information'])[0];
    const cdlRecord = this.toArray(driver['CDL Medical Information'])[0] ?? (this.isPlainRecord(driver['CDL Medical Information']) ? (driver['CDL Medical Information'] as DriverRecord) : undefined);

    return [
      0,
      this.exportFieldValue(driver, ['Suffix']),
      this.exportFieldValue(driver, ['Given Name', 'First Name']),
      this.exportFieldValue(driver, ['Last Name', 'Surname']),
      this.exportFieldValue(driver, ['Date of Birth', 'DOB']),
      this.exportFieldValue(driver, ['License Number', 'DL Number', 'Primary License']),
      this.exportFieldValue(driver, ['Years Of Experience', 'Years of Experience', 'Experience']),
      this.exportFieldValue(driver, ['State of Driver Record', 'Driver State', 'LIC State', 'License State']),
      this.exportFieldValue(driver, ['Expiration Date', 'License Expiration Date']) || this.exportFieldValue(licenseRecord, ['Expiration Date', 'Expires']),
      this.exportFieldValue(driver, ['License Class Type', 'Class']) || this.exportFieldValue(licenseRecord, ['Class', 'License Class']),
      this.exportFieldValue(cdlRecord, ['Expiration Date', 'Medical Expiration Date', 'MED CERT EXP', 'Med Cert Exp']),
      this.exportFieldValue(driver, ['Driver Phone', 'Phone', 'Phone Number']),
      this.exportFieldValue(driver, ['Driver Email', 'Email', 'Email Address']),
      this.exportFieldValue(driver, ['Driver Address', 'Driver Address Line 1', 'Addr Line 1', 'Address']),
      this.exportFieldValue(driver, ['Driver City', 'City']),
      this.exportFieldValue(driver, ['Driver State', 'State']),
      this.exportFieldValue(driver, ['Driver ZIP', 'Driver Zip', 'ZIP', 'Zip', 'Postal Code']),
    ];
  }

  private exportFieldValue(record: unknown, keys: string[]): string {
    if (!this.isPlainRecord(record)) return '';

    for (const key of keys) {
      const directValue = this.fieldValue(record[key]);
      if (directValue) return directValue;
    }

    const normalizedKeys = new Set(keys.map((key) => this.normalizedExportKey(key)));
    return this.findNestedExportFieldValue(record, normalizedKeys);
  }

  private findNestedExportFieldValue(value: unknown, normalizedKeys: Set<string>): string {
    if (Array.isArray(value)) {
      for (const item of value) {
        const nestedValue = this.findNestedExportFieldValue(item, normalizedKeys);
        if (nestedValue) return nestedValue;
      }
      return '';
    }

    if (!this.isPlainRecord(value)) return '';

    for (const [key, childValue] of Object.entries(value)) {
      if (normalizedKeys.has(this.normalizedExportKey(key))) {
        const fieldDataValue = this.fieldValue(childValue);
        if (fieldDataValue) return fieldDataValue;
      }
    }

    for (const childValue of Object.values(value)) {
      if (this.isFieldData(childValue)) continue;
      const nestedValue = this.findNestedExportFieldValue(childValue, normalizedKeys);
      if (nestedValue) return nestedValue;
    }

    return '';
  }

  private normalizedExportKey(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private isPlainRecord(value: unknown): value is DriverRecord {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private createXlsxBlob(rows: SpreadsheetCell[][]): Blob {
    const sheetXml = this.worksheetXml(rows);
    const files = [
      {
        name: '[Content_Types].xml',
        content:
          '<?xml version="1.0" encoding="UTF-8"?>' +
          '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
          '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
          '<Default Extension="xml" ContentType="application/xml"/>' +
          '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
          '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
          '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>' +
          '<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>' +
          '</Types>',
      },
      {
        name: '_rels/.rels',
        content:
          '<?xml version="1.0" encoding="UTF-8"?>' +
          '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
          '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
          '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>' +
          '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>' +
          '</Relationships>',
      },
      {
        name: 'xl/workbook.xml',
        content:
          '<?xml version="1.0" encoding="UTF-8"?>' +
          '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
          '<sheets><sheet name="Drivers" sheetId="1" r:id="rId1"/></sheets></workbook>',
      },
      {
        name: 'xl/_rels/workbook.xml.rels',
        content:
          '<?xml version="1.0" encoding="UTF-8"?>' +
          '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
          '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
          '</Relationships>',
      },
      { name: 'xl/worksheets/sheet1.xml', content: sheetXml },
      {
        name: 'docProps/core.xml',
        content:
          '<?xml version="1.0" encoding="UTF-8"?>' +
          '<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">' +
          '<dc:creator>MVR Review</dc:creator><cp:lastModifiedBy>MVR Review</cp:lastModifiedBy>' +
          `<dcterms:created xsi:type="dcterms:W3CDTF">${new Date().toISOString()}</dcterms:created>` +
          '</cp:coreProperties>',
      },
      {
        name: 'docProps/app.xml',
        content:
          '<?xml version="1.0" encoding="UTF-8"?>' +
          '<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">' +
          '<Application>MVR Review</Application></Properties>',
      },
    ];

    return this.zipBlob(files);
  }

  private worksheetXml(rows: SpreadsheetCell[][]): string {
    const lastColumn = this.columnName(Math.max(...rows.map((row) => row.length), 1));
    const dimension = `A1:${lastColumn}${rows.length}`;
    const columnWidths = DRIVER_EXPORT_HEADERS.map((header, index) => `<col min="${index + 1}" max="${index + 1}" width="${Math.max(10, header.length + 4)}" customWidth="1"/>`).join('');
    const rowXml = rows.map((row, rowIndex) => this.worksheetRowXml(row, rowIndex + 1)).join('');

    return (
      '<?xml version="1.0" encoding="UTF-8"?>' +
      '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
      `<dimension ref="${dimension}"/>` +
      `<cols>${columnWidths}</cols>` +
      `<sheetData>${rowXml}</sheetData>` +
      '</worksheet>'
    );
  }

  private worksheetRowXml(row: SpreadsheetCell[], rowNumber: number): string {
    const cells = row.map((cell, index) => this.worksheetCellXml(cell, this.cellRef(index + 1, rowNumber))).join('');
    return `<row r="${rowNumber}">${cells}</row>`;
  }

  private worksheetCellXml(cell: SpreadsheetCell, ref: string): string {
    const value = cell ?? '';
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `<c r="${ref}"><v>${value}</v></c>`;
    }

    return `<c r="${ref}" t="inlineStr"><is><t>${this.escapeXml(String(value))}</t></is></c>`;
  }

  private cellRef(column: number, row: number): string {
    return `${this.columnName(column)}${row}`;
  }

  private columnName(column: number): string {
    let name = '';
    let index = column;
    while (index > 0) {
      const remainder = (index - 1) % 26;
      name = String.fromCharCode(65 + remainder) + name;
      index = Math.floor((index - 1) / 26);
    }
    return name;
  }

  private escapeXml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private zipBlob(files: Array<{ name: string; content: string }>): Blob {
    const encoder = new TextEncoder();
    const localParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;

    files.forEach((file) => {
      const nameBytes = encoder.encode(file.name);
      const data = encoder.encode(file.content);
      const crc = this.crc32(data);
      const localHeader = new Uint8Array(30 + nameBytes.length);
      const localView = new DataView(localHeader.buffer);
      localView.setUint32(0, 0x04034b50, true);
      localView.setUint16(4, 20, true);
      localView.setUint16(8, 0, true);
      localView.setUint32(14, crc, true);
      localView.setUint32(18, data.length, true);
      localView.setUint32(22, data.length, true);
      localView.setUint16(26, nameBytes.length, true);
      localHeader.set(nameBytes, 30);
      localParts.push(localHeader, data);

      const centralHeader = new Uint8Array(46 + nameBytes.length);
      const centralView = new DataView(centralHeader.buffer);
      centralView.setUint32(0, 0x02014b50, true);
      centralView.setUint16(4, 20, true);
      centralView.setUint16(6, 20, true);
      centralView.setUint32(16, crc, true);
      centralView.setUint32(20, data.length, true);
      centralView.setUint32(24, data.length, true);
      centralView.setUint16(28, nameBytes.length, true);
      centralView.setUint32(42, offset, true);
      centralHeader.set(nameBytes, 46);
      centralParts.push(centralHeader);
      offset += localHeader.length + data.length;
    });

    const centralOffset = offset;
    const centralSize = centralParts.reduce((total, part) => total + part.length, 0);
    const end = new Uint8Array(22);
    const endView = new DataView(end.buffer);
    endView.setUint32(0, 0x06054b50, true);
    endView.setUint16(8, files.length, true);
    endView.setUint16(10, files.length, true);
    endView.setUint32(12, centralSize, true);
    endView.setUint32(16, centralOffset, true);

    const allParts = [...localParts, ...centralParts, end];
    const totalLength = allParts.reduce((total, part) => total + part.length, 0);
    const combined = new Uint8Array(totalLength);
    let cursor = 0;
    allParts.forEach((part) => {
      combined.set(part, cursor);
      cursor += part.length;
    });

    return new Blob([combined.buffer as ArrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  private crc32(data: Uint8Array): number {
    let crc = 0xffffffff;
    for (const byte of data) {
      crc ^= byte;
      for (let bit = 0; bit < 8; bit += 1) {
        crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
      }
    }
    return (crc ^ 0xffffffff) >>> 0;
  }

  private async loadPdfDocument(): Promise<void> {
    if (this.pdfDocument) return;
    if (!this.pdfSourceUrl) return;
    await this.pdfWorkerReady;
    this.pdfDocument = await pdfjsLib.getDocument(this.pdfSourceUrl).promise;
    this.pdfPageCount = this.pdfDocument.numPages;
  }

  private async updatePdfPage(page: number, fitToWidth = false): Promise<void> {
    await this.renderPdfPage(page, fitToWidth);
  }

  private scrollPdfToField(field: ReviewField, behavior: ScrollBehavior = 'smooth'): void {
    if (!field.bbox || !this.pdfFrame) return;
    const frame = this.pdfFrame.nativeElement;
    const targetTop = this.hotspotTop(field) - frame.clientHeight / 2 + this.hotspotHeight(field) / 2;
    frame.scrollTo({
      left: frame.scrollLeft,
      top: Math.max(0, targetTop),
      behavior,
    });
  }

  private scrollPdfToFieldWhenReady(field: ReviewField): void {
    this.scrollPdfToField(field, 'auto');
    requestAnimationFrame(() => this.scrollPdfToField(field, 'auto'));
    setTimeout(() => this.scrollPdfToField(field, 'auto'), 80);
    setTimeout(() => this.scrollPdfToField(field), 220);
  }

  private setPdfZoom(scale: number): void {
    this.pdfPageScale = Math.max(0.75, Math.min(1.8, Math.round(scale * 100) / 100));
    void this.renderPdfPage(this.currentPdfPage);
  }

  private async renderPdfPage(pageNumber: number, fitToWidth = false): Promise<void> {
    if (!this.pdfCanvas) return;
    await this.loadPdfDocument();
    if (!this.pdfDocument) return;

    this.renderTask?.cancel();
    const safePageNumber = Math.max(1, Math.min(pageNumber, this.pdfDocument.numPages));
    const page = await this.pdfDocument.getPage(safePageNumber);
    const baseViewport = page.getViewport({ scale: 1 });
    if (fitToWidth) {
      const frameWidth = this.pdfFrame?.nativeElement.clientWidth ?? 0;
      const fitScale = frameWidth > 0 ? (frameWidth - 8) / baseViewport.width : this.pdfPageScale;
      this.pdfPageScale = Math.max(0.75, Math.min(1.8, Math.round(fitScale * 100) / 100));
    }
    const viewport = page.getViewport({ scale: this.pdfPageScale });
    const canvas = this.pdfCanvas.nativeElement;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    context.clearRect(0, 0, canvas.width, canvas.height);
    this.currentPdfPage = safePageNumber;
    this.renderedPdfPage = safePageNumber;

    this.renderTask = page.render({ canvasContext: context, viewport });
    try {
      await this.renderTask.promise;
    } catch (error) {
      if ((error as Error).name !== 'RenderingCancelledException') {
        console.error('Unable to render PDF page', error);
      }
    }
  }

  private selectNextPendingField(): void {
    const nextField = this.lowFields.find((field) => field.status === 'pending' || field.status === 'editing');
    if (nextField) this.selectField(nextField);
  }

  private confidenceFor(fieldName: string, field: FieldData): number {
    const parsed = Number.parseFloat(String(field.conf));
    if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed <= 1 ? parsed * 100 : parsed);
    return FALLBACK_CONFIDENCE[fieldName] ?? 80;
  }

  private isLowConfidenceField(field: FieldData, value: string, confidence: number): boolean {
    if (!value.trim()) return true;
    return confidence < LOW_CONFIDENCE_THRESHOLD;
  }

  private confidenceLevelFor(value: string, confidence: number): ConfidenceLevel {
    return value.trim() && confidence >= LOW_CONFIDENCE_THRESHOLD ? 'high' : 'low';
  }

  private markFieldAccepted(field: ReviewField): void {
    field.status = 'accepted';
    field.confidence = Math.max(field.confidence, 100);
    field.level = 'high';
    field.userReviewed = true;
  }

  private isUserReviewedFieldData(field: FieldData): boolean {
    const reviewStatus = String(field.review_status ?? '').trim().toLowerCase();
    return field.user_reviewed === true
      || reviewStatus === 'accepted'
      || reviewStatus === 'edited'
      || String(field.conf ?? '').trim().toLowerCase() === 'user_reviewed';
  }

  private fieldValue(field: unknown): string {
    if (!field || typeof field !== 'object' || !('value' in field)) return '';
    return String((field as FieldData).value ?? '').trim();
  }

  private violationDescriptionOptions(field: unknown): DescriptionOption[] {
    if (!field || typeof field !== 'object') {
      const value = String(field ?? '').trim();
      return value ? [{ key: 'value', value, confidence: null }] : [];
    }

    const record = field as Record<string, unknown>;
    const falconTopOptions = this.falconDescriptionOptions(record);
    if (falconTopOptions.length) return falconTopOptions;

    const numberedOptions: DescriptionOption[] = Object.keys(record)
      .map((key) => {
        const match = key.match(/^desc(\d+)$/i);
        return match ? { key, index: Number(match[1]) } : null;
      })
      .filter((item): item is { key: string; index: number } => !!item)
      .sort((a, b) => a.index - b.index)
      .reduce<DescriptionOption[]>((options, { key, index }) => {
        const value = String(record[key] ?? '').trim();
        if (!value) return options;
        options.push({
          key,
          value,
          confidence: this.normalizedConfidence(record[`conf${index}`]),
          bbox: this.fieldBbox(record as unknown as FieldData),
          page: typeof record['page'] === 'number' ? record['page'] : undefined,
          unit: typeof record['unit'] === 'string' ? record['unit'] : null,
        });
        return options;
      }, []);

    if (numberedOptions.length) return numberedOptions;

    const value = this.fieldValue(field);
    if (!value) return [];
    return [
      {
        key: 'value',
        value,
        confidence: this.normalizedConfidence((record as unknown as FieldData).conf),
        bbox: this.fieldBbox(record as unknown as FieldData),
        page: typeof record['page'] === 'number' ? record['page'] : undefined,
        unit: typeof record['unit'] === 'string' ? record['unit'] : null,
      },
    ];
  }

  private falconDescriptionOptions(record: Record<string, unknown>): DescriptionOption[] {
    const candidates = Array.isArray(record['falcon_top5'])
      ? (record['falcon_top5'] as Record<string, unknown>[])
      : Array.isArray(record['falcon_top'])
        ? (record['falcon_top'] as Record<string, unknown>[])
        : [];
    return candidates.reduce<DescriptionOption[]>((options, candidate, index) => {
      if (!candidate || typeof candidate !== 'object') return options;
      const description = String(candidate['description'] ?? '').trim();
      if (!description) return options;
      options.push({
        key: `falcon-top-${index}`,
        value: description,
        confidence: this.normalizedConfidence(candidate['confidence']),
        svcCode: String(candidate['svc_code'] ?? '').trim() || undefined,
        bbox: this.fieldBbox(record as unknown as FieldData),
        page: typeof record['page'] === 'number' ? record['page'] : undefined,
        unit: typeof record['unit'] === 'string' ? record['unit'] : null,
      });
      return options;
    }, []);
  }

  private descriptionCandidateFieldData(field: unknown): FieldData | undefined {
    if (!field || typeof field !== 'object') return undefined;
    const record = field as Record<string, unknown>;
    const hasDescriptionCandidates = Array.isArray(record['falcon_top5'])
      || Array.isArray(record['falcon_top'])
      || Object.keys(record).some((key) => /^desc\d+$/i.test(key));
    if (!hasDescriptionCandidates) return undefined;
    const options = this.violationDescriptionOptions(field);
    const first = options[0];
    if (!first) return undefined;
    return {
      value: first.value,
      conf: first.confidence !== null ? first.confidence / 100 : '',
      bbox: first.bbox,
      page: first.page,
      unit: first.unit,
    };
  }

  private selectedViolationDescription(violationIndex: number, options: DescriptionOption[], currentValue = ''): string {
    const selected = this.descriptionSelections[this.descriptionSelectionKey(violationIndex)];
    if (selected !== undefined) return selected;
    return currentValue;
  }

  private descriptionOptionForValue(options: DescriptionOption[], value: string): DescriptionOption | undefined {
    return options.find((option) => option.value === value);
  }

  private violationJsonPath(violationIndex: number, jsonField: string): JsonPathPart[] {
    if (jsonField === ORIGINAL_DESCRIPTION_FIELD) {
      return [VIOLATIONS_FIELD, violationIndex, 'Description'];
    }
    return [VIOLATIONS_FIELD, violationIndex, jsonField];
  }

  private suspensionJsonPath(suspensionIndex: number, jsonField: string): JsonPathPart[] {
    return [SUSPENSIONS_FIELD, suspensionIndex, jsonField];
  }

  private violationFieldDataForEdit(record: DriverRecord, jsonField: string): FieldData | undefined {
    const field = jsonField === ORIGINAL_DESCRIPTION_FIELD ? record['Description'] : record[jsonField];
    return this.isFieldData(field) ? field : undefined;
  }

  private violationValueForEdit(record: DriverRecord, jsonField: string): string {
    if (jsonField === ORIGINAL_DESCRIPTION_FIELD) return this.violationOriginalDescription(record['Description']);
    return this.fieldValue(record[jsonField]);
  }

  private suspensionFieldDataForEdit(record: DriverRecord, jsonField: string): FieldData | undefined {
    const field = record[jsonField];
    return this.isFieldData(field) ? field : undefined;
  }

  private suspensionValueForEdit(record: DriverRecord, jsonField: string): string {
    return this.fieldValue(record[jsonField]);
  }

  private violationOriginalDescription(field: unknown): string {
    if (!field || typeof field !== 'object') return '';
    const record = field as Record<string, unknown>;
    return this.scalarFieldText(record['original_description'])
      || this.scalarFieldText(record['original_value'])
      || '';
  }

  private scalarFieldText(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object' && !Array.isArray(value)) {
      const record = value as Record<string, unknown>;
      return this.scalarFieldText(record['value']);
    }
    return String(value).trim();
  }

  private reviewedViolationRecord(reviewedJson: MvrDocument | undefined, driverIndex: number, violationIndex: number): DriverRecord | undefined {
    const driver = reviewedJson?.drivers?.[this.fileLocalDriverIndex(driverIndex)];
    if (!driver) return undefined;
    return this.toArray(driver[VIOLATIONS_FIELD])[violationIndex];
  }

  private reviewedSuspensionFieldValue(reviewedJson: MvrDocument | undefined, field: ReviewField, jsonField: string): string {
    const reviewedDriver = reviewedJson?.drivers?.[this.fileLocalDriverIndex(this.activeDriverIndex)];
    if (!reviewedDriver) return '';

    const pathValue = this.valueAtPath(reviewedDriver, field.jsonPath);
    const directValue = this.fieldValue(pathValue);
    if (directValue) return directValue;
    if (pathValue !== null && pathValue !== undefined && (typeof pathValue !== 'object' || Array.isArray(pathValue))) {
      return String(pathValue).trim();
    }

    const suspensionIndex = field.jsonPath.find((part) => typeof part === 'number');
    const records = this.toArray(reviewedDriver[SUSPENSIONS_FIELD]);
    const record = typeof suspensionIndex === 'number' ? records[suspensionIndex] : records[records.length - 1];
    return this.fieldValue((record as DriverRecord | undefined)?.[jsonField]);
  }

  private normalizedConfidence(value: unknown): number | null {
    const parsed = Number.parseFloat(String(value ?? ''));
    if (!Number.isFinite(parsed)) return null;
    return Math.round((parsed <= 1 ? parsed * 100 : parsed) * 10) / 10;
  }

  private patchViolationDescription(record: DriverRecord, value: string, option: DescriptionOption | undefined): void {
    const current = record['Description'];
    const base = current && typeof current === 'object' ? { ...(current as Record<string, unknown>) } : {};
    record['Description'] = {
      ...base,
      value,
      conf: 'user_reviewed',
      bbox: option?.bbox ? [...option.bbox] : [],
      page: option?.page ?? null,
      unit: option?.unit ?? (option?.bbox ? 'inch' : null),
    };
  }

  private patchViolationCode(record: DriverRecord, value: string): void {
    const current = record['Viol Code'];
    const base = current && typeof current === 'object' ? { ...(current as Record<string, unknown>) } : {};
    record['Viol Code'] = {
      ...base,
      value,
      conf: 'user_reviewed',
      bbox: this.isFieldData(current) ? current.bbox ?? [] : [],
      page: this.isFieldData(current) ? current.page ?? null : null,
      unit: this.isFieldData(current) ? current.unit ?? null : null,
    };
  }

  private patchViolationSimpleField(record: DriverRecord, jsonField: string, value: string, fieldData: FieldData | undefined): void {
    if (jsonField === ORIGINAL_DESCRIPTION_FIELD) {
      const current = record['Description'];
      const base = current && typeof current === 'object' ? { ...(current as Record<string, unknown>) } : {};
      record['Description'] = {
        ...base,
        original_description: value,
        conf: 'user_reviewed',
        bbox: fieldData?.bbox ?? [],
        page: fieldData?.page ?? null,
        unit: fieldData?.unit ?? null,
      };
      return;
    }

    const current = record[jsonField];
    const base = current && typeof current === 'object' ? { ...(current as Record<string, unknown>) } : {};
    record[jsonField] = {
      ...base,
      value,
      conf: 'user_reviewed',
      bbox: fieldData?.bbox ?? [],
      page: fieldData?.page ?? null,
      unit: fieldData?.unit ?? null,
    };
  }

  private patchSuspensionSimpleField(record: DriverRecord, jsonField: string, value: string, fieldData: FieldData | undefined): void {
    const current = record[jsonField];
    const base = current && typeof current === 'object' ? { ...(current as Record<string, unknown>) } : {};
    record[jsonField] = {
      ...base,
      value,
      conf: 'user_reviewed',
      bbox: fieldData?.bbox ?? [],
      page: fieldData?.page ?? null,
      unit: fieldData?.unit ?? null,
    };
  }

  private driverName(driver: DriverRecord): string {
    const first = this.fieldValue(driver['Given Name']);
    const last = this.fieldValue(driver['Last Name']);
    return `${first} ${last}`.trim() || 'Unknown Driver';
  }

  // Feedback responses only contain one file's drivers; keep other files' drivers intact.
  private mergeReviewedDrivers(reviewedDrivers: DriverRecord[]): DriverRecord[] {
    const existingDrivers = this.mvrData?.drivers;
    if (!existingDrivers || existingDrivers.length <= reviewedDrivers.length) return reviewedDrivers;
    return existingDrivers.map(
      (existing) => reviewedDrivers.find((updated) => this.driverName(updated) === this.driverName(existing)) ?? existing,
    );
  }

  private driverInitials(driver: DriverRecord): string {
    const first = this.fieldValue(driver['Given Name']).charAt(0);
    const last = this.fieldValue(driver['Last Name']).charAt(0);
    return `${first}${last || first}`.toUpperCase() || 'DR';
  }

  private slug(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  private pdfSourceForDriver(driver: DriverRecord): string {
    const fileName = this.fieldValue(driver['File Name']);
    const matchingUpload = this.uploadQueue.find((item) => this.normalizedFileName(item.file.name) === this.normalizedFileName(fileName));
    return matchingUpload?.objectUrl || this.fieldValue(driver['File URL']);
  }

  private normalizedFileName(value: string): string {
    return value.trim().toLowerCase();
  }

  private async setPdfFiles(files: File[]): Promise<void> {
    const pdfFiles = files.filter((file) => file.name.toLowerCase().endsWith('.pdf'));
    if (!pdfFiles.length) {
      this.uploadStatus = 'error';
      this.uploadError = 'Only PDF files are supported for MVR extraction.';
      return;
    }

    if (pdfFiles.length > this.maxPdfFiles) {
      this.uploadStatus = 'error';
      this.uploadError = `Too many files. Select at most ${this.maxPdfFiles} PDFs at a time.`;
      return;
    }

    const oversized = pdfFiles.find((file) => file.size === 0 || file.size > this.maxPdfBytes);
    if (oversized) {
      this.uploadStatus = 'error';
      this.uploadError = `"${oversized.name}" must be a non-empty PDF no larger than ${Math.round(this.maxPdfBytes / (1024 * 1024))} MB.`;
      return;
    }

    const totalBytes = pdfFiles.reduce((total, file) => total + file.size, 0);
    if (totalBytes > this.maxTotalUploadBytes) {
      this.uploadStatus = 'error';
      this.uploadError = `Total upload size exceeds ${Math.round(this.maxTotalUploadBytes / (1024 * 1024))} MB. Remove some files and try again.`;
      return;
    }

    for (const file of pdfFiles) {
      if (!(await this.hasPdfMagicBytes(file))) {
        this.uploadStatus = 'error';
        this.uploadError = `"${file.name}" is not a valid PDF file.`;
        return;
      }
    }

    this.revokeUploadObjectUrls();
    this.selectedPdfFiles = pdfFiles;
    this.uploadQueue = pdfFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      file,
      objectUrl: URL.createObjectURL(file),
      status: 'pending',
      message: 'Pending',
    }));
    const firstFile = this.uploadQueue[0];
    this.selectedPdfFile = firstFile.file;
    this.pdfFileName = firstFile.file.name;
    this.pdfObjectUrl = firstFile.objectUrl;
    this.pdfSourceUrl = this.pdfObjectUrl;
    this.pdfPath = '';
    this.batchId = null;
    this.pollAttempt = 0;
    this.uploadStatus = 'idle';
    this.uploadError = '';
    this.uploadMessage = `${pdfFiles.length} PDF${pdfFiles.length === 1 ? '' : 's'} selected. Start processing when ready.`;
  }

  private async hasPdfMagicBytes(file: File): Promise<boolean> {
    try {
      const header = new Uint8Array(await file.slice(0, 5).arrayBuffer());
      // "%PDF-" signature.
      return header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46 && header[4] === 0x2d;
    } catch {
      return false;
    }
  }

  private revokeUploadObjectUrls(): void {
    this.uploadQueue.forEach((item) => URL.revokeObjectURL(item.objectUrl));
    if (!this.uploadQueue.length && this.pdfObjectUrl) URL.revokeObjectURL(this.pdfObjectUrl);
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Unable to read the selected PDF.'));
      reader.onload = () => {
        const result = String(reader.result ?? '');
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      reader.readAsDataURL(file);
    });
  }

  private captureApiKeyFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const apiKey = params.get('api_key') || params.get('api-key') || params.get('apiKey');
    if (!apiKey?.trim()) return;

    this.apiKey = apiKey.trim();
    this.apiCredentials = null;

    // Remove the secret from the URL so it can't leak via history, referrer, or bookmarks.
    params.delete('api_key');
    params.delete('api-key');
    params.delete('apiKey');
    const query = params.toString();
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
  }

  private captureDriverHistoryContextFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const tenant = params.get('tenant') || params.get('tenant_id') || params.get('tenantId');
    if (tenant?.trim()) this.driverHistoryTenant = tenant.trim();
  }

  private captureDriverHistoryContextFromMessage(message: Record<string, unknown>): void {
    const tenant = [message['tenant'], message['tenant_id'], message['tenantId']]
      .map((value) => String(value ?? '').trim())
      .find(Boolean);
    if (tenant) this.driverHistoryTenant = tenant;
  }

  private todayInputDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private driverHistoryExportFileName(response: Response): string {
    const disposition = response.headers.get('content-disposition') ?? '';
    const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
    const responseFileName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1]) : '';
    if (responseFileName) return responseFileName;

    const fromDate = this.driverHistoryFromDate || this.todayInputDate();
    const toDate = this.driverHistoryToDate || fromDate;
    return `mvr-driver-history-${fromDate}-to-${toDate}.xlsx`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }

  private currentApiKey(): string {
    return this.apiKey;
  }

  private cachedApiCredentials(): ApiCredentials | null {
    return this.apiCredentials;
  }

  private async validateApiKey(forceRefresh = false): Promise<ApiCredentials> {
    if (!forceRefresh) {
      const cached = this.cachedApiCredentials();
      if (cached) return cached;
    }

    const apiKey = this.currentApiKey();
    if (!apiKey) {
      throw new Error('Missing API key. Open this app from Falcon with a valid api_key parameter.');
    }

    const response = await fetch(this.apiKeyValidationUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ api_key: apiKey }),
    });

    if (!response.ok) {
      throw new Error(await this.apiErrorMessage(response, 'API key validation failed'));
    }

    const data = (await response.json()) as {
      success?: boolean;
      api_user_id?: string;
      api_email?: string;
      api_password?: string;
      error?: string;
      message?: string;
    };

    if (!data.success || !data.api_user_id || !data.api_email || !data.api_password) {
      throw new Error(data.error || data.message || 'API key validation did not return Falcon credentials.');
    }

    this.apiCredentials = {
      user_id: data.api_user_id,
      email: data.api_email,
      password: data.api_password,
    };
    return this.apiCredentials;
  }

  private async getMvrToken(): Promise<string> {
    const tokenUser = await this.validateApiKey();
    const response = await fetch(`${this.apiBaseUrl}/get-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tokenUser),
    });

    if (!response.ok) {
      throw new Error(`Token request failed (${response.status}).`);
    }

    const data = (await response.json()) as { token?: string };
    if (!data.token) throw new Error('Token response did not include a token.');
    return data.token;
  }

  private async submitMvrBatch(token: string, files: MvrBatchFilePayload[]): Promise<{ batch_id: number | string }> {
    const payload: Record<string, unknown> = {
      files,
      include_bbox: this.stringBooleanConfigValue(this.apiConfig.includeBbox),
      include_highlighted_pdf: this.stringBooleanConfigValue(this.apiConfig.includeHighlightedPdf),
      quote_id: `BATCH-MVR-${Date.now()}`,
    };
    if (this.apiConfig.callbackUrl) {
      payload['callback_url'] = this.apiConfig.callbackUrl;
    }

    const response = await fetch(`${this.apiBaseUrl}/get-mvr-data-batch`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok && response.status !== 202) {
      throw new Error(`MVR batch request failed (${response.status}).`);
    }

    const data = (await response.json()) as { batch_id?: number | string };
    if (!data.batch_id) throw new Error('Batch response did not include batch_id.');
    return { batch_id: data.batch_id };
  }

  private async submitFieldsToFalcon(token: string, fileNames: string[]): Promise<Record<string, unknown>> {
    const payload = {
      batch_id: this.normalizedBatchId(),
      files: fileNames.length ? fileNames : [this.currentReviewFileName()],
    };

    const response = await fetch(`${this.apiBaseUrl}/fields-submit-to-falcon`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await this.apiErrorMessage(response, 'Submit to Falcon request failed'));
    }

    return (await response.json()) as Record<string, unknown>;
  }

  private falconFileResults(response: Record<string, unknown>): Record<string, unknown>[] {
    const files = response['files'];
    if (!Array.isArray(files)) return [];
    return files.filter((file): file is Record<string, unknown> => !!file && typeof file === 'object');
  }

  private failedFalconFiles(fileResults: Record<string, unknown>[]): string[] {
    return fileResults
      .filter((file) => String(file['status'] ?? '').toLowerCase() === 'failed')
      .map((file) => String(file['file_name'] ?? 'Unknown file'));
  }

  private falconProviderFromFiles(fileResults: Record<string, unknown>[]): string {
    for (const file of fileResults) {
      const provider = this.extractMvrProvider(file);
      if (provider) return provider;
    }
    return '';
  }

  private notifyFalconReviewSubmitted(response: Record<string, unknown>): void {
    if (!this.isEmbeddedInParentWindow()) return;

    const targetOrigin = this.parentPostTargetOrigin();
    if (!targetOrigin) return;

    // Prefer the merged review data so every file's drivers reach Falcon, not just the last response.
    const reviewedDocument = this.mvrData?.drivers?.length ? this.mvrData : this.extractMvrDocuments(response)[0] ?? this.mvrData;
    const drivers = reviewedDocument?.drivers ?? [];
    window.parent.postMessage(
      {
        type: 'MVR_REVIEW_SUBMITTED',
        source: 'mvr-review',
        batchId: this.normalizedBatchId(),
        fileName: this.currentReviewFileName(),
        pdfFileName: this.pdfFileName,
        reviewedJson: reviewedDocument,
        drivers,
        // Falcon reads `response` first and grabs the first driver list it finds, so expose all drivers here.
        response: { ...response, drivers, reviewed_json: reviewedDocument },
      },
      targetOrigin,
    );
  }

  private isEmbeddedInParentWindow(): boolean {
    return typeof window !== 'undefined' && window.parent !== window;
  }

  private isTrustedParentMessage(event: MessageEvent): boolean {
    if (!this.allowedParentOrigins.length) return false;
    if (!this.allowedParentOrigins.includes(event.origin)) return false;
    // Only accept messages from the hosting parent window/frame.
    return event.source === window.parent;
  }

  private parentPostTargetOrigin(): string {
    // Reuse the origin the app was actually loaded from within Falcon when it is allowlisted,
    // otherwise fall back to the first configured trusted origin. Never post to '*'.
    const referrerOrigin = document.referrer ? new URL(document.referrer).origin : '';
    if (referrerOrigin && this.allowedParentOrigins.includes(referrerOrigin)) return referrerOrigin;
    return this.allowedParentOrigins[0] ?? '';
  }

  private stringBooleanConfigValue(value: unknown): 'true' | 'false' {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value).toLowerCase() === 'true' ? 'true' : 'false';
  }

  private async submitMvrReviewFeedback(token: string, operations: ReviewFeedbackOperation[]): Promise<ReviewFeedbackResponse> {
    const payload = {
      batch_id: this.normalizedBatchId(),
      file_name: this.currentReviewFileName(),
      operations,
      include_updated_json: true,
      save_to_blob: true,
    };

    const response = await fetch(`${this.apiBaseUrl}/submit-mvr-review-feedback`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await this.apiErrorMessage(response, 'MVR review feedback request failed'));
    }

    const data = (await response.json()) as ReviewFeedbackResponse;
    if (data.success === false) {
      throw new Error(this.reviewFeedbackErrorMessage(data, 'MVR review feedback request failed'));
    }
    return data;
  }

  private async submitMvrReviewFeedbackByBbox(token: string, operations: ReviewFeedbackOperation[]): Promise<ReviewFeedbackResponse> {
    const payload: BboxReviewFeedbackPayload = {
      batch_id: this.normalizedBatchId(),
      file_name: this.currentReviewFileName(),
      pdf_path: this.currentReviewPdfPath(),
      operations,
      include_updated_json: true,
      save_to_blob: true,
    };

    const response = await fetch(`${this.apiBaseUrl}/submit-mvr-review-feedback-bybbox`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await this.apiErrorMessage(response, 'MVR bbox feedback request failed'));
    }

    const data = (await response.json()) as ReviewFeedbackResponse;
    if (data.success === false) {
      throw new Error(this.reviewFeedbackErrorMessage(data, 'MVR bbox feedback request failed'));
    }
    return data;
  }

  private async pollMvrBatch(token: string, batchId: number | string): Promise<Record<string, unknown>> {
    const startedAt = Date.now();
    const completedStatuses = new Set(['completed', 'complete', 'done']);
    const failedStatuses = new Set(['failed', 'error']);

    while (Date.now() - startedAt < this.pollTimeoutMs) {
      this.pollAttempt += 1;
      this.uploadStatus = 'running';
      this.uploadMessage = `Batch ${batchId} is processing. Status check ${this.pollAttempt}.`;

      const response = await fetch(`${this.apiBaseUrl}/get-mvr-batch-status?batch_id=${encodeURIComponent(String(batchId))}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Batch status request failed (${response.status}).`);
      }

      const data = (await response.json()) as Record<string, unknown>;
      const status = String(data['status'] ?? '').toLowerCase();

      if (completedStatuses.has(status) || (status === 'finalizing' && this.extractMvrDocuments(data).length)) return data;
      if (failedStatuses.has(status)) throw new Error(`MVR batch ended with status "${status}".`);

      await this.delay(this.pollIntervalMs);
    }

    throw new Error('MVR batch polling timed out.');
  }

  private async loadApiResult(response: Record<string, unknown>): Promise<void> {
    const extractedDocuments = this.extractMvrDocuments(response);
    const filterResult = await this.filterDuplicateProcessedDrivers(response, extractedDocuments);
    if (filterResult.cancelled) {
      this.confirmCancelReset();
      this.uploadStatus = 'idle';
      this.uploadMessage = 'Upload cancelled. Select an MVR PDF to start again.';
      return;
    }

    const documents = filterResult.documents;
    const drivers = documents.flatMap((document) => document.drivers);

    if (!Array.isArray(drivers) || !drivers.length) {
      if (extractedDocuments.some((document) => document.drivers.length)) {
        this.confirmCancelReset();
        this.uploadMessage = 'The previously processed driver was skipped. Upload another MVR PDF to continue.';
        return;
      }
      const apiMessage = this.apiStatusMessage(response);
      throw new Error(apiMessage || 'API response did not include driver extraction results.');
    }

    this.mvrData = { drivers };
    this.mvrProvider = this.extractMvrProvider(response);
    this.activeDriverIndex = 0;
    this.validationItems = [];
    this.finalSubmitAfterValidationFixPending = false;
    this.driverFieldState.clear();
    this.driverEditedFieldIds.clear();
    this.driverSubmittedFieldKeys.clear();
    this.editedFieldIds.clear();
    this.submittedFieldUpdateKeys.clear();
    this.editOriginalValues.clear();
    this.editOriginalValidationComments.clear();
    this.editOriginalBboxes.clear();
    this.workflowMode = 'review';
    this.pdfDocument = null;
    this.pdfPath = this.extractPdfPath(response) || this.fieldValue(drivers[0]['File URL']) || this.pdfPath;
    this.applyDriver(drivers[0]);

    if (!this.pdfSourceUrl) {
      const fileUrl = this.fieldValue(drivers[0]['File URL']);
      this.pdfSourceUrl = fileUrl;
    }

    setTimeout(() => {
      void this.loadPdfDocument().then(() => this.renderPdfPage(this.selectedPdfPage));
    });
  }

  private async filterDuplicateProcessedDrivers(response: Record<string, unknown>, documents: MvrDocument[]): Promise<DuplicateDriverFilterResult> {
    const duplicateNotices = this.duplicateDriverNotices(response);
    if (!duplicateNotices.length || !documents.length) return { documents, cancelled: false };

    const choice = await this.confirmDuplicateDrivers(duplicateNotices);
    if (choice.cancelled) return { documents: [], cancelled: true };

    if (!choice.skippedNotices.length) return { documents, cancelled: false };

    return {
      documents: documents
        .map((document) => ({
          ...document,
          drivers: document.drivers.filter((driver, driverIndex) => !this.shouldSkipDuplicateDriver(driver, driverIndex, choice.skippedNotices, documents.length)),
        }))
        .filter((document) => document.drivers.length),
      cancelled: false,
    };
  }

  private duplicateDriverNotices(response: Record<string, unknown>): DuplicateDriverNotice[] {
    const duplicates = Array.isArray(response['duplicates']) ? response['duplicates'] : [];
    return duplicates.flatMap((duplicate) => {
      if (!duplicate || typeof duplicate !== 'object') return [];
      const record = duplicate as Record<string, unknown>;
      const driverIndex = Number(record['driver_index']);
      if (!Number.isFinite(driverIndex) || driverIndex < 0) return [];

      const driverName = String(record['driver_name'] ?? record['previous_driver_name'] ?? '').trim();
      const licenseNumber = String(record['license_number'] ?? '').trim();
      const dob = String(record['dob'] ?? '').trim();
      const message = String(record['message'] ?? '').trim()
        || `${driverName || 'This driver'} with License Number ${licenseNumber || '--'} and DOB ${dob || '--'} were previously processed.`;

      return [{
        driverIndex,
        fileName: String(record['current_file_name'] ?? record['file_name'] ?? '').trim(),
        driverName,
        licenseNumber,
        dob,
        message,
      }];
    });
  }

  private shouldSkipDuplicateDriver(driver: DriverRecord, driverIndex: number, skippedNotices: DuplicateDriverNotice[], documentCount: number): boolean {
    const driverFileName = this.normalizedDuplicateFileName(this.fieldValue(driver['File Name']));
    return skippedNotices.some((notice) => {
      if (notice.driverIndex !== driverIndex) return false;
      const noticeFileName = this.normalizedDuplicateFileName(notice.fileName);
      return !noticeFileName || documentCount === 1 || noticeFileName === driverFileName;
    });
  }

  private normalizedDuplicateFileName(fileName: string): string {
    return fileName.trim().toLowerCase();
  }

  private confirmDuplicateDrivers(notices: DuplicateDriverNotice[]): Promise<DuplicateDriverChoice> {
    this.duplicateNoticesForReview = notices;
    this.selectedDuplicateDriverIndexes = new Set(notices.map((_, index) => index));
    this.duplicateDriverSearch = '';
    this.showDuplicateModal = true;
    return new Promise((resolve) => {
      this.duplicateChoiceResolver = resolve;
    });
  }

  private resolveDuplicateDriverChoice(choice: DuplicateDriverChoice): void {
    this.showDuplicateModal = false;
    this.duplicateNoticesForReview = [];
    this.selectedDuplicateDriverIndexes = new Set<number>();
    this.duplicateDriverSearch = '';
    const resolver = this.duplicateChoiceResolver;
    this.duplicateChoiceResolver = null;
    resolver?.(choice);
  }

  private extractMvrDocuments(response: Record<string, unknown>): MvrDocument[] {
    const documents: MvrDocument[] = [];
    const directCandidates = [response['reviewed_json'], response['result'], response['results'], response];
    for (const candidate of directCandidates) {
      if (this.isMvrDocument(candidate)) documents.push(candidate);
    }

    const results = response['results'];
    if (Array.isArray(results)) {
      for (const item of results) {
        if (!item || typeof item !== 'object') continue;
        const record = item as Record<string, unknown>;
        const candidates = [record['reviewed_json'], record['result'], record['results'], record];
        for (const candidate of candidates) {
          if (this.isMvrDocument(candidate)) documents.push(candidate);
        }
      }
    }

    return this.uniqueMvrDocuments(documents);
  }

  private extractMvrProvider(response: Record<string, unknown>): string {
    const candidates: unknown[] = [
      response['mvr_provider'],
      response['mvr_type'],
      response['provider'],
      response['doc_type'],
      this.valueAtPath(response, ['validated_json', 'doc_type']),
      this.valueAtPath(response, ['result', 'doc_type']),
    ];

    const results = response['results'];
    if (Array.isArray(results)) {
      results.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const record = item as Record<string, unknown>;
        candidates.push(
          record['mvr_provider'],
          record['mvr_type'],
          record['provider'],
          record['doc_type'],
          this.valueAtPath(record, ['results', 'doc_type']),
          this.valueAtPath(record, ['reviewed_json', 'doc_type']),
          this.valueAtPath(record, ['validated_json', 'doc_type']),
          this.valueAtPath(record, ['result', 'doc_type']),
        );
      });
    }

    for (const candidate of candidates) {
      const provider = this.providerValue(candidate);
      if (provider) return provider;
    }
    return this.findProviderInTree(response);
  }

  private providerValue(value: unknown): string {
    if (!value) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
    if (typeof value !== 'object') return '';
    const record = value as Record<string, unknown>;
    return this.providerValue(record['value'])
      || this.providerValue(record['provider'])
      || this.providerValue(record['mvr_provider'])
      || this.providerValue(record['mvr_type'])
      || this.providerValue(record['doc_type']);
  }

  private findProviderInTree(value: unknown, depth = 0, seen = new Set<unknown>()): string {
    if (!value || typeof value !== 'object' || depth > 8 || seen.has(value)) return '';
    seen.add(value);

    const record = value as Record<string, unknown>;
    for (const key of ['doc_type', 'mvr_type', 'mvr_provider', 'provider']) {
      const provider = this.providerValue(record[key]);
      if (provider) return provider;
    }

    for (const child of Object.values(record)) {
      const provider = this.findProviderInTree(child, depth + 1, seen);
      if (provider) return provider;
    }
    return '';
  }

  private uniqueMvrDocuments(documents: MvrDocument[]): MvrDocument[] {
    const seen = new Set<MvrDocument>();
    return documents.filter((document) => {
      if (seen.has(document)) return false;
      seen.add(document);
      return true;
    });
  }

  private isMvrDocument(value: unknown): value is MvrDocument {
    return !!value && typeof value === 'object' && Array.isArray((value as MvrDocument).drivers);
  }

  private validationItemsFromResponse(response: Record<string, unknown>): ValidationItem[] {
    const validatedJson = response['validated_json'];
    if (!validatedJson || typeof validatedJson !== 'object') return [];

    const drivers = (validatedJson as Record<string, unknown>)['drivers'];
    if (!Array.isArray(drivers)) return [];

    const validationErrors = response['validation_errors'];
    if (validationErrors && typeof validationErrors === 'object') {
      const errorDrivers = (validationErrors as Record<string, unknown>)['drivers'];
      if (Array.isArray(errorDrivers)) {
        const items: ValidationItem[] = [];
        errorDrivers.forEach((driverErrors, localIndex) => {
          if (!driverErrors || typeof driverErrors !== 'object') return;
          const validatedDriver = drivers[localIndex];
          const validatedName = this.validatedDriverNameFromPlain(validatedDriver, localIndex);
          const globalIndex = this.globalDriverIndexByName(validatedName, localIndex);
          const fallbackDriver = this.mvrData?.drivers[globalIndex];
          const driverName = fallbackDriver ? this.driverName(fallbackDriver) : validatedName;
          this.collectValidationErrorItems(driverErrors, validatedDriver, [], globalIndex, driverName, items);
        });
        return items;
      }
    }

    const items: ValidationItem[] = [];
    drivers.forEach((driver, localIndex) => {
      if (!driver || typeof driver !== 'object') return;
      const validatedName = this.validatedDriverName(driver as DriverRecord, localIndex);
      const globalIndex = this.globalDriverIndexByName(validatedName, localIndex);
      const fallbackDriver = this.mvrData?.drivers[globalIndex];
      const driverName = fallbackDriver ? this.driverName(fallbackDriver) : validatedName;
      this.collectValidationItems(driver, [], globalIndex, driverName, items);
    });
    return items;
  }

  // Per-file responses index drivers locally; map back to the merged review's global index by name.
  private globalDriverIndexByName(name: string, fallbackIndex: number): number {
    const drivers = this.mvrData?.drivers;
    if (!drivers) return fallbackIndex;
    const matchIndex = drivers.findIndex((driver) => this.driverName(driver) === name);
    return matchIndex >= 0 ? matchIndex : fallbackIndex;
  }

  private validationItemsFromFalconErrors(errors: unknown): ValidationItem[] {
    const list = Array.isArray(errors) ? errors : errors ? [errors] : [];
    return list.map((error, index) => this.validationItemFromFalconError(error, index));
  }

  private validationItemFromFalconError(error: unknown, index: number): ValidationItem {
    if (typeof error === 'string') {
      return this.fallbackFalconValidationItem(error.trim() || 'Falcon validation failed.', index);
    }
    if (!error || typeof error !== 'object') return this.fallbackFalconValidationItem('Falcon validation failed.', index);

    const record = error as Record<string, unknown>;
    const rawDriverIndex = record['driverIndex'] ?? record['driver_index'];
    const parsedDriverIndex =
      typeof rawDriverIndex === 'number'
        ? rawDriverIndex
        : typeof rawDriverIndex === 'string'
          ? Number(rawDriverIndex)
          : Number.NaN;
    const driverIndex = Number.isFinite(parsedDriverIndex) && parsedDriverIndex >= 0 ? parsedDriverIndex : this.activeDriverIndex;
    const fallbackName = this.activeValidationDriverName(driverIndex);
    const comment = String(record['comment'] ?? record['message'] ?? record['error'] ?? 'Falcon validation failed.').trim();

    return {
      id: `falcon-validation-${index}`,
      driverIndex,
      driverName: String(record['driverName'] ?? record['driver_name'] ?? fallbackName),
      field: String(record['field'] ?? record['field_name'] ?? record['name'] ?? 'Falcon Validation'),
      value: this.validationPlainDisplayValue(record['value'] ?? record['current_value'] ?? record['currentValue'] ?? ''),
      comment: comment || 'Falcon validation failed.',
      path: this.jsonPathFromUnknownPath(record['path']),
    };
  }

  private fallbackFalconValidationItem(comment: string, index = 0): ValidationItem {
    return {
      id: `falcon-validation-${index}`,
      driverIndex: this.activeDriverIndex,
      driverName: this.activeValidationDriverName(),
      field: 'Falcon Validation',
      value: '--',
      comment,
      path: [],
    };
  }

  private jsonPathFromUnknownPath(path: unknown): JsonPathPart[] {
    if (!Array.isArray(path)) return [];
    return path.filter((part): part is JsonPathPart => typeof part === 'string' || typeof part === 'number');
  }

  private activeValidationDriverName(driverIndex = this.activeDriverIndex): string {
    const driver = this.mvrData?.drivers?.[driverIndex];
    return driver ? this.driverName(driver) : `Driver ${driverIndex + 1}`;
  }

  private collectValidationErrorItems(
    errorValue: unknown,
    validatedValue: unknown,
    path: JsonPathPart[],
    driverIndex: number,
    driverName: string,
    items: ValidationItem[],
  ): void {
    if (Array.isArray(errorValue)) {
      errorValue.forEach((item, index) => {
        const childValue = Array.isArray(validatedValue) ? validatedValue[index] : undefined;
        this.collectValidationErrorItems(item, childValue, [...path, index], driverIndex, driverName, items);
      });
      return;
    }

    if (errorValue && typeof errorValue === 'object') {
      const valueRecord = validatedValue && typeof validatedValue === 'object' ? validatedValue as Record<string, unknown> : {};
      Object.entries(errorValue as Record<string, unknown>).forEach(([key, childError]) => {
        this.collectValidationErrorItems(childError, valueRecord[key], [...path, key], driverIndex, driverName, items);
      });
      return;
    }

    const comment = String(errorValue ?? '').trim();
    if (!comment || comment.toLowerCase() === 'pass') return;
    if (this.shouldHideValidationItem(path, comment)) return;

    items.push({
      id: `${driverIndex}-${path.map(String).join('-')}-${items.length}`,
      driverIndex,
      driverName,
      field: this.validationFieldLabel(path),
      value: this.validationPlainDisplayValue(validatedValue),
      comment,
      path,
    });
  }

  private collectValidationItems(
    value: unknown,
    path: JsonPathPart[],
    driverIndex: number,
    driverName: string,
    items: ValidationItem[],
  ): void {
    if (Array.isArray(value)) {
      value.forEach((item, index) => this.collectValidationItems(item, [...path, index], driverIndex, driverName, items));
      return;
    }

    if (!value || typeof value !== 'object') return;
    const record = value as Record<string, unknown>;

    if (typeof record['is_valid'] === 'boolean') {
      if (!record['is_valid']) {
        const displayValue = this.validationDisplayValue(record);
        const comment = String(record['comment'] ?? '').trim() || 'Validation failed.';
        if (!this.hasValidationDisplayValue(displayValue)) return;
        if (this.shouldHideValidationItem(path, comment)) return;
        items.push({
          id: `${driverIndex}-${path.map(String).join('-')}-${items.length}`,
          driverIndex,
          driverName,
          field: this.validationFieldLabel(path),
          value: displayValue,
          comment,
          path,
        });
      }
      return;
    }

    Object.entries(record).forEach(([key, childValue]) => {
      this.collectValidationItems(childValue, [...path, key], driverIndex, driverName, items);
    });
  }

  private validationFieldLabel(path: JsonPathPart[]): string {
    return path
      .map((part) => (typeof part === 'number' ? `#${part + 1}` : part))
      .join(' / ');
  }

  private validationDisplayValue(wrapper: Record<string, unknown>): string {
    const value = wrapper['value'];
    const fieldDataValue = this.fieldValue(value);
    if (fieldDataValue) return fieldDataValue;
    if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
    if (value === null || value === undefined) return '--';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '--';
  }

  private validationPlainDisplayValue(value: unknown): string {
    const fieldDataValue = this.fieldValue(value);
    if (fieldDataValue) return fieldDataValue;
    if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
    if (value === null || value === undefined || value === '') return '--';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '--';
  }

  private hasValidationDisplayValue(value: string): boolean {
    const normalized = value.trim();
    return !!normalized && normalized !== '--';
  }

  private shouldHideValidationItem(path: JsonPathPart[], comment: string): boolean {
    const fieldName = String(path[path.length - 1] ?? '').trim().toLowerCase();
    return fieldName === 'description' && comment.trim().toLowerCase() === 'expected dict, got str';
  }

  private validatedDriverName(driver: DriverRecord, driverIndex: number): string {
    const first = this.validationNestedFieldValue(driver['Given Name']);
    const last = this.validationNestedFieldValue(driver['Last Name']);
    return `${first} ${last}`.trim() || `Driver ${driverIndex + 1}`;
  }

  private validatedDriverNameFromPlain(driver: unknown, driverIndex: number): string {
    if (!driver || typeof driver !== 'object') return `Driver ${driverIndex + 1}`;
    const record = driver as Record<string, unknown>;
    const first = this.validationPlainDisplayValue(record['Given Name']);
    const last = this.validationPlainDisplayValue(record['Last Name']);
    return [first, last].filter((part) => part && part !== '--').join(' ') || `Driver ${driverIndex + 1}`;
  }

  private validationNestedFieldValue(value: unknown): string {
    if (!value || typeof value !== 'object') return '';
    return this.fieldValue((value as Record<string, unknown>)['value']);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private normalizedBatchId(): number | string {
    const numericBatchId = Number(this.batchId);
    return Number.isFinite(numericBatchId) ? numericBatchId : String(this.batchId);
  }

  private currentReviewFileName(): string {
    const driverFileName = this.currentDriver ? this.fieldValue(this.currentDriver['File Name']) : '';
    return driverFileName || this.pdfFileName || this.selectedPdfFile?.name || 'MVR.pdf';
  }

  private reviewFileNames(): string[] {
    const names = new Set<string>();
    this.mvrData?.drivers?.forEach((driver) => {
      const name = this.fieldValue(driver['File Name']);
      if (name) names.add(name);
    });
    if (!names.size) names.add(this.currentReviewFileName());
    return [...names];
  }

  private currentReviewPdfPath(): string {
    const driverFileUrl = this.currentDriver ? this.fieldValue(this.currentDriver['File URL']) : '';
    const remotePdfSource = this.pdfSourceUrl && !this.pdfSourceUrl.startsWith('blob:') ? this.pdfSourceUrl : '';
    return this.cleanPdfPath(this.pdfPath || driverFileUrl || remotePdfSource || this.currentReviewFileName());
  }

  private extractPdfPath(response: Record<string, unknown>): string {
    const candidates = [
      response['pdf_path'],
      response['pdf_url'],
      response['file_url'],
      response['source_pdf_url'],
      response['highlighted_pdf_url'],
      response['pdf_blob_url'],
      response['blob_url'],
    ];

    const results = Array.isArray(response['results']) ? (response['results'] as Record<string, unknown>[]) : [];
    results.forEach((result) => {
      candidates.push(result['pdf_path'], result['pdf_url'], result['file_url'], result['source_pdf_url'], result['highlighted_pdf_url'], result['pdf_blob_url'], result['blob_url']);
    });
    const resultObject = response['results'];
    if (resultObject && typeof resultObject === 'object' && !Array.isArray(resultObject)) {
      const result = resultObject as Record<string, unknown>;
      candidates.push(result['pdf_path'], result['pdf_url'], result['file_url'], result['source_pdf_url'], result['highlighted_pdf_url'], result['pdf_blob_url'], result['blob_url']);
    }

    return this.cleanPdfPath(candidates.map((value) => String(value ?? '').trim()).find(Boolean) ?? '');
  }

  private cleanPdfPath(value: string): string {
    return value.split('?')[0].trim();
  }

  private async apiErrorMessage(response: Response, fallback: string): Promise<string> {
    let detail = '';
    try {
      const data = (await response.json()) as Record<string, unknown>;
      detail = this.reviewFeedbackErrorMessage(data, '');
    } catch {
      detail = await response.text().catch(() => '');
    }

    return detail || `${fallback} (${response.status}).`;
  }

  private reviewFeedbackErrorMessage(response: unknown, fallback: string): string {
    const record = response && typeof response === 'object' ? response as Record<string, unknown> : {};
    const error = String(record['error'] ?? record['message'] ?? record['detail'] ?? '').trim();
    return error || fallback || 'Request failed.';
  }

  private apiStatusMessage(response: Record<string, unknown>): string {
    const messages = [...this.toMessageList(response['all_messages']), ...this.toMessageList(response['upload_messages'])];
    if (!messages.length) return '';
    return `API processing message: ${messages.join(' | ')}`;
  }

  private toMessageList(value: unknown): string[] {
    if (!Array.isArray(value)) return [];

    return value
      .map((message) => {
        if (typeof message === 'string') return message;
        if (!message || typeof message !== 'object') return '';

        const record = message as Record<string, unknown>;
        const rule = String(record['rule'] ?? '').trim();
        const detail = String(record['message'] ?? record['detail'] ?? record['error'] ?? '').trim();
        return [rule, detail].filter(Boolean).join(': ');
      })
      .filter(Boolean);
  }
}
